import { formatClockValue } from "./pageDataHelpers.js";
import { formatDurationShort } from "./routeModelsData.js";
import {
  buildExcludedTruckRefreshErrorId,
  selectKpiGridData,
} from "./resultsChromeData.js";
import { formatTons } from "./resultsFleetData.js";
import { isTruckDompeurType } from "./fleetReassignmentHelpers.js";
import { isLunchBreakSegment } from "./lunchBreakTimeline.js";
import {
  buildCompartmentFillIndicatorLabel,
  normalizeCompartmentFillPercent,
  summarizeCompartmentFillSegments,
} from "../lib/productCompartmentFillRules.js";

const MIN_SEGMENT_VISUAL_PERCENT = 1.8;

const TRUCK_HOVER_CARD_EXCLUDED_KPI_KEYS = new Set([
  "stop-count",
  "estimated-duration",
  "total-estimated-duration",
  "estimated-cost",
]);

const EMPTY_TRUCK_HOVER_CARD_VALUE = "—";

const EXCLUDED_TRUCK_REFRESH_FALLBACK_DETAIL = "Data manager a été modifié. Comme ce camion est exclu du bouton Optimiser (camion protégé, commande gelée ou réordonnancée, ou dispatch désactivé), il n'a pas été mis à jour automatiquement.";

function normalizeExcludedTruckRefreshText(value = "") {
  return typeof value === "string" ? value.trim() : "";
}

export function getExcludedTruckRefreshImpactedLocationLabels(refreshWarning) {
  const locations = Array.isArray(refreshWarning?.impactedLocations)
    ? refreshWarning.impactedLocations
    : [];
  const seenLabels = new Set();
  const labels = [];

  locations.forEach((location) => {
    const label = normalizeExcludedTruckRefreshText(location?.label);
    if (!label) {
      return;
    }
    const key = label.toLocaleLowerCase("fr-CA");
    if (seenLabels.has(key)) {
      return;
    }
    seenLabels.add(key);
    labels.push(label);
  });

  return labels;
}

function formatExcludedTruckRefreshImpactTime(value) {
  const normalizedValue = normalizeExcludedTruckRefreshText(value);
  if (!normalizedValue) {
    return "";
  }

  const match = normalizedValue.match(/\b(\d{1,2}):(\d{2})\b/);
  if (!match) {
    return normalizedValue;
  }

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return normalizedValue;
  }

  return `${String(hours).padStart(2, "0")}h${String(minutes).padStart(2, "0")}`;
}

function buildExcludedTruckRefreshSpecificImpactText(refreshWarning) {
  const locations = Array.isArray(refreshWarning?.impactedLocations)
    ? refreshWarning.impactedLocations
    : [];

  for (const location of locations) {
    const label = normalizeExcludedTruckRefreshText(location?.label);
    if (!label) {
      continue;
    }

    const currentOpeningTime = formatExcludedTruckRefreshImpactTime(location?.currentOpeningTime || location?.currentStartTime);
    const dataManagerOpeningTime = formatExcludedTruckRefreshImpactTime(location?.dataManagerOpeningTime || location?.dataManagerStartTime);
    if (currentOpeningTime && dataManagerOpeningTime && currentOpeningTime !== dataManagerOpeningTime) {
      return `${label} conserve son début à ${currentOpeningTime} au lieu de ${dataManagerOpeningTime}.`;
    }

    const currentClosingTime = formatExcludedTruckRefreshImpactTime(location?.currentClosingTime);
    const dataManagerClosingTime = formatExcludedTruckRefreshImpactTime(location?.dataManagerClosingTime);
    if (currentClosingTime && dataManagerClosingTime && currentClosingTime !== dataManagerClosingTime) {
      return `${label} conserve sa fermeture à ${currentClosingTime} au lieu de ${dataManagerClosingTime}.`;
    }
  }

  return "";
}

function getExcludedTruckRefreshReasonSearchText(refreshWarning) {
  return [
    refreshWarning?.actionCode,
    refreshWarning?.reasonCode,
    refreshWarning?.reasonLabel,
    refreshWarning?.reasonDetail,
    refreshWarning?.reasonStage,
  ].map((value) => normalizeExcludedTruckRefreshText(value).toLocaleLowerCase("fr-CA")).join(" ");
}

function resolveExcludedTruckRefreshReasonKind(refreshWarning) {
  const actionCode = normalizeExcludedTruckRefreshText(refreshWarning?.actionCode).toLowerCase();
  const reasonCode = normalizeExcludedTruckRefreshText(refreshWarning?.reasonCode).toLowerCase();
  const reasonText = getExcludedTruckRefreshReasonSearchText(refreshWarning);

  if (actionCode === "preserve_or_release_manual_order") {
    return "protected_sequence";
  }
  if (actionCode === "correct_capacity_or_split_load") {
    return "capacity";
  }
  if (actionCode === "correct_compartments_or_change_truck") {
    return "compartments";
  }
  if (actionCode === "allow_overtime_or_reduce_route") {
    return "max_work_time";
  }
  if (actionCode === "correct_time_window") {
    return "time_window";
  }
  if (actionCode === "correct_data_manager") {
    return "missing_data_or_eligibility";
  }
  if (actionCode === "review_technical_failure") {
    return "technical";
  }

  if (reasonCode === "visible_order_sequence_preservation_failed") {
    return "protected_sequence";
  }
  if (reasonCode === "compartment_capacity_insufficient" || /compartiment/.test(reasonText)) {
    return "compartments";
  }
  if (reasonCode === "capacity_insufficient" || /capacit[eé]/.test(reasonText)) {
    return "capacity";
  }
  if (reasonCode === "time_insufficient" || /(temps journalier|heures? maximales?|max work|work time|overtime|surtemps)/.test(reasonText)) {
    return "max_work_time";
  }
  if (/(window|fen[eê]tre|horaire|ouverture|fermeture)/.test(reasonText)) {
    return "time_window";
  }
  if (/(eligib|admissib|incompatib|missing|incomplete|introuvable|manquant|workbook_scope|payload_missing|assignment_missing|donn[eé]es?[^.]*incompl)/.test(reasonText)) {
    return "missing_data_or_eligibility";
  }

  return "technical";
}

export function buildExcludedTruckRefreshProblemText(refreshWarning, fallbackLabel = "") {
  const reasonKind = resolveExcludedTruckRefreshReasonKind(refreshWarning);
  const problemByKind = {
    capacity: "La charge requise dépasse la capacité disponible.",
    compartments: "Les compartiments ne peuvent pas contenir la charge requise.",
    max_work_time: "Le temps maximal travaillé du camionneur serait dépassé.",
    missing_data_or_eligibility: "Des données Data manager requises sont manquantes ou incompatibles.",
    protected_sequence: "La mise à jour changerait l'ordre protégé.",
    technical: "La reconstruction a échoué pour une cause technique à examiner.",
    time_window: "Une fenêtre horaire empêche la reconstruction du plan.",
  };

  return problemByKind[reasonKind]
    || normalizeExcludedTruckRefreshText(fallbackLabel)
    || "Le camion n'a pas pu être reconstruit avec les données Data manager actuelles.";
}

export function buildExcludedTruckRefreshProblemDetailText(refreshWarning = null) {
  return normalizeExcludedTruckRefreshText(refreshWarning?.reasonDetail) || EXCLUDED_TRUCK_REFRESH_FALLBACK_DETAIL;
}

export function buildExcludedTruckRefreshImpactText(refreshWarning) {
  const reasonKind = resolveExcludedTruckRefreshReasonKind(refreshWarning);
  if (reasonKind === "protected_sequence") {
    return "Plan et ordre actuels conservés.";
  }

  const specificImpactText = buildExcludedTruckRefreshSpecificImpactText(refreshWarning);
  if (specificImpactText) {
    return specificImpactText;
  }

  const labels = getExcludedTruckRefreshImpactedLocationLabels(refreshWarning);
  const reasonText = getExcludedTruckRefreshReasonSearchText(refreshWarning);
  const isTimingIssue = /(temps|time|horaire|fenetre|fenêtre|ouverture|fermeture|window)/.test(reasonText);
  const changedDataPhrase = isTimingIssue ? "du nouvel horaire" : "de la nouvelle configuration";

  if (!labels.length) {
    return `Ce camion reste sur l'ancien plan; il ne tient pas compte ${changedDataPhrase} Data manager.`;
  }
  if (labels.length === 1) {
    return `${labels[0]} reste sur l'ancien plan; ce site ne tient pas compte ${changedDataPhrase} Data manager.`;
  }
  return `Certains sites de ce camion, dont ${labels[0]}, restent sur l'ancien plan; ils ne tiennent pas compte ${changedDataPhrase} Data manager.`;
}

export function buildExcludedTruckRefreshSolutionLines(refreshWarning) {
  // Mélange protégé ou compartiment partagé : « Optimiser ce camion » le refuse, le serveur donne l'autre conseil.
  const recommendedAction = normalizeExcludedTruckRefreshText(refreshWarning?.recommendedAction);
  if (recommendedAction) {
    return [recommendedAction];
  }
  const reasonKind = resolveExcludedTruckRefreshReasonKind(refreshWarning);

  if (reasonKind === "protected_sequence") {
    return [
      "Ordre voulu : rien à faire. Sinon, retirez la protection puis optimisez le camion.",
    ];
  }
  if (reasonKind === "capacity") {
    return [
      "Réduire ou fractionner la charge, choisir un camion de capacité suffisante ou corriger sa capacité dans Data manager.",
      "Relancer ensuite Optimiser ce camion.",
    ];
  }
  if (reasonKind === "compartments") {
    return [
      "Corriger les compartiments dans Data manager ou choisir un camion compatible.",
      "Relancer ensuite Optimiser ce camion.",
    ];
  }
  if (reasonKind === "max_work_time") {
    return [
      "Ouvrir le temps supplémentaire (clic droit sur le camion) ou alléger la tournée.",
      "Relancer ensuite Optimiser ce camion.",
    ];
  }
  if (reasonKind === "time_window") {
    return [
      "Corriger la fenêtre horaire ou la planification concernée.",
      "Relancer ensuite Optimiser ce camion.",
    ];
  }
  if (reasonKind === "missing_data_or_eligibility") {
    return [
      "Corriger les données précises signalées dans Data manager.",
      "Relancer ensuite Optimiser ce camion.",
    ];
  }

  return ["Consulter le diagnostic et ses détails techniques avant toute modification; le plan actuel a été conservé."];
}

export function buildExcludedTruckRefreshOpenRequest(truckId = "") {
  const errorId = buildExcludedTruckRefreshErrorId(truckId);
  return errorId
    ? {
        viewName: "warnings",
        errorId,
        filter: "excluded_truck_refresh",
        source: "fleet-triangle",
      }
    : null;
}

export function buildHaversineRouteWarningOpenRequest(errorId = "") {
  const request = {
    viewName: "warnings",
    filter: "routing_issues",
    source: "fleet-triangle",
  };
  const normalizedErrorId = normalizeExcludedTruckRefreshText(errorId);
  return normalizedErrorId ? { ...request, errorId: normalizedErrorId } : request;
}

export function buildExcludedTruckRefreshPresentation(refreshWarning = null, { truckId = "", truckName = "" } = {}) {
  if (!refreshWarning || typeof refreshWarning !== "object") {
    return null;
  }

  const reasonKind = resolveExcludedTruckRefreshReasonKind(refreshWarning);
  const reasonLabel = normalizeExcludedTruckRefreshText(refreshWarning.reasonLabel) || "Non mis à jour avec Data manager";
  const isProtectedSequence = reasonKind === "protected_sequence";
  const reasonDetail = isProtectedSequence ? "" : buildExcludedTruckRefreshProblemDetailText(refreshWarning);
  const resolvedTruckId = normalizeExcludedTruckRefreshText(truckId || refreshWarning.truckId);
  const resolvedTruckName = normalizeExcludedTruckRefreshText(truckName || refreshWarning.truckName || resolvedTruckId);
  const isActionableConstraint = [
    "capacity",
    "compartments",
    "max_work_time",
    "missing_data_or_eligibility",
    "time_window",
  ].includes(reasonKind);
  const title = [reasonLabel, reasonDetail].filter(Boolean).join(" · ");
  const problemText = buildExcludedTruckRefreshProblemText(refreshWarning, reasonLabel);
  const actionLines = buildExcludedTruckRefreshSolutionLines(refreshWarning);
  const accessibleTitle = isProtectedSequence
    ? [problemText, ...actionLines].filter(Boolean).join(" ")
    : title;

  return {
    actionCode: normalizeExcludedTruckRefreshText(refreshWarning.actionCode),
    actionLines,
    animation: isActionableConstraint ? "pulse" : "none",
    ariaLabel: accessibleTitle
      ? `Camion ${resolvedTruckName || "exclu"} non mis à jour avec Data manager: ${accessibleTitle}. Ouvrir dans le centre des avertissements.`
      : `Camion ${resolvedTruckName || "exclu"} non mis à jour avec Data manager. Ouvrir dans le centre des avertissements.`,
    impactedLocationLabels: isProtectedSequence ? [] : getExcludedTruckRefreshImpactedLocationLabels(refreshWarning),
    impactText: buildExcludedTruckRefreshImpactText(refreshWarning),
    openRequest: buildExcludedTruckRefreshOpenRequest(resolvedTruckId),
    problemText,
    reasonCode: normalizeExcludedTruckRefreshText(refreshWarning.reasonCode),
    reasonContext: refreshWarning.reasonContext && typeof refreshWarning.reasonContext === "object"
      ? refreshWarning.reasonContext
      : null,
    reasonDetail,
    reasonKind,
    reasonLabel,
    reasonStage: normalizeExcludedTruckRefreshText(refreshWarning.reasonStage),
    tone: isActionableConstraint ? "danger" : "warning",
  };
}

// Bulle courte pour le répartiteur : chiffres du camion, une cause, une consigne qui marche.
export function buildHaversineRouteWarningPresentation(routeIssue = null, { truckName = "", summary = null } = {}) {
  const issue = routeIssue && typeof routeIssue === "object" ? routeIssue : {};
  const fallback = issue.haversineFallback && typeof issue.haversineFallback === "object" ? issue.haversineFallback : {};
  const count = Number(summary?.count ?? fallback.haversine_segment_count);
  const total = Number(summary?.total ?? fallback.total_segment_count);
  const example = summary?.example;
  const resolvedTruckName = normalizeExcludedTruckRefreshText(truckName || issue.truckName || issue.truckId);
  const problemRows = [
    ...(count > 0 ? [["Sans route", `${count} trajet${count > 1 ? "s" : ""}${total > 0 ? ` sur ${total}` : ""}`]] : []),
    ...(example?.from && example?.to ? [["Exemple", `${example.from} → ${example.to} : ${example.distanceLabel} à vol d'oiseau`]] : []),
    ["Effet", "Km et heures de ce camion approximatifs"],
  ];
  const causeText = summary?.cause === "stale_route"
    ? "Tournée modifiée après le dernier calcul HERE"
    : "HERE n'a pas fourni la distance de ces trajets";
  // « Optimiser ce camion » garde la route de départ en erreur ; le bouton de la bulle la recalcule.
  const actionLines = ["Recalculer la route de ce camion (l'ordre ne change pas)."];

  return {
    actionLines,
    animation: "pulse",
    ariaLabel: `Route incomplète pour ${resolvedTruckName || "ce camion"} : ${problemRows.map((row) => row.join(" ")).join(", ")}. ${causeText}. Ouvrir dans le centre des avertissements.`,
    causeText,
    openRequest: buildHaversineRouteWarningOpenRequest(issue.errorId),
    problemRows,
    title: resolvedTruckName ? `Route incomplète · ${resolvedTruckName}` : "Route incomplète",
    tone: "warning",
  };
}

function formatDurationDense(minutes) {
  if (minutes == null || !Number.isFinite(minutes) || minutes < 0) {
    return null;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = Math.round(minutes % 60);
  if (hours === 0) {
    return `${remainingMinutes} min`;
  }
  if (remainingMinutes === 0) {
    return `${hours} h`;
  }

  return `${hours} h ${remainingMinutes}`;
}

function compactTonsLabel(label = "") {
  if (typeof label !== "string") {
    return "";
  }

  return label.replace(/(\d+)\.0\b/g, "$1").trim();
}

function getCompactCapacityLabel(shell) {
  const peakLoadLabel = compactTonsLabel(shell?.peakLoadLabel || "");
  const capacityLabel = compactTonsLabel(shell?.capacityLabel || "");

  if (peakLoadLabel && capacityLabel) {
    const compactPeakLoadLabel = peakLoadLabel.endsWith(" t") ? peakLoadLabel.slice(0, -2) : peakLoadLabel;
    return `${compactPeakLoadLabel}/${capacityLabel}`;
  }

  return peakLoadLabel || capacityLabel || "";
}

function isFiniteTimelineMinutes(value) {
  return typeof value === "number" && Number.isFinite(value);
}

export function isPreviousDayPreloadDisplaySegment(segment = null) {
  if (
    segment?.isInitialPreload
    || segment?.is_initial_preload
    || segment?.isStartOfDayPreload
    || segment?.is_start_of_day_preload
  ) {
    return true;
  }

  const stopType = segment?.stopType
    || segment?.stop_type
    || segment?.businessStep?.stepType
    || segment?.businessStep?.step_type
    || "";

  return Boolean(
    (segment?.isPreloadedPreviousDay || segment?.is_preloaded_previous_day)
    && stopType === "loading"
  );
}

function isLeadingPreloadTimelineSegment(segment) {
  return Boolean(segment?.isInitialPreload || segment?.isEndOfDayLoadSourcePreload);
}

function isEndOfDayLoadTimelineSegment(segment) {
  return Boolean(segment?.isEndOfDayLoad || segment?.isEndOfDayLoadSourcePreload);
}

function resolveRenderSegmentStartMinutes(segment) {
  if (isFiniteTimelineMinutes(segment?.visualSegmentStartMinutes)) {
    return Number(segment.visualSegmentStartMinutes);
  }

  if (isFiniteTimelineMinutes(segment?.segmentStartMinutes)) {
    return Number(segment.segmentStartMinutes);
  }

  return null;
}

function resolveSegmentTimelineAnchorStartMinutes(segment, { hasInitialPreload = false } = {}) {
  if (hasInitialPreload && !isLeadingPreloadTimelineSegment(segment) && isFiniteTimelineMinutes(segment?.segmentStartMinutes)) {
    return Number(segment.segmentStartMinutes);
  }

  return resolveRenderSegmentStartMinutes(segment);
}

function resolveRenderSegmentEndMinutes(segment) {
  if (isFiniteTimelineMinutes(segment?.visualSegmentEndMinutes)) {
    return Number(segment.visualSegmentEndMinutes);
  }

  if (isFiniteTimelineMinutes(segment?.segmentEndMinutes)) {
    return Number(segment.segmentEndMinutes);
  }

  return null;
}

function resolveDisplayedSegmentEndMinutes(segment) {
  return isFiniteTimelineMinutes(segment?.displayedSegmentEndMinutes)
    ? Number(segment.displayedSegmentEndMinutes)
    : null;
}

function resolveTimelinePositionStartMinutes(segment, options = {}) {
  const { hasInitialPreload = false } = options;
  if (isFiniteTimelineMinutes(segment?.displayedSegmentStartMinutes)) {
    return Number(segment.displayedSegmentStartMinutes);
  }

  return resolveSegmentTimelineAnchorStartMinutes(segment, { hasInitialPreload });
}

function resolveTimelinePositionEndMinutes(segment) {
  if (isFiniteTimelineMinutes(segment?.displayedSegmentEndMinutes)) {
    return Number(segment.displayedSegmentEndMinutes);
  }

  return resolveRenderSegmentEndMinutes(segment);
}

function isInvisibleDisplayedTimelineMarker(segment = null) {
  if (!segment || typeof segment !== "object") {
    return false;
  }

  const widthPercent = Number(segment.widthPercent);
  const timelineDurationMinutes = Number(segment.timelineDurationMinutes);
  const visualTimelineDurationMinutes = Number(segment.visualTimelineDurationMinutes);
  const hasZeroWidth = Number.isFinite(widthPercent) && widthPercent === 0;
  const hasZeroDuration = (!Number.isFinite(timelineDurationMinutes) || timelineDurationMinutes === 0)
    && (!Number.isFinite(visualTimelineDurationMinutes) || visualTimelineDurationMinutes === 0);

  return hasZeroDuration
    && segment.stopType === "depot_return"
    && (hasZeroWidth || segment.isTerminalRouteMarker === true);
}

function resolveTerminalMergedStopEndCandidate(segment, { useDisplayedEnd = false } = {}) {
  const displayedEndMinutes = useDisplayedEnd ? resolveDisplayedSegmentEndMinutes(segment) : null;
  if (displayedEndMinutes != null) {
    return {
      at: segment?.displayedSegmentEndAt || null,
      minutes: displayedEndMinutes,
    };
  }

  if (isFiniteTimelineMinutes(segment?.terminalMergedStopEndMinutes)) {
    return {
      at: segment?.terminalMergedStopEndAt || null,
      minutes: Number(segment.terminalMergedStopEndMinutes),
    };
  }

  return {
    at: segment?.visualSegmentEndAt || segment?.segmentEndAt || null,
    minutes: resolveRenderSegmentEndMinutes(segment),
  };
}

function resolveTerminalMergedStopEnd(previousItem = null, resolvedSegments = [], terminalItemIndex = 0) {
  const previousItemCandidate = resolveTerminalMergedStopEndCandidate(previousItem, { useDisplayedEnd: true });
  const sameSiteGroupKey = typeof previousItem?.sameSiteGroupKey === "string"
    ? previousItem.sameSiteGroupKey.trim()
    : "";

  if (!previousItem?.isSameSiteSequenceGroup || !sameSiteGroupKey) {
    return previousItemCandidate;
  }

  let cursor = terminalItemIndex - 1;
  let bestCandidate = previousItemCandidate;

  while (cursor >= 0) {
    const candidateSegment = resolvedSegments[cursor] || null;
    const candidateGroupKey = typeof candidateSegment?.sameSiteGroupKey === "string"
      ? candidateSegment.sameSiteGroupKey.trim()
      : "";

    if (candidateGroupKey !== sameSiteGroupKey) {
      break;
    }

    const candidate = resolveTerminalMergedStopEndCandidate(candidateSegment, { useDisplayedEnd: true });

    if (candidate.minutes != null && (bestCandidate.minutes == null || candidate.minutes > bestCandidate.minutes)) {
      bestCandidate = candidate;
    }

    cursor -= 1;
  }

  return bestCandidate;
}

function collectSameSiteGroupMembers(resolvedSegments, cursor, sameSiteGroupKey) {
  const memberSegments = [resolvedSegments[cursor] || null];
  let groupEnd = cursor + 1;

  while (groupEnd < resolvedSegments.length) {
    const candidateSegment = resolvedSegments[groupEnd] || null;
    const candidateGroupKey = typeof candidateSegment?.sameSiteGroupKey === "string"
      ? candidateSegment.sameSiteGroupKey.trim()
      : "";

    if (candidateGroupKey !== sameSiteGroupKey) {
      break;
    }

    memberSegments.push(candidateSegment);
    groupEnd += 1;
  }

  return { groupEnd, memberSegments };
}

function buildSameSiteSequenceManualBufferTotals(segments = []) {
  const resolvedSegments = Array.isArray(segments) ? segments.filter(Boolean) : [];
  const totals = new Map();
  let cursor = 0;

  while (cursor < resolvedSegments.length) {
    const firstSegment = resolvedSegments[cursor] || null;
    const sameSiteGroupKey = typeof firstSegment?.sameSiteGroupKey === "string"
      ? firstSegment.sameSiteGroupKey.trim()
      : "";
    const sameSiteGroupSize = Number.isInteger(firstSegment?.sameSiteGroupSize) ? firstSegment.sameSiteGroupSize : 1;

    if (!firstSegment?.isSameSiteSequenceGroup || !sameSiteGroupKey || sameSiteGroupSize <= 1 || firstSegment.sameSiteGroupIndex !== 0) {
      cursor += 1;
      continue;
    }

    const { groupEnd, memberSegments } = collectSameSiteGroupMembers(resolvedSegments, cursor, sameSiteGroupKey);

    totals.set(
      getSameSiteSequenceOverrideKey(firstSegment, cursor),
      memberSegments.reduce((total, memberSegment) => total + resolveSequenceManualBufferMinutes(memberSegment), 0),
    );
    cursor = groupEnd;
  }

  return totals;
}

export function buildFleetDisplayedSegmentTimings(segments = [], hovercardContext = null) {
  const resolvedSegments = Array.isArray(segments) ? segments.filter(Boolean) : [];

  if (!resolvedSegments.length) {
    return [];
  }

  const displayOverrides = buildSameSiteSequenceDisplayOverrides(buildFleetSequenceDisplayItems(resolvedSegments));
  const sameSiteManualBufferTotals = buildSameSiteSequenceManualBufferTotals(resolvedSegments);
  const lastIndex = resolvedSegments.length - 1;
  const terminalMergeSourceIndex = lastIndex > 0 && resolvedSegments[lastIndex]?.stopType === "depot_return"
    ? lastIndex - 1
    : null;

  return resolvedSegments.map((segment, fallbackIndex) => {
    const displayedTravelDuration = resolveSameSiteSequenceDisplayedTravelDuration(
      segment,
      displayOverrides,
      fallbackIndex,
      hovercardContext,
    );
    const displayedTravelDurationMinutes = Number(displayedTravelDuration.minutes);
    const safeDisplayedTravelDurationMinutes = Number.isFinite(displayedTravelDurationMinutes)
      && displayedTravelDurationMinutes >= 0
      ? displayedTravelDurationMinutes
      : 0;
    const serviceDurationMinutes = resolveTimelineDisplayServiceDurationMinutes(segment);
    const manualBufferMinutes = sameSiteManualBufferTotals.get(getSameSiteSequenceOverrideKey(segment, fallbackIndex))
      ?? resolveSequenceManualBufferMinutes(segment);
    const rawWaitMinutes = Number(segment?.waitMinutes);
    const waitMinutes = Number.isFinite(rawWaitMinutes) && rawWaitMinutes >= 0 ? rawWaitMinutes : 0;
    const displayedSegmentGeometryDurationMinutes = safeDisplayedTravelDurationMinutes + serviceDurationMinutes + manualBufferMinutes;
    const displayedSegmentTotalDurationMinutes = displayedSegmentGeometryDurationMinutes + waitMinutes;
    const displayedSegmentStartMinutes = resolveRenderSegmentStartMinutes(segment);
    // Utiliser la fin ancrée Gantt (backend, continue) pour que Fin[N] = Début[N+1].
    // Exception : segment fusionné avec le retour dépôt (la valeur ancrée est pré-fusion).
    const anchoredEnd = resolveRenderSegmentEndMinutes(segment);
    const isTerminalMerged = fallbackIndex === terminalMergeSourceIndex
      || segment?.isTerminalRouteMerged === true;
    const rawTerminalMergedEndMinutes = isTerminalMerged && isFiniteTimelineMinutes(segment?.terminalMergedStopEndMinutes)
      ? Number(segment.terminalMergedStopEndMinutes)
      : null;
    const durationDrivenTerminalEndMinutes = isTerminalMerged && displayedSegmentStartMinutes != null
      ? displayedSegmentStartMinutes + displayedSegmentGeometryDurationMinutes
      : null;
    const terminalMergedEndMinutes = isTerminalMerged
      ? (
          rawTerminalMergedEndMinutes != null
            ? Math.max(rawTerminalMergedEndMinutes, anchoredEnd ?? rawTerminalMergedEndMinutes)
            : (durationDrivenTerminalEndMinutes ?? anchoredEnd)
        )
      : null;
    const displayedSegmentEndMinutes = isTerminalMerged
      ? (terminalMergedEndMinutes != null
        ? terminalMergedEndMinutes
        : (displayedSegmentStartMinutes != null
          ? displayedSegmentStartMinutes + displayedSegmentGeometryDurationMinutes
          : anchoredEnd))
      : (anchoredEnd != null
        ? anchoredEnd
        : displayedSegmentStartMinutes != null
          ? displayedSegmentStartMinutes + displayedSegmentTotalDurationMinutes
          : null);

    return {
      ...segment,
      displayedSegmentEndAt: terminalMergedEndMinutes != null
        ? (rawTerminalMergedEndMinutes === terminalMergedEndMinutes ? (segment?.terminalMergedStopEndAt || null) : null)
        : (segment?.displayedSegmentEndAt || null),
      displayedSegmentEndMinutes,
      displayedSegmentTotalDurationMinutes,
      fleetDisplayedTravelUsesHereTraffic: displayedTravelDuration.usesHereTraffic === true,
    };
  });
}

function resolveExpandedTimelineGroupMemberWeight(memberSegment) {
  if (memberSegment?.isSameSiteSequenceGroup) {
    return 1;
  }

  const visualTimelineDurationMinutes = Number(memberSegment?.visualTimelineDurationMinutes);
  if (Number.isFinite(visualTimelineDurationMinutes) && visualTimelineDurationMinutes > 0) {
    return visualTimelineDurationMinutes;
  }

  const memberWidthPercent = Number(memberSegment?.widthPercent);
  if (Number.isFinite(memberWidthPercent) && memberWidthPercent > 0) {
    return memberWidthPercent;
  }

  return 1;
}

export function buildExpandedTimelineGroupMemberSegments(memberSegments = [], {
  leftPercent = 0,
  widthPercent = 0,
} = {}) {
  const resolvedSegments = Array.isArray(memberSegments) ? memberSegments.filter(Boolean) : [];
  if (!resolvedSegments.length) {
    return [];
  }

  const safeLeftPercent = Number.isFinite(Number(leftPercent)) ? Number(leftPercent) : 0;
  const safeWidthPercent = Number.isFinite(Number(widthPercent)) ? Math.max(Number(widthPercent), 0) : 0;
  const layoutWeights = resolvedSegments.map((memberSegment) => resolveExpandedTimelineGroupMemberWeight(memberSegment));
  const totalLayoutWeight = layoutWeights.reduce((total, weight) => total + weight, 0);
  let groupCursorPercent = safeLeftPercent;

  return resolvedSegments.map((memberSegment, memberIndex) => {
    const isLastMember = memberIndex === resolvedSegments.length - 1;
    const proportionalWidthPercent = totalLayoutWeight > 0
      ? (safeWidthPercent * layoutWeights[memberIndex]) / totalLayoutWeight
      : 0;
    const resolvedWidthPercent = isLastMember
      ? Math.max((safeLeftPercent + safeWidthPercent) - groupCursorPercent, 0)
      : Math.max(proportionalWidthPercent, 0);
    const layoutSegment = {
      ...memberSegment,
      leftPercent: groupCursorPercent,
      widthPercent: resolvedWidthPercent,
    };

    groupCursorPercent += resolvedWidthPercent;
    return layoutSegment;
  });
}

function parseGroupedHoverRouteSegmentIndex(value) {
  const routeSegmentIndex = Number.parseInt(value, 10);
  return Number.isInteger(routeSegmentIndex) ? routeSegmentIndex : null;
}

export function resolveGroupedHoverRouteSegmentIndex(memberSegments = []) {
  const resolvedMemberSegments = Array.isArray(memberSegments) ? memberSegments.filter(Boolean) : [];

  for (let memberIndex = resolvedMemberSegments.length - 1; memberIndex >= 0; memberIndex -= 1) {
    const memberSegment = resolvedMemberSegments[memberIndex];
    if (memberSegment?.isTerminalRouteMerged !== true) {
      continue;
    }

    const routeSegmentIndex = parseGroupedHoverRouteSegmentIndex(memberSegment?.routeSegmentIndex);
    if (routeSegmentIndex != null) {
      return routeSegmentIndex;
    }
  }

  for (let memberIndex = 0; memberIndex < resolvedMemberSegments.length; memberIndex += 1) {
    const routeSegmentIndex = parseGroupedHoverRouteSegmentIndex(resolvedMemberSegments[memberIndex]?.routeSegmentIndex);
    if (routeSegmentIndex != null) {
      return routeSegmentIndex;
    }
  }

  return null;
}

export function resolveTerminalMergedComparisonSourceSegments(rowSegments = [], mergedSegment = null) {
  const resolvedRowSegments = Array.isArray(rowSegments) ? rowSegments.filter(Boolean) : [];
  const mergedTripId = typeof mergedSegment?.tripId === "string" ? mergedSegment.tripId.trim() : "";

  if (mergedSegment?.isTerminalRouteMerged !== true) {
    return [];
  }

  const mergedRouteSegmentIndex = parseGroupedHoverRouteSegmentIndex(mergedSegment?.routeSegmentIndex);
  if (mergedRouteSegmentIndex == null) {
    return [];
  }

  const terminalSegmentIndex = resolvedRowSegments.findIndex((candidateSegment) => {
    if (candidateSegment?.stopType !== "depot_return") {
      return false;
    }

    const candidateTripId = typeof candidateSegment?.tripId === "string" ? candidateSegment.tripId.trim() : "";
    if (mergedTripId && candidateTripId && candidateTripId !== mergedTripId) {
      return false;
    }

    return parseGroupedHoverRouteSegmentIndex(candidateSegment?.routeSegmentIndex) === mergedRouteSegmentIndex;
  });

  if (terminalSegmentIndex <= 0) {
    return [];
  }

  const previousSegment = resolvedRowSegments[terminalSegmentIndex - 1] || null;
  const terminalSegment = resolvedRowSegments[terminalSegmentIndex] || null;

  return [previousSegment, terminalSegment].filter(Boolean);
}

function shouldRenderPriorityIdleItem(segment) {
  if (!segment?.hasPriorityTimeAnchor) {
    return false;
  }

  const rawWaitMinutes = segment?.waitMinutes;
  if (rawWaitMinutes != null && rawWaitMinutes !== "") {
    const waitMinutes = Number(rawWaitMinutes);
    if (Number.isFinite(waitMinutes)) {
      return waitMinutes > 0;
    }
  }

  return true;
}

function parseSequenceDistanceKm(distanceLabel = "") {
  if (typeof distanceLabel !== "string") {
    return null;
  }

  const match = distanceLabel.match(/-?\d+(?:[.,]\d+)?/);
  if (!match) {
    return null;
  }

  const parsedDistance = Number.parseFloat(match[0].replace(",", "."));
  return Number.isFinite(parsedDistance) ? parsedDistance : null;
}

function formatSequenceDistanceLabel(distanceKm) {
  if (distanceKm == null || !Number.isFinite(distanceKm) || distanceKm < 0) {
    return "";
  }

  return `${Math.round(distanceKm)} km`;
}

function formatMergedSameAddressQuantityLabel(quantityTons) {
  const normalizedQuantityTons = Number(quantityTons);
  if (!Number.isFinite(normalizedQuantityTons) || normalizedQuantityTons <= 0) {
    return "";
  }

  return `${normalizedQuantityTons.toFixed(1)} t`;
}

export function resolveMergedSameAddressQuantityTons(memberSegments = []) {
  const resolvedMemberSegments = Array.isArray(memberSegments) ? memberSegments.filter(Boolean) : [];
  const positiveQuantities = resolvedMemberSegments
    .map((segment) => Number(segment?.quantityTons))
    .filter((quantityTons) => Number.isFinite(quantityTons) && quantityTons > 0);

  if (!positiveQuantities.length) {
    return 0;
  }

  if (isMergedSameAddressLoadingDeliveryVisit(resolvedMemberSegments)) {
    const quantityByOrderKey = new Map();

    resolvedMemberSegments.forEach((segment) => {
      const orderKey = normalizeMergedSameAddressOrderKey(segment);
      const quantityTons = Number(segment?.quantityTons);
      if (!orderKey || !Number.isFinite(quantityTons) || quantityTons <= 0) {
        return;
      }

      quantityByOrderKey.set(orderKey, Math.max(quantityByOrderKey.get(orderKey) || 0, quantityTons));
    });

    const totalQuantityTons = Array.from(quantityByOrderKey.values()).reduce(
      (totalQuantityTons, quantityTons) => totalQuantityTons + quantityTons,
      0,
    );
    return Math.round(totalQuantityTons * 1000) / 1000;
  }

  return positiveQuantities.reduce((totalQuantityTons, quantityTons) => totalQuantityTons + quantityTons, 0);
}

export function resolveGroupedSegmentQuantityTons(memberSegments = []) {
  return resolveMergedSameAddressQuantityTons(memberSegments);
}

function resolveGroupedSegmentOrderLabel(segment = null) {
  const orderLabel = segment?.orderLabel || segment?.businessStep?.orderId || "";
  return typeof orderLabel === "string" ? orderLabel.trim() : "";
}

function resolveGroupedSegmentWorkOrderLabel(segment = null) {
  const workOrderLabel = segment?.workOrderLabel
    || segment?.businessStep?.workOrderNumber
    || segment?.businessStep?.work_order_number
    || "";
  return typeof workOrderLabel === "string" ? workOrderLabel.trim() : "";
}

function isCompartmentChip(chip = null) {
  const chipKind = typeof chip?.kind === "string"
    ? chip.kind.trim()
    : typeof chip?.key === "string"
      ? chip.key.trim()
      : "";
  return chipKind === "compartment";
}

export function joinGroupedSegmentCompartmentLabel(memberSegments = []) {
  const labels = [];
  const seenLabels = new Set();

  (Array.isArray(memberSegments) ? memberSegments.filter(Boolean) : []).forEach((segment) => {
    const chips = Array.isArray(segment?.chips) ? segment.chips : [];
    chips.forEach((chip) => {
      if (!isCompartmentChip(chip) || typeof chip?.label !== "string") {
        return;
      }

      chip.label.split(",").forEach((token) => {
        const label = token.trim();
        if (!label) {
          return;
        }

        const labelKey = label.toLocaleLowerCase("fr-CA");
        if (seenLabels.has(labelKey)) {
          return;
        }

        seenLabels.add(labelKey);
        labels.push(label);
      });
    });
  });

  return labels.join(", ");
}

function resolveGroupedSegmentCompartmentLabel(segment = null) {
  const label = joinGroupedSegmentCompartmentLabel([segment]);
  return label || "—";
}

function formatOperationalSequenceRange(startSequence, count = 1) {
  const start = Number(startSequence);
  const resolvedCount = Number(count);
  if (!Number.isInteger(start) || start <= 0) {
    return "";
  }

  const sequenceCount = Number.isInteger(resolvedCount) && resolvedCount > 0 ? resolvedCount : 1;
  return sequenceCount <= 1 ? String(start) : `${start}-${start + sequenceCount - 1}`;
}

// Chargé PM du soir : un seul passage au site, numéroté sur ses commandes (« 28-30 »), comme le lendemain matin.
export function resolvePmLoadingOrderCount(segment = null) {
  return segment?.isPmOperationalLoading === true
    ? Math.max(new Set((segment.endOfDayLoadOrderIds || []).filter(Boolean)).size, 1)
    : 1;
}

export function resolveMergedOperationalOrderCount(segment = null) {
  if (!isMergedOperationalMissionSegment(segment)) {
    return resolvePmLoadingOrderCount(segment);
  }

  const missionOrderIds = Array.isArray(segment?.missionOrderIds)
    ? segment.missionOrderIds.filter((orderId) => typeof orderId === "string" && orderId.trim())
    : [];
  if (missionOrderIds.length > 0) {
    return missionOrderIds.length;
  }

  const orderKeys = new Set(
    (Array.isArray(segment?.mergedMemberSegments) ? segment.mergedMemberSegments : [])
      .map((memberSegment) => normalizeMergedSameAddressOrderKey(memberSegment))
      .filter(Boolean),
  );
  if (orderKeys.size > 0) {
    return orderKeys.size;
  }

  return Math.max(buildGroupedSegmentOrderHoverEntries(segment?.mergedMemberSegments).length, 1);
}

export function resolveDisplayedOperationalSequenceLabel(segment = null) {
  const assignedLabel = typeof segment?.mergedOrderSequenceLabel === "string"
    ? segment.mergedOrderSequenceLabel.trim()
    : "";
  if (assignedLabel) {
    return assignedLabel;
  }

  if (isMergedOperationalMissionSegment(segment)) {
    const mergedOrderSequenceLabel = resolveMergedSameAddressOrderSequenceLabel(segment?.mergedMemberSegments);
    if (mergedOrderSequenceLabel) {
      return mergedOrderSequenceLabel;
    }

    const mergedSequenceLabel = typeof segment?.mergedSequenceLabel === "string"
      ? segment.mergedSequenceLabel.trim()
      : "";
    if (mergedSequenceLabel) {
      return mergedSequenceLabel;
    }
  }

  const displaySequence = Number(segment?.routeSequenceDisplaySequence);
  if (Number.isInteger(displaySequence) && displaySequence > 0) {
    return String(displaySequence);
  }

  const globalSequence = Number(segment?.globalSequence);
  return Number.isInteger(globalSequence) && globalSequence > 0 ? String(globalSequence) : "";
}

function normalizeGroupedSegmentMetaValue(value = "") {
  return typeof value === "string" ? value.trim() : "";
}

function resolveGroupedSegmentClientLabel(segment = null) {
  const stopType = normalizeGroupedSegmentMetaValue(segment?.stopType).toLowerCase();
  const deliveryClientName = normalizeGroupedSegmentMetaValue(
    segment?.businessStep?.orderDetails?.deliveryClientName
    || segment?.operationOwnerLabel
    || "",
  );
  const loadingClientName = normalizeGroupedSegmentMetaValue(
    segment?.businessStep?.orderDetails?.loadingClientName
    || segment?.loading_client_name
    || "",
  );
  const destinationLabel = normalizeGroupedSegmentMetaValue(segment?.toLabel);
  const originLabel = normalizeGroupedSegmentMetaValue(segment?.fromLabel);

  // Regroupement chargement → nom de livraison ; déchargement → nom de chargement.
  if (stopType === "loading") {
    return deliveryClientName || destinationLabel || "—";
  }

  if (stopType === "delivery") {
    return loadingClientName || originLabel || "—";
  }

  return deliveryClientName || loadingClientName || destinationLabel || originLabel || "—";
}

function resolveGroupedSegmentDisplaySequence(segment = null) {
  const candidates = [
    segment?.routeSequenceDisplaySequence,
    segment?.globalSequence,
    segment?.businessStep?.displaySequence,
  ];

  for (const candidate of candidates) {
    const displaySequence = Number(candidate);
    if (Number.isInteger(displaySequence) && displaySequence > 0) {
      return displaySequence;
    }
  }

  return null;
}

export function resolveMergedSameAddressOrderSequenceLabel(memberSegments = []) {
  const resolvedMemberSegments = Array.isArray(memberSegments) ? memberSegments.filter(Boolean) : [];
  const firstSequenceByOrderKey = new Map();

  resolvedMemberSegments.forEach((segment) => {
    const orderKey = normalizeMergedSameAddressOrderKey(segment);
    const displaySequence = resolveGroupedSegmentDisplaySequence(segment);
    if (!orderKey || !Number.isInteger(displaySequence) || displaySequence <= 0) {
      return;
    }

    const existingSequence = firstSequenceByOrderKey.get(orderKey);
    if (!Number.isInteger(existingSequence) || displaySequence < existingSequence) {
      firstSequenceByOrderKey.set(orderKey, displaySequence);
    }
  });

  const orderSequences = Array.from(firstSequenceByOrderKey.values());
  if (!orderSequences.length) {
    return "";
  }

  const startSequence = Math.min(...orderSequences);
  const endSequence = startSequence + orderSequences.length - 1;
  return startSequence === endSequence
    ? String(startSequence)
    : `${startSequence}-${endSequence}`;
}

export function buildGroupedSegmentOrderHoverEntries(memberSegments = []) {
  const resolvedMemberSegments = Array.isArray(memberSegments) ? memberSegments.filter(Boolean) : [];
  const orderEntrySegments = isMergedSameAddressLoadingDeliveryVisit(resolvedMemberSegments)
    ? Array.from(
        resolvedMemberSegments.reduce((segmentsByOrderKey, segment) => {
          const orderKey = normalizeMergedSameAddressOrderKey(segment);
          if (!orderKey) {
            return segmentsByOrderKey;
          }

          const currentSegment = segmentsByOrderKey.get(orderKey);
          const currentQuantityTons = Number(currentSegment?.quantityTons);
          const candidateQuantityTons = Number(segment?.quantityTons);
          if (
            !currentSegment
            || (
              Number.isFinite(candidateQuantityTons)
              && candidateQuantityTons > 0
              && (!Number.isFinite(currentQuantityTons) || candidateQuantityTons > currentQuantityTons)
            )
          ) {
            segmentsByOrderKey.set(orderKey, segment);
          }

          return segmentsByOrderKey;
        }, new Map()).values(),
      )
    : resolvedMemberSegments;

  return orderEntrySegments.map((segment) => {
    const quantityTons = Number(segment?.quantityTons);
    const quantityLabel = Number.isFinite(quantityTons) && quantityTons > 0
      ? formatMergedSameAddressQuantityLabel(quantityTons)
      : "—";

    return {
      clientLabel: resolveGroupedSegmentClientLabel(segment),
      compartmentFillLabel: buildCompartmentFillIndicatorLabel(normalizeCompartmentFillPercent(segment?.compartmentFillPercent)),
      compartmentLabel: resolveGroupedSegmentCompartmentLabel(segment),
      globalSequence: segment?.globalSequence ?? null,
      orderLabel: resolveGroupedSegmentOrderLabel(segment) || "—",
      quantityLabel,
      workOrderLabel: resolveGroupedSegmentWorkOrderLabel(segment),
    };
  });
}

export function buildGroupedSegmentOrdersHoverCardDetails(memberSegments = []) {
  const entries = buildGroupedSegmentOrderHoverEntries(memberSegments);
  if (!entries.length) {
    return null;
  }
  const resolvedMemberSegments = Array.isArray(memberSegments) ? memberSegments.filter(Boolean) : [];
  const missionGroupKeys = new Set(
    resolvedMemberSegments
      .map((segment) => normalizeAspiratorMissionKey(segment?.aspiratorMissionGroupKey))
      .filter(Boolean),
  );
  const isAspiratorMissionGroup = missionGroupKeys.size === 1
    && resolvedMemberSegments.every((segment) => segment?.requiresAspirator === true);

  return {
    type: "formula",
    lines: entries.map((entry) => ({
      label: entry.orderLabel,
      secondary: entry.workOrderLabel || "",
      value: [entry.clientLabel, entry.compartmentLabel !== "—" ? entry.compartmentLabel : "", entry.compartmentFillLabel]
        .filter(Boolean)
        .join(" · "),
    })),
    note: entries.length > 1
      ? (
          isAspiratorMissionGroup
            ? "Commandes regroupées dans cette mission Aspiration."
            : "Commandes regroupées sur ce même arrêt."
        )
      : "",
  };
}

export function buildGroupedSegmentQuantityHoverCardDetails(memberSegments = []) {
  const entries = buildGroupedSegmentOrderHoverEntries(memberSegments);
  if (!entries.length) {
    return null;
  }

  const totalTons = resolveGroupedSegmentQuantityTons(memberSegments);
  const totalLabel = totalTons > 0 ? formatMergedSameAddressQuantityLabel(totalTons) : "";

  return {
    type: "formula",
    layout: "quantity-breakdown",
    lines: entries.map((entry) => ({
      compartments: entry.compartmentLabel !== "—" ? entry.compartmentLabel : "",
      label: entry.clientLabel,
      order: entry.orderLabel !== "—" ? entry.orderLabel : "",
      quantity: entry.quantityLabel,
    })),
    note: entries.length > 1 ? "Détail du tonnage et des compartiments par commande." : "",
    total: totalLabel,
    totalLabel: "Tonnage total",
  };
}

export function buildMergedSameAddressQuantityHoverCard(quantityLabel = "", {
  isSingleCount = false,
} = {}) {
  if (!quantityLabel) {
    return null;
  }

  return {
    type: "formula",
    lines: [
      {
        label: isSingleCount ? "Quantité commande" : "Quantité cumulée",
        value: quantityLabel,
      },
    ],
    note: isSingleCount
      ? "Commande aspirateur fusionnée: le tonnage est compté une seule fois même si le chargement et le déchargement partagent le segment."
      : "Le tonnage affiché additionne les étapes fusionnées dans ce segment unique.",
    total: quantityLabel,
    totalLabel: "Quantité",
  };
}

function buildMergedSameAddressCompartmentHoverCard(compartmentLabel = "") {
  if (!compartmentLabel) {
    return null;
  }

  return {
    type: "formula",
    lines: [
      {
        label: "Entrée",
        value: compartmentLabel,
      },
      {
        label: "Sortie",
        value: compartmentLabel,
      },
    ],
    note: "Commande aspirateur fusionnée: le même compartiment est affiché en entrée et en sortie.",
    total: `${compartmentLabel} · entrée/sortie`,
    totalLabel: "Compartiment",
  };
}

function decorateMergedSameAddressCompartmentChip(chip = null, memberSegments = []) {
  if (!isCompartmentChip(chip)) {
    return chip;
  }

  const unionLabel = joinGroupedSegmentCompartmentLabel(memberSegments);
  const compartmentLabel = unionLabel || (typeof chip?.label === "string" ? chip.label.trim() : "");
  if (!compartmentLabel) {
    return chip;
  }

  if (isMergedSameAddressLoadingDeliveryPair(memberSegments)) {
    const displayLabel = `${compartmentLabel} · entrée/sortie`;
    return {
      ...chip,
      displayLabel,
      hoverCardDetails: buildMergedSameAddressCompartmentHoverCard(compartmentLabel),
      hoverCardTitle: "Compartiment aspirateur",
      key: "compartment",
      kind: "compartment",
      label: compartmentLabel,
      stopType: "aspiration",
      title: `Compartiment(s) : ${displayLabel}`,
    };
  }

  return {
    ...chip,
    key: "compartment",
    kind: "compartment",
    label: compartmentLabel,
    title: `Compartiment(s) : ${compartmentLabel}`,
  };
}

function resolveMergedQuantityHoverCardDetails(memberSegments = [], quantityLabel = "", {
  isSingleCount = false,
} = {}) {
  if (buildGroupedSegmentOrderHoverEntries(memberSegments).length > 1) {
    return buildGroupedSegmentQuantityHoverCardDetails(memberSegments);
  }

  return buildMergedSameAddressQuantityHoverCard(quantityLabel, { isSingleCount });
}

function withMergedCompartmentChip(chips = [], memberSegments = []) {
  const resolvedChips = Array.isArray(chips) ? chips.filter(Boolean) : [];
  const unionLabel = joinGroupedSegmentCompartmentLabel(memberSegments);
  if (!unionLabel) {
    return resolvedChips.map((chip) => decorateMergedSameAddressCompartmentChip(chip, memberSegments));
  }

  if (resolvedChips.some(isCompartmentChip)) {
    return resolvedChips.map((chip) => decorateMergedSameAddressCompartmentChip(chip, memberSegments));
  }

  return [
    decorateMergedSameAddressCompartmentChip({
      key: "compartment",
      kind: "compartment",
      label: unionLabel,
    }, memberSegments),
    ...resolvedChips,
  ];
}

function resolveMergedSameAddressAspiratorTimeChip(memberSegments = []) {
  let bestMinutes = 0;
  let bestChip = null;

  (Array.isArray(memberSegments) ? memberSegments : []).forEach((segment) => {
    const directMinutes = resolveTimelineAspiratorServiceMinutes(segment);
    const aspiratorChip = (Array.isArray(segment?.chips) ? segment.chips : []).find((chip) => (
      chip?.kind === "aspirator_time" || chip?.key === "aspirator_time"
    )) || null;
    const chipMinutes = typeof aspiratorChip?.label === "string"
      ? Number(aspiratorChip.label.replace(/[^\d]/g, ""))
      : 0;
    const candidateMinutes = Math.max(
      Number.isFinite(directMinutes) ? directMinutes : 0,
      Number.isFinite(chipMinutes) ? chipMinutes : 0,
    );

    if (candidateMinutes > bestMinutes) {
      bestMinutes = candidateMinutes;
      bestChip = aspiratorChip;
    }
  });

  if (bestMinutes <= 0) {
    return null;
  }

  return {
    ...(bestChip || {}),
    key: "aspirator_time",
    kind: "aspirator_time",
    label: `+${Math.round(bestMinutes)} min`,
    tone: bestChip?.tone || "aspiration",
    title: bestChip?.title
      || `Temps aspirateur : +${Math.round(bestMinutes)} min. Temps du camion (la base) plus les tonnes au débit d'aspiration (Paramètres système), quand la commande exige l'aspirateur.`,
  };
}

function buildMergedSameAddressChips(memberSegments = [], themedSegment = null) {
  const aspiratorTimeChip = resolveMergedSameAddressAspiratorTimeChip(memberSegments);
  const resolvedBaseChips = (Array.isArray(themedSegment?.chips) ? themedSegment.chips.filter(Boolean) : [])
    .filter((chip) => chip?.kind !== "aspirator_time" && chip?.key !== "aspirator_time");
  const appendAspiratorTimeChip = (chips = []) => aspiratorTimeChip
    ? [...chips, aspiratorTimeChip]
    : chips;

  const chipsWithCompartment = withMergedCompartmentChip(resolvedBaseChips, memberSegments);

  if (!chipsWithCompartment.length) {
    return appendAspiratorTimeChip(chipsWithCompartment);
  }

  const isSingleCount = isMergedSameAddressLoadingDeliveryPair(memberSegments);
  const mergedQuantityTons = resolveMergedSameAddressQuantityTons(memberSegments);
  const mergedQuantityLabel = formatMergedSameAddressQuantityLabel(mergedQuantityTons);
  const quantityHoverCardDetails = resolveMergedQuantityHoverCardDetails(memberSegments, mergedQuantityLabel, {
    isSingleCount,
  });
  if (!mergedQuantityLabel) {
    return appendAspiratorTimeChip(chipsWithCompartment);
  }

  let hasQuantityChip = false;
  const mergedChips = chipsWithCompartment.map((chip) => {
    const chipKind = typeof chip?.kind === "string"
      ? chip.kind.trim()
      : typeof chip?.key === "string"
        ? chip.key.trim()
        : "";

    if (chipKind !== "qty") {
      return chip;
    }

    hasQuantityChip = true;
    return {
      ...chip,
      hoverCardDetails: quantityHoverCardDetails,
      hoverCardTitle: "Quantité",
      label: mergedQuantityLabel,
      title: isSingleCount ? `Quantité aspirateur : ${mergedQuantityLabel}` : `Quantité cumulée : ${mergedQuantityLabel}`,
    };
  });

  if (hasQuantityChip) {
    return appendAspiratorTimeChip(mergedChips);
  }

  return appendAspiratorTimeChip([
    ...mergedChips,
    {
      key: "qty",
      kind: "qty",
      hoverCardDetails: quantityHoverCardDetails,
      hoverCardTitle: "Quantité",
      label: mergedQuantityLabel,
      title: isSingleCount ? `Quantité aspirateur : ${mergedQuantityLabel}` : `Quantité cumulée : ${mergedQuantityLabel}`,
    },
  ]);
}

function getSameSiteSequenceOverrideKey(segment, fallbackIndex = 0) {
  return `${segment?.assignmentId || "assignment"}::${segment?.tripId || "trip"}::${segment?.globalSequence || fallbackIndex + 1}`;
}

export function getSameSiteSequenceDisplayOverride(segment, displayOverrides, fallbackIndex = 0) {
  if (!(displayOverrides instanceof Map)) {
    return null;
  }

  return displayOverrides.get(getSameSiteSequenceOverrideKey(segment, fallbackIndex)) || null;
}

export function getSameSiteSequenceDisplayDistanceLabel(segment, displayOverrides, fallbackIndex = 0) {
  const override = getSameSiteSequenceDisplayOverride(segment, displayOverrides, fallbackIndex);
  const sourceLabel = typeof override?.distanceLabel === "string" && override.distanceLabel.trim()
    ? override.distanceLabel.trim()
    : (typeof segment?.distanceLabel === "string" && segment.distanceLabel.trim()
      ? segment.distanceLabel.trim()
      : "—");

  if (sourceLabel === "—") {
    return sourceLabel;
  }

  const distanceKm = parseSequenceDistanceKm(sourceLabel);
  return distanceKm != null ? formatSequenceDistanceLabel(distanceKm) : sourceLabel;
}

function hasTimelineAbsorbedGap(segment) {
  const absorbedGapMinutes = Number(segment?.fleetTimelineAbsorbedGapMinutes);
  return Number.isFinite(absorbedGapMinutes) && absorbedGapMinutes > 0;
}

function resolveAbsorbedGapDurationField(segment, fields = []) {
  if (!hasTimelineAbsorbedGap(segment)) {
    return null;
  }

  for (const field of fields) {
    const durationMinutes = Number(segment?.[field]);
    if (Number.isFinite(durationMinutes) && durationMinutes >= 0) {
      return durationMinutes;
    }
  }

  return null;
}

function resolveSequenceTravelMinutes(segment) {
  const travelDurationMinutes = Number(segment?.travelDurationMinutes);
  return Number.isFinite(travelDurationMinutes) && travelDurationMinutes >= 0 ? travelDurationMinutes : 0;
}

function resolveSequenceScheduledTravelMinutes(segment) {
  const scheduledTravelDurationMinutes = Number(segment?.routeScheduledTravelDurationMinutes);
  return Number.isFinite(scheduledTravelDurationMinutes) && scheduledTravelDurationMinutes >= 0
    ? scheduledTravelDurationMinutes
    : null;
}

function resolveSequenceManualBufferMinutes(segment) {
  const manualBufferMinutes = Number(segment?.manualBufferMinutes);
  return Number.isFinite(manualBufferMinutes) && manualBufferMinutes >= 0 ? manualBufferMinutes : 0;
}

function resolveSequenceDisplayTravelMinutes(segment) {
  const displayTravelDurationMinutes = Number(segment?.displayTravelDurationMinutes);
  if (Number.isFinite(displayTravelDurationMinutes) && displayTravelDurationMinutes >= 0) {
    return displayTravelDurationMinutes;
  }

  return resolveSequenceTravelMinutes(segment);
}

function resolveSequenceLegacyDisplayTravelMinutes(segment) {
  const legacyDisplayTravelDurationMinutes = Number(segment?.legacyDisplayTravelDurationMinutes);
  if (Number.isFinite(legacyDisplayTravelDurationMinutes) && legacyDisplayTravelDurationMinutes >= 0) {
    return legacyDisplayTravelDurationMinutes;
  }

  return resolveSequenceDisplayTravelMinutes(segment);
}

export function getSameSiteSequenceDisplayTravelDurationMinutes(segment, displayOverrides, fallbackIndex = 0) {
  const absorbedGapTravelDurationMinutes = resolveAbsorbedGapDurationField(segment, [
    "displayTravelDurationMinutes",
    "fleetDisplayedTravelDurationMinutes",
    "travelDurationMinutes",
  ]);
  if (absorbedGapTravelDurationMinutes != null) {
    return absorbedGapTravelDurationMinutes;
  }

  const override = getSameSiteSequenceDisplayOverride(segment, displayOverrides, fallbackIndex);
  if (Number.isFinite(override?.displayTravelDurationMinutes) && override.displayTravelDurationMinutes >= 0) {
    return override.displayTravelDurationMinutes;
  }

  if (Number.isFinite(override?.travelDurationMinutes) && override.travelDurationMinutes >= 0) {
    return override.travelDurationMinutes;
  }

  return resolveSequenceDisplayTravelMinutes(segment);
}

export function getSameSiteSequenceLegacyTravelDurationMinutes(segment, displayOverrides, fallbackIndex = 0) {
  const absorbedGapLegacyTravelDurationMinutes = resolveAbsorbedGapDurationField(segment, [
    "legacyDisplayTravelDurationMinutes",
    "displayTravelDurationMinutes",
    "travelDurationMinutes",
  ]);
  if (absorbedGapLegacyTravelDurationMinutes != null) {
    return absorbedGapLegacyTravelDurationMinutes;
  }

  const override = getSameSiteSequenceDisplayOverride(segment, displayOverrides, fallbackIndex);
  if (Number.isFinite(override?.legacyDisplayTravelDurationMinutes) && override.legacyDisplayTravelDurationMinutes >= 0) {
    return override.legacyDisplayTravelDurationMinutes;
  }

  return resolveSequenceLegacyDisplayTravelMinutes(segment);
}

function resolveSameSiteSequenceScheduledTravelDurationMinutes(segment, displayOverrides, fallbackIndex = 0) {
  const absorbedGapScheduledTravelDurationMinutes = resolveAbsorbedGapDurationField(segment, [
    "routeScheduledTravelDurationMinutes",
    "displayTravelDurationMinutes",
    "travelDurationMinutes",
  ]);
  if (absorbedGapScheduledTravelDurationMinutes != null) {
    return absorbedGapScheduledTravelDurationMinutes;
  }

  const override = getSameSiteSequenceDisplayOverride(segment, displayOverrides, fallbackIndex);
  if (Number.isFinite(override?.routeScheduledTravelDurationMinutes) && override.routeScheduledTravelDurationMinutes >= 0) {
    return override.routeScheduledTravelDurationMinutes;
  }

  return resolveSequenceScheduledTravelMinutes(segment);
}

export function resolveSameSiteSequenceDisplayedTravelDuration(
  segment,
  displayOverrides,
  fallbackIndex = 0,
  hovercardContext = null,
) {
  const override = getSameSiteSequenceDisplayOverride(segment, displayOverrides, fallbackIndex);
  const displayedTravelDurationMinutes = getSameSiteSequenceDisplayTravelDurationMinutes(segment, displayOverrides, fallbackIndex);
  const usesHereTrafficDisplay = override?.fleetDisplayedTravelUsesHereTraffic === true
    || segment?.fleetDisplayedTravelUsesHereTraffic === true;
  const hereTimeAwareRoutingEnabled = hovercardContext?.here_time_aware_routing_enabled === true;
  if (usesHereTrafficDisplay && Number.isFinite(displayedTravelDurationMinutes) && displayedTravelDurationMinutes >= 0) {
    return {
      minutes: displayedTravelDurationMinutes,
      usesHereTraffic: true,
    };
  }

  if (hereTimeAwareRoutingEnabled) {
    const scheduledTravelDurationMinutes = resolveSameSiteSequenceScheduledTravelDurationMinutes(
      segment,
      displayOverrides,
      fallbackIndex,
    );
    if (shouldUseFleetSegmentComparisonCurrentDuration(segment, scheduledTravelDurationMinutes)) {
      return {
        minutes: scheduledTravelDurationMinutes,
        usesHereTraffic: true,
      };
    }
  }

  return {
    minutes: displayedTravelDurationMinutes,
    usesHereTraffic: false,
  };
}

export function buildFleetGroupedComparisonSummary(
  memberSegments = [],
  {
    comparisonPayloadByKey = null,
    dispatchDate = "",
    displayOverrides = null,
  } = {},
) {
  const resolvedMemberSegments = Array.isArray(memberSegments) ? memberSegments.filter(Boolean) : [];
  const comparisonMap = comparisonPayloadByKey instanceof Map ? comparisonPayloadByKey : new Map();
  const comparisonPayloadByKeyForMember = new Map();
  const terminalMergedMemberIndex = resolvedMemberSegments.findIndex((segment) => segment?.isTerminalRouteMerged === true);
  const terminalMergedMetricSegments = terminalMergedMemberIndex > 0
    ? [resolvedMemberSegments[0]]
    : resolvedMemberSegments;
  const terminalMergedComparisonSegments = terminalMergedMemberIndex > 0
    ? [resolvedMemberSegments[terminalMergedMemberIndex]]
    : resolvedMemberSegments;
  const currentDurationMinutes = sumFleetSegmentMinutes(
    terminalMergedMetricSegments.map((segment, memberIndex) => getSameSiteSequenceDisplayTravelDurationMinutes(segment, displayOverrides, memberIndex)),
  );
  const legacyDurationMinutes = sumFleetSegmentMinutes(
    terminalMergedMetricSegments.map((segment, memberIndex) => getSameSiteSequenceLegacyTravelDurationMinutes(segment, displayOverrides, memberIndex)),
  );
  const comparisonKeys = [];
  const seenComparisonKeys = new Set();

  terminalMergedComparisonSegments.forEach((segment) => {
    const comparisonKey = buildFleetSegmentComparisonCacheKey({
      dispatchDate,
      routeSegmentIndex: segment?.routeSegmentIndex,
      tripId: segment?.tripId,
    });

    if (!comparisonKey || seenComparisonKeys.has(comparisonKey)) {
      return;
    }

    seenComparisonKeys.add(comparisonKey);
    comparisonKeys.push(comparisonKey);
    comparisonPayloadByKeyForMember.set(comparisonKey, segment);
  });

  const currentHereTrafficDurationMinutes = sumFleetSegmentMinutes(
    comparisonKeys.map((comparisonKey) => {
      const segment = comparisonPayloadByKeyForMember.get(comparisonKey);
      const payload = comparisonMap.get(comparisonKey);
      const payloadCurrentDurationMinutes = Number(payload?.current_duration_minutes);
      const metricSegment = terminalMergedMemberIndex > 0 ? terminalMergedMetricSegments[0] : segment;

      if (payload?.status === "available" && shouldUseFleetSegmentComparisonCurrentDuration(segment, payloadCurrentDurationMinutes)) {
        return payloadCurrentDurationMinutes;
      }

      const memberIndex = terminalMergedMemberIndex > 0 ? 0 : resolvedMemberSegments.indexOf(segment);
      const scheduledTravelDurationMinutes = resolveSameSiteSequenceScheduledTravelDurationMinutes(
        metricSegment,
        displayOverrides,
        memberIndex >= 0 ? memberIndex : 0,
      );
      return shouldUseFleetSegmentComparisonCurrentDuration(segment, scheduledTravelDurationMinutes)
        ? scheduledTravelDurationMinutes
        : null;
    }),
  );

  const comparisonScopeCovered = comparisonKeys.length > 0
    && comparisonKeys.every((comparisonKey) => {
      const payload = comparisonMap.get(comparisonKey);
      const comparisonDurationMinutes = Number(payload?.comparison_duration_minutes);
      return payload?.status === "available"
        && Number.isFinite(comparisonDurationMinutes)
        && comparisonDurationMinutes >= 0;
    });
  const comparisonDurationMinutes = comparisonScopeCovered
    ? comparisonKeys.reduce((totalMinutes, comparisonKey) => {
      return totalMinutes + Number(comparisonMap.get(comparisonKey)?.comparison_duration_minutes || 0);
    }, 0)
    : null;

  return {
    comparisonDurationMinutes,
    comparisonScopeCovered,
    comparisonSegmentCount: comparisonKeys.length,
    currentDurationMinutes,
    currentHereTrafficDurationMinutes,
    legacyDurationMinutes,
  };
}

function parseDisplayedDurationChipMinutes(label = "") {
  const normalizedLabel = typeof label === "string"
    ? label.replace(/^≈\s*/u, "").trim()
    : "";

  if (!normalizedLabel || normalizedLabel === "—") {
    return null;
  }

  const hoursMatch = normalizedLabel.match(/(\d+)\s*h\b/i);
  const minutesMatch = normalizedLabel.match(/(\d+)\s*min\b/i);

  if (!hoursMatch && !minutesMatch) {
    return null;
  }

  const hours = hoursMatch ? Number(hoursMatch[1]) : 0;
  const minutes = minutesMatch ? Number(minutesMatch[1]) : 0;
  const totalMinutes = (hours * 60) + minutes;

  return Number.isFinite(totalMinutes) ? totalMinutes : null;
}

function resolveDisplayedDurationChipMinutes(chips = []) {
  const resolvedChips = Array.isArray(chips) ? chips.filter(Boolean) : [];
  const durationChip = resolvedChips.find((chip) => chip?.key === "dur" || chip?.kind === "dur") || null;

  return durationChip ? parseDisplayedDurationChipMinutes(durationChip.label) : null;
}

export function shouldSyncDisplayedTravelDurationChip(
  segment,
  displayedTravelDurationMinutes,
  { showsHereTrafficDuration = false } = {},
) {
  const normalizedDisplayedTravelDurationMinutes = Number(displayedTravelDurationMinutes);
  if (!Number.isFinite(normalizedDisplayedTravelDurationMinutes) || normalizedDisplayedTravelDurationMinutes < 0) {
    return false;
  }

  if (showsHereTrafficDuration) {
    return true;
  }

  const rawDisplayTravelDurationMinutes = resolveSequenceDisplayTravelMinutes(segment);
  if (!Number.isFinite(rawDisplayTravelDurationMinutes)
    || Math.abs(normalizedDisplayedTravelDurationMinutes - rawDisplayTravelDurationMinutes) > 1e-9) {
    return true;
  }

  const chipDisplayTravelDurationMinutes = resolveDisplayedDurationChipMinutes(segment?.chips);
  return chipDisplayTravelDurationMinutes == null
    || Math.abs(normalizedDisplayedTravelDurationMinutes - chipDisplayTravelDurationMinutes) > 1e-9;
}

export function buildFleetSegmentComparisonCacheKey({ dispatchDate = "", routeSegmentIndex = null, tripId = "" } = {}) {
  const normalizedTripId = typeof tripId === "string" ? tripId.trim() : "";
  const normalizedRouteSegmentIndex = Number.parseInt(routeSegmentIndex, 10);
  const normalizedDispatchDate = typeof dispatchDate === "string" ? dispatchDate.trim() : "";

  if (!normalizedTripId || !Number.isInteger(normalizedRouteSegmentIndex) || normalizedRouteSegmentIndex < 1) {
    return "";
  }

  return `${normalizedDispatchDate || "dispatch"}::${normalizedTripId}::${normalizedRouteSegmentIndex}`;
}

function hasPositiveFleetSegmentReferenceDuration(segment) {
  return [
    segment?.routeScheduledTravelDurationMinutes,
    segment?.travelDurationMinutes,
    segment?.displayTravelDurationMinutes,
    segment?.fleetDisplayedTravelDurationMinutes,
    segment?.legacyDisplayTravelDurationMinutes,
    resolveDisplayedDurationChipMinutes(segment?.chips),
  ].some((value) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) && numericValue > 0;
  });
}

function hasPositiveFleetSegmentDistance(segment) {
  const numericDistanceKm = Number(segment?.distanceKm);
  if (Number.isFinite(numericDistanceKm) && numericDistanceKm > 0) {
    return true;
  }

  const distanceLabels = [segment?.distanceLabel];
  (Array.isArray(segment?.chips) ? segment.chips : []).forEach((chip) => {
    const chipKind = typeof chip?.kind === "string"
      ? chip.kind.trim()
      : typeof chip?.key === "string"
        ? chip.key.trim()
        : "";

    if (chipKind === "dist" && typeof chip?.label === "string") {
      distanceLabels.push(chip.label);
    }
  });

  return distanceLabels.some((distanceLabel) => {
    const distanceKm = parseSequenceDistanceKm(distanceLabel);
    return distanceKm != null && distanceKm > 0;
  });
}

export function shouldUseFleetSegmentComparisonCurrentDuration(segment, currentDurationMinutes) {
  const normalizedCurrentDurationMinutes = Number(currentDurationMinutes);
  if (!Number.isFinite(normalizedCurrentDurationMinutes) || normalizedCurrentDurationMinutes < 0) {
    return false;
  }

  if (normalizedCurrentDurationMinutes > 0) {
    return true;
  }

  const hasPositiveDistance = hasPositiveFleetSegmentDistance(segment);
  return !hasPositiveDistance && !hasPositiveFleetSegmentReferenceDuration(segment);
}

function sumFleetSegmentMinutes(values = []) {
  const resolvedValues = Array.isArray(values) ? values : [values];
  let hasValue = false;
  let totalMinutes = 0;

  resolvedValues.forEach((value) => {
    if (value == null || value === "") {
      return;
    }

    const numericValue = Number(value);
    if (!Number.isFinite(numericValue) || numericValue < 0) {
      return;
    }

    hasValue = true;
    totalMinutes += numericValue;
  });

  return hasValue ? totalMinutes : null;
}

export function applyFleetSegmentComparisonDurations(
  segments = [],
  comparisonPayloadByKey = null,
  hovercardContext = null,
  dispatchDate = "",
) {
  const resolvedSegments = Array.isArray(segments) ? segments.filter(Boolean) : [];
  const comparisonMap = comparisonPayloadByKey instanceof Map ? comparisonPayloadByKey : new Map();

  if (hovercardContext?.here_time_aware_routing_enabled !== true || !resolvedSegments.length || !comparisonMap.size) {
    return resolvedSegments;
  }

  return resolvedSegments.map((segment) => {
    const comparisonKey = buildFleetSegmentComparisonCacheKey({
      dispatchDate,
      routeSegmentIndex: segment?.routeSegmentIndex,
      tripId: segment?.tripId,
    });

    if (!comparisonKey) {
      return segment;
    }

    const payload = comparisonMap.get(comparisonKey);
    const currentDurationMinutes = Number(payload?.current_duration_minutes);
    const useCurrentDuration = payload?.status === "available"
      && shouldUseFleetSegmentComparisonCurrentDuration(segment, currentDurationMinutes);

    if (!useCurrentDuration) {
      return segment;
    }

    return {
      ...segment,
      displayTravelDurationMinutes: currentDurationMinutes,
      fleetDisplayedTravelDurationMinutes: currentDurationMinutes,
      fleetDisplayedTravelUsesHereTraffic: true,
      liveComparisonTravelDurationMinutes: currentDurationMinutes,
    };
  });
}

function formatSequenceDisplayTravelChipLabel(value, { approximate = false } = {}) {
  const durationMinutes = Number(value);
  if (!Number.isFinite(durationMinutes) || durationMinutes < 0) {
    return "—";
  }

  if (durationMinutes === 0) {
    return "0 min";
  }

  const formattedLabel = formatDurationShort(durationMinutes) || "0 min";
  return approximate ? `≈ ${formattedLabel}` : formattedLabel;
}

export function buildSameSiteSequenceHoverMetaChips(
  segment,
  displayOverrides,
  {
    fallbackIndex = 0,
    hideFollowerDurationChips = true,
  } = {},
) {
  const segmentChips = Array.isArray(segment?.chips) ? segment.chips.filter(Boolean) : [];
  const shouldKeepDurationChips = !hideFollowerDurationChips || shouldDisplaySameSiteDurationChips(segment);
  const displayDistanceLabel = getSameSiteSequenceDisplayDistanceLabel(segment, displayOverrides, fallbackIndex);
  const normalizedDisplayDistanceLabel = typeof displayDistanceLabel === "string" ? displayDistanceLabel.trim() : "";
  const rawDistanceLabel = typeof segment?.distanceLabel === "string" ? segment.distanceLabel.trim() : "";
  const displayTravelDurationMinutes = getSameSiteSequenceDisplayTravelDurationMinutes(segment, displayOverrides, fallbackIndex);
  const shouldOverrideTravelChip = shouldSyncDisplayedTravelDurationChip(segment, displayTravelDurationMinutes);

  return segmentChips
    .filter((chip) => shouldKeepDurationChips || (chip?.key !== "dur" && chip?.key !== "svc"))
    .map((chip) => {
      if (chip?.key === "dist" && normalizedDisplayDistanceLabel) {
        const labelChanged = normalizedDisplayDistanceLabel !== rawDistanceLabel;
        return {
          ...chip,
          hoverCardDetails: labelChanged ? null : chip.hoverCardDetails,
          label: normalizedDisplayDistanceLabel,
          title: labelChanged ? `Distance affichée : ${normalizedDisplayDistanceLabel}` : chip.title,
        };
      }

      if (chip?.key === "dur" && shouldOverrideTravelChip) {
        const displayLabel = formatSequenceDisplayTravelChipLabel(displayTravelDurationMinutes, { approximate: true });
        return {
          ...chip,
          hoverCardDetails: null,
          label: displayLabel,
          title: `Temps de conduite affiché : ${displayLabel}`,
        };
      }

      return chip;
    });
}

function replaceSequenceMetricChips(baseChips = [], metricChips = []) {
  const resolvedBaseChips = Array.isArray(baseChips) ? baseChips.filter(Boolean) : [];
  const resolvedMetricChips = Array.isArray(metricChips)
    ? metricChips.filter((chip) => chip && (chip.key === "dist" || chip.key === "dur"))
    : [];

  if (!resolvedMetricChips.length) {
    return resolvedBaseChips;
  }

  const metricChipByKey = new Map(resolvedMetricChips.map((chip) => [chip.key, chip]));
  const mergedChips = resolvedBaseChips.map((chip) => metricChipByKey.get(chip.key) || chip);

  resolvedMetricChips.forEach((chip) => {
    if (!mergedChips.some((existingChip) => existingChip?.key === chip.key)) {
      mergedChips.push(chip);
    }
  });

  return mergedChips;
}

function formatSequenceTruckLoadLabel(loadTons) {
  const resolvedLoadTons = Number(loadTons);
  if (!Number.isFinite(resolvedLoadTons) || resolvedLoadTons < 0) {
    return "—";
  }

  return `${resolvedLoadTons.toFixed(1)} t`;
}

function resolveSequenceTruckLoadDeltaTons(segment = null) {
  const quantityTons = Number(segment?.quantityTons);
  if (!Number.isFinite(quantityTons) || quantityTons <= 0) {
    return 0;
  }

  const stopType = typeof segment?.stopType === "string" ? segment.stopType.trim() : "";
  if (stopType === "loading") {
    return quantityTons;
  }

  if (stopType === "delivery") {
    return -quantityTons;
  }

  return 0;
}

function resolveSequenceTruckLoadBeforeTons(segment = null) {
  const loadTons = Number(segment?.truckLoadBeforeTons);
  return Number.isFinite(loadTons) && loadTons >= 0 ? loadTons : null;
}

export function buildSequenceTruckLoadByKey(segments = []) {
  const resolvedSegments = Array.isArray(segments) ? segments.filter(Boolean) : [];
  const loadByKey = new Map();
  let runningLoadTons = 0;

  resolvedSegments.forEach((segment, index) => {
    const stopType = typeof segment?.stopType === "string" ? segment.stopType.trim() : "";
    if (stopType === "depot_start" && segment?.isTerminalRouteMarker !== true) {
      runningLoadTons = 0;
    }

    const truckLoadBeforeTons = resolveSequenceTruckLoadBeforeTons(segment);
    if (truckLoadBeforeTons != null) {
      runningLoadTons = truckLoadBeforeTons;
    }

    runningLoadTons = Math.max(runningLoadTons + resolveSequenceTruckLoadDeltaTons(segment), 0);
    loadByKey.set(getSameSiteSequenceOverrideKey(segment, index), {
      label: formatSequenceTruckLoadLabel(runningLoadTons),
      valueTons: runningLoadTons,
    });
  });

  return loadByKey;
}

export function buildFleetSequenceDisplayItems(segments = []) {
  const resolvedSegments = buildAspiratorMissionDisplaySegments(segments);

  if (resolvedSegments.length < 2) {
    return resolvedSegments;
  }

  const terminalItemIndex = resolvedSegments.length - 1;
  const terminalItem = resolvedSegments[terminalItemIndex] || null;

  if (terminalItem?.stopType !== "depot_return") {
    return resolvedSegments;
  }

  const previousItem = resolvedSegments[terminalItemIndex - 1] || null;

  if (!previousItem) {
    return resolvedSegments;
  }

  const isPrebalancedTerminalMarker = terminalItem?.isTerminalRouteMarker === true;
  const isAspiratorTerminalMission = previousItem?.isAspiratorMissionMerged === true;
  const deferredTerminalTravelSegments = Array.isArray(previousItem?.aspiratorMissionDeferredTerminalTravelSegments)
    ? previousItem.aspiratorMissionDeferredTerminalTravelSegments.filter(Boolean)
    : [];
  const terminalHasOwnTravelMetric = [
    resolveSequenceTravelMinutes(terminalItem),
    resolveSequenceDisplayTravelMinutes(terminalItem),
    resolveSequenceScheduledTravelMinutes(terminalItem),
    resolveAspiratorMissionDistanceKm(terminalItem),
  ].some((value) => Number.isFinite(Number(value)) && Number(value) > 0);
  const terminalMissionTravelSegments = isAspiratorTerminalMission && !isPrebalancedTerminalMarker
    ? (
        terminalHasOwnTravelMetric
          ? [terminalItem]
          : deferredTerminalTravelSegments
      )
    : [];
  const terminalMetricSource = isPrebalancedTerminalMarker
    ? previousItem
    : terminalMissionTravelSegments[terminalMissionTravelSegments.length - 1] || terminalItem;
  const previousTravelDurationMinutes = resolveSequenceTravelMinutes(previousItem);
  const terminalTravelDurationMinutes = isPrebalancedTerminalMarker
    ? 0
    : isAspiratorTerminalMission
      ? sumAspiratorMissionMetric(terminalMissionTravelSegments, resolveSequenceTravelMinutes)
      : resolveSequenceTravelMinutes(terminalItem);
  const mergedTravelDurationMinutes = previousTravelDurationMinutes + terminalTravelDurationMinutes;
  const terminalDisplayTravelDurationMinutes = isAspiratorTerminalMission && !isPrebalancedTerminalMarker
    ? resolveSequenceDisplayTravelMinutes(previousItem)
      + sumAspiratorMissionMetric(terminalMissionTravelSegments, resolveSequenceDisplayTravelMinutes)
    : resolveSequenceDisplayTravelMinutes(terminalMetricSource);
  const terminalScheduledTravelValues = isAspiratorTerminalMission && !isPrebalancedTerminalMarker
    ? [
        resolveSequenceScheduledTravelMinutes(previousItem),
        ...terminalMissionTravelSegments.map(resolveSequenceScheduledTravelMinutes),
      ].filter((value) => Number.isFinite(value) && value >= 0)
    : [];
  const terminalScheduledTravelDurationMinutes = terminalScheduledTravelValues.length
    ? terminalScheduledTravelValues.reduce((total, value) => total + value, 0)
    : resolveSequenceScheduledTravelMinutes(terminalMetricSource);
  const terminalLegacyDisplayTravelDurationMinutes = isAspiratorTerminalMission && !isPrebalancedTerminalMarker
    ? resolveSequenceLegacyDisplayTravelMinutes(previousItem)
      + sumAspiratorMissionMetric(terminalMissionTravelSegments, resolveSequenceLegacyDisplayTravelMinutes)
    : Number.isFinite(Number(terminalMetricSource?.legacyDisplayTravelDurationMinutes))
      && Number(terminalMetricSource?.legacyDisplayTravelDurationMinutes) >= 0
      ? Number(terminalMetricSource.legacyDisplayTravelDurationMinutes)
      : terminalDisplayTravelDurationMinutes;
  const mergedServiceDurationMinutes = resolveTimelineDisplayServiceDurationMinutes(previousItem);
  const mergedManualBufferMinutes = resolveSequenceManualBufferMinutes(previousItem);
  const mergedBaseTimelineDurationMinutes = mergedServiceDurationMinutes + mergedTravelDurationMinutes;
  const mergedTimelineDurationMinutes = mergedBaseTimelineDurationMinutes + mergedManualBufferMinutes;
  const usesDurationDrivenTerminalGeometry = previousItem?.usesDurationDrivenTimelineGeometry === true
    || terminalItem?.usesDurationDrivenTimelineGeometry === true;

  let terminalMergedStopEnd;
  if (usesDurationDrivenTerminalGeometry) {
    const lastStopStartMinutes = resolveRenderSegmentStartMinutes(previousItem);
    if (lastStopStartMinutes != null) {
      const durationDrivenEndMinutes = lastStopStartMinutes
        + mergedServiceDurationMinutes
        + mergedManualBufferMinutes
        + terminalDisplayTravelDurationMinutes;
      terminalMergedStopEnd = { at: null, minutes: durationDrivenEndMinutes };
    } else {
      terminalMergedStopEnd = resolveTerminalMergedStopEnd(previousItem, resolvedSegments, terminalItemIndex);
    }
  } else {
    terminalMergedStopEnd = resolveTerminalMergedStopEnd(previousItem, resolvedSegments, terminalItemIndex);
  }
  const terminalMergedStopEndAt = terminalMergedStopEnd.at;
  const terminalMergedStopEndMinutes = terminalMergedStopEnd.minutes;
  const mergedAspiratorMissionTravelSegments = isAspiratorTerminalMission && !isPrebalancedTerminalMarker
    ? [
        ...(Array.isArray(previousItem?.aspiratorMissionTravelSegments)
          ? previousItem.aspiratorMissionTravelSegments.filter(Boolean)
          : []),
        ...terminalMissionTravelSegments,
      ]
    : previousItem?.aspiratorMissionTravelSegments;
  const mergedAspiratorMissionOutboundTravelSegments = isAspiratorTerminalMission && !isPrebalancedTerminalMarker
    ? [
        ...(Array.isArray(previousItem?.aspiratorMissionOutboundTravelSegments)
          ? previousItem.aspiratorMissionOutboundTravelSegments.filter(Boolean)
          : []),
        ...terminalMissionTravelSegments,
      ]
    : previousItem?.aspiratorMissionOutboundTravelSegments;
  const mergedAspiratorMissionDistanceKm = isAspiratorTerminalMission && !isPrebalancedTerminalMarker
    ? resolveAspiratorMissionDistanceKm(previousItem)
      + sumAspiratorMissionMetric(terminalMissionTravelSegments, resolveAspiratorMissionDistanceKm)
    : resolveAspiratorMissionDistanceKm(terminalMetricSource);
  const mergedAspiratorMissionChips = isAspiratorTerminalMission
    ? buildAspiratorMissionChips(previousItem, {
        aspiratorMinutes: resolveTimelineAspiratorServiceMinutes(previousItem),
        distanceKm: mergedAspiratorMissionDistanceKm,
        quantityTons: Number(previousItem?.quantityTons) || 0,
        travelMinutes: terminalDisplayTravelDurationMinutes,
      })
    : null;

  return [
    ...resolvedSegments.slice(0, terminalItemIndex - 1),
    {
      ...previousItem,
      ...(isAspiratorTerminalMission
        ? {
            aspiratorMissionDeferredTerminalTravelSegments: [],
            aspiratorMissionExitAddress: terminalMetricSource?.routeToAddress || terminalMetricSource?.toAddress || "",
            aspiratorMissionExitLabel: terminalMetricSource?.routeToLabel || terminalMetricSource?.toLabel || "Dépôt",
            aspiratorMissionOutboundTravelSegments: mergedAspiratorMissionOutboundTravelSegments,
            aspiratorMissionTravelSegments: mergedAspiratorMissionTravelSegments,
          }
        : {}),
      baseTimelineDurationLabel: formatDurationShort(mergedBaseTimelineDurationMinutes) || "0 min",
      baseTimelineDurationMinutes: mergedBaseTimelineDurationMinutes,
      chips: isAspiratorTerminalMission
        ? mergedAspiratorMissionChips
        : isPrebalancedTerminalMarker
          ? previousItem.chips
          : replaceSequenceMetricChips(previousItem.chips, terminalItem.chips),
      displayTravelDurationMinutes: terminalDisplayTravelDurationMinutes,
      distanceLabel: isAspiratorTerminalMission
        ? formatSequenceDistanceLabel(mergedAspiratorMissionDistanceKm) || previousItem.distanceLabel
        : terminalMetricSource.distanceLabel || previousItem.distanceLabel,
      fleetDisplayedTravelDurationMinutes: terminalDisplayTravelDurationMinutes,
      fleetDisplayedTravelUsesHereTraffic: isAspiratorTerminalMission
        ? previousItem?.fleetDisplayedTravelUsesHereTraffic === true
          || terminalMissionTravelSegments.some((segment) => segment?.fleetDisplayedTravelUsesHereTraffic === true)
        : terminalMetricSource?.fleetDisplayedTravelUsesHereTraffic === true,
      isTerminalRouteMerged: true,
      legacyDisplayTravelDurationMinutes: terminalLegacyDisplayTravelDurationMinutes,
      routeSegmentIndex: terminalMetricSource.routeSegmentIndex ?? previousItem.routeSegmentIndex,
      routeDistanceSource: terminalMetricSource.routeDistanceSource || previousItem.routeDistanceSource || "unavailable",
      routeDistanceSourceLabel: terminalMetricSource.routeDistanceSourceLabel || previousItem.routeDistanceSourceLabel || null,
      routeEstimatedTravelDurationMinutes: terminalMetricSource.routeEstimatedTravelDurationMinutes ?? previousItem.routeEstimatedTravelDurationMinutes ?? null,
      routeScheduledTravelDurationMinutes: terminalScheduledTravelDurationMinutes,
      routeSpeedKmh: terminalMetricSource.routeSpeedKmh ?? previousItem.routeSpeedKmh ?? null,
      routeFromAddress: isAspiratorTerminalMission
        ? previousItem.routeFromAddress
        : terminalMetricSource.routeFromAddress || previousItem.routeFromAddress,
      routeFromLabel: isAspiratorTerminalMission
        ? previousItem.routeFromLabel
        : terminalMetricSource.routeFromLabel || previousItem.routeFromLabel,
      routeToAddress: terminalMetricSource.routeToAddress || previousItem.routeToAddress,
      routeToLabel: terminalMetricSource.routeToLabel || previousItem.routeToLabel,
      terminalMergedStopEndAt,
      terminalMergedStopEndMinutes,
      timelineDurationLabel: formatDurationShort(mergedTimelineDurationMinutes) || "0 min",
      timelineDurationMinutes: mergedTimelineDurationMinutes,
      travelDurationLabel: mergedTravelDurationMinutes > 0 ? formatDurationShort(mergedTravelDurationMinutes) : (mergedTravelDurationMinutes === 0 ? "0 min" : null),
      travelDurationMinutes: mergedTravelDurationMinutes,
      visualTimelineDurationMinutes: mergedTimelineDurationMinutes,
    },
    {
      ...terminalItem,
      chips: [],
      displayTravelDurationMinutes: 0,
      displayedSegmentEndAt: terminalMergedStopEndAt,
      displayedSegmentEndMinutes: terminalMergedStopEndMinutes,
      distanceLabel: null,
      isTerminalRouteMarker: true,
      serviceDurationLabel: null,
      serviceDurationMinutes: 0,
      travelDurationMinutes: 0,
    },
  ];
}

function resolveCombinedTimelineDurationMinutes(previousItem = null, terminalItem = null) {
  const previousTimelineDurationMinutes = Number(previousItem?.timelineDurationMinutes);
  const terminalTimelineDurationMinutes = Number(terminalItem?.timelineDurationMinutes);

  return Math.max(previousTimelineDurationMinutes, 0) + Math.max(terminalTimelineDurationMinutes, 0);
}

function resolveTimelineDisplayTravelDurationMinutes(item = null) {
  const displayTravelDurationMinutes = Number(item?.displayTravelDurationMinutes);
  if (Number.isFinite(displayTravelDurationMinutes) && displayTravelDurationMinutes >= 0) {
    return displayTravelDurationMinutes;
  }

  const travelDurationMinutes = Number(item?.travelDurationMinutes);
  return Number.isFinite(travelDurationMinutes) && travelDurationMinutes >= 0 ? travelDurationMinutes : 0;
}

function resolveTimelineDisplayServiceDurationMinutes(item = null) {
  const serviceDurationMinutes = Number(item?.serviceDurationMinutes);
  if (Number.isFinite(serviceDurationMinutes) && serviceDurationMinutes >= 0) {
    return serviceDurationMinutes;
  }

  const visualServiceDurationMinutes = Number(item?.visualServiceDurationMinutes);
  return Number.isFinite(visualServiceDurationMinutes) && visualServiceDurationMinutes >= 0 ? visualServiceDurationMinutes : 0;
}

function resolveTimelineAspiratorServiceMinutes(item = null) {
  if (!item) {
    return 0;
  }

  const directMinutes = Number(item?.aspiratorServiceMinutes ?? item?.aspirator_service_minutes);
  return Number.isFinite(directMinutes) && directMinutes > 0 ? Math.round(directMinutes) : 0;
}

function getTimelineBreakdownServiceSegments(segment = null) {
  if (segment?.isAspiratorMissionMerged === true) {
    return segment ? [segment] : [];
  }

  if (segment?.isSameAddressLoadingDeliveryMerged === true && Array.isArray(segment?.mergedMemberSegments)) {
    return segment.mergedMemberSegments.filter(Boolean);
  }

  return segment ? [segment] : [];
}

function addTimelineServiceBreakdownSegment(totals, segment = null) {
  const stopType = typeof segment?.stopType === "string" ? segment.stopType.trim() : "";
  const serviceMinutes = resolveTimelineDisplayServiceDurationMinutes(segment);
  if (segment?.isAspiratorMissionMerged === true) {
    totals.aspiratorMinutes += resolveTimelineAspiratorServiceMinutes(segment) || serviceMinutes;
    return totals;
  }

  const aspiratorMinutes = stopType === "delivery"
    ? resolveTimelineAspiratorServiceMinutes(segment)
    : 0;
  const baseServiceMinutes = aspiratorMinutes > 0 && serviceMinutes >= aspiratorMinutes
    ? serviceMinutes - aspiratorMinutes
    : serviceMinutes;

  if (stopType === "depot_start") {
    totals.inspectionMinutes += serviceMinutes;
  } else if (stopType === "loading") {
    totals.loadingMinutes += baseServiceMinutes;
  } else if (stopType === "delivery") {
    totals.unloadingMinutes += baseServiceMinutes;
  }

  addTimelineFixedServiceMinutes(totals, stopType, segment, segment?.isPreloadedPreviousDay);
  totals.aspiratorMinutes += aspiratorMinutes;
  return totals;
}

// Temps fixes par chargement et par livraison (Paramètres système), déjà compris dans charg. et décharg.
function addTimelineFixedServiceMinutes(totals, stopType, item, isPreloaded = false) {
  if (stopType === "loading" && !isPreloaded) {
    totals.loadingFixedMinutes = (totals.loadingFixedMinutes || 0) + Math.max(0, Math.round(Number(item?.loading_fixed_minutes) || 0));
  } else if (stopType === "delivery") {
    totals.unloadingFixedMinutes = (totals.unloadingFixedMinutes || 0) + Math.max(0, Math.round(Number(item?.delivery_fixed_minutes) || 0));
  }
  return totals;
}

function resolveTimelineScaleMinutesFromPositionedItem(item = null) {
  const explicitTimelineScaleMinutes = Number(item?.timelineScaleMinutes);
  if (Number.isFinite(explicitTimelineScaleMinutes) && explicitTimelineScaleMinutes > 0) {
    return explicitTimelineScaleMinutes;
  }

  const widthPercent = Number(item?.widthPercent);
  if (!Number.isFinite(widthPercent) || widthPercent <= 0) {
    return null;
  }

  const visualTimelineDurationMinutes = Number(item?.visualTimelineDurationMinutes);
  if (Number.isFinite(visualTimelineDurationMinutes) && visualTimelineDurationMinutes > 0) {
    return (visualTimelineDurationMinutes / widthPercent) * 100;
  }

  const timelineDurationMinutes = Number(item?.timelineDurationMinutes);
  return Number.isFinite(timelineDurationMinutes) && timelineDurationMinutes > 0
    ? (timelineDurationMinutes / widthPercent) * 100
    : null;
}

function resolveTimelineUsablePercentFromPositionedItem(item = null) {
  const explicitTimelineUsablePercent = Number(item?.timelineUsablePercent);
  if (Number.isFinite(explicitTimelineUsablePercent) && explicitTimelineUsablePercent > 0) {
    return explicitTimelineUsablePercent;
  }

  return 100;
}

function normalizeMergedSameAddressStopType(stopType = "") {
  const normalizedStopType = typeof stopType === "string" ? stopType.trim() : "";
  return normalizedStopType === "loading" || normalizedStopType === "delivery"
    ? normalizedStopType
    : "";
}

function normalizeMergedSameAddressTextValue(value = "") {
  return typeof value === "string" ? value.trim().toLowerCase() : "";
}

function normalizeMergedSameAddressOrderKey(segment = null) {
  const operationOrderId = typeof segment?.operationOrderId === "string"
    ? segment.operationOrderId.trim()
    : "";
  if (operationOrderId) {
    return operationOrderId.toLowerCase();
  }

  const orderLabel = typeof segment?.orderLabel === "string"
    ? segment.orderLabel.trim()
    : "";
  return orderLabel ? orderLabel.toLowerCase() : "";
}

export function isMergedOperationalMissionSegment(segment = null) {
  return Boolean(
    segment?.isSameAddressLoadingDeliveryMerged === true
    || segment?.isAspiratorMissionMerged === true
  );
}

function resolveRenderItemSequenceStartCandidate(item = null) {
  if (!item || item.kind === "idle") {
    return null;
  }

  const segment = item.kind === "group"
    ? (Array.isArray(item.memberSegments) ? item.memberSegments.find(Boolean) : null)
    : item.segment;
  const candidates = [
    segment?.routeSequenceDisplaySequence,
    segment?.globalSequence,
    item.startSequence,
  ];

  for (const candidate of candidates) {
    const displaySequence = Number(candidate);
    if (Number.isInteger(displaySequence) && displaySequence > 0) {
      return displaySequence;
    }
  }

  return null;
}

function resolveVisibleOperationalSequenceStart(renderItems = []) {
  for (const item of Array.isArray(renderItems) ? renderItems : []) {
    const startSequence = resolveRenderItemSequenceStartCandidate(item);
    if (startSequence != null) {
      return startSequence;
    }
  }

  return 1;
}

function resolveRenderItemOrderCount(item = null) {
  if (item?.kind === "group") {
    const memberCount = (Array.isArray(item.memberSegments) ? item.memberSegments.filter(Boolean) : [])
      .reduce((total, memberSegment) => total + resolvePmLoadingOrderCount(memberSegment), 0);
    return Math.max(memberCount, 1);
  }

  if (item?.kind === "segment" || item?.kind === "group-member") {
    return resolveMergedOperationalOrderCount(item.segment);
  }

  return 1;
}

export function assignVisibleOperationalDisplaySequences(renderItems = []) {
  const resolvedItems = Array.isArray(renderItems) ? renderItems : [];
  let nextSequence = resolveVisibleOperationalSequenceStart(resolvedItems);
  let groupMemberSequence = null;

  return resolvedItems.map((item) => {
    if (!item || item.kind === "idle") {
      return item;
    }

    if (item.kind === "group-member") {
      const memberSequence = Number.isInteger(groupMemberSequence) && groupMemberSequence > 0
        ? groupMemberSequence
        : nextSequence;
      const memberOrderCount = resolvePmLoadingOrderCount(item.segment);
      if (Number.isInteger(groupMemberSequence) && groupMemberSequence > 0) {
        groupMemberSequence += memberOrderCount;
      } else {
        nextSequence += memberOrderCount;
      }

      const memberLabel = formatOperationalSequenceRange(memberSequence, memberOrderCount);
      return {
        ...item,
        segment: item.segment
          ? {
              ...item.segment,
              ...(isMergedOperationalMissionSegment(item.segment) || memberOrderCount > 1
                ? { mergedOrderSequenceLabel: memberLabel }
                : {}),
              routeSequenceDisplaySequence: memberSequence,
            }
          : item.segment,
      };
    }

    groupMemberSequence = null;
    const orderCount = resolveRenderItemOrderCount(item);
    const startSequence = nextSequence;
    const sequenceLabel = formatOperationalSequenceRange(startSequence, orderCount);
    nextSequence += orderCount;

    if (item.kind === "group") {
      groupMemberSequence = startSequence;
      return {
        ...item,
        endSequence: startSequence + orderCount - 1,
        sameSiteGroupDisplayEndSequence: startSequence + orderCount - 1,
        sameSiteGroupDisplayStartSequence: startSequence,
        startSequence,
      };
    }

    if (!item.segment) {
      return item;
    }

    return {
      ...item,
      segment: {
        ...item.segment,
        ...(isMergedOperationalMissionSegment(item.segment) || orderCount > 1
          ? { mergedOrderSequenceLabel: sequenceLabel }
          : {}),
        routeSequenceDisplaySequence: startSequence,
      },
    };
  });
}

function normalizeAspiratorMissionKey(value = "") {
  return typeof value === "string" ? value.trim() : "";
}

function normalizeAspiratorMissionAddressKey(value = "") {
  return normalizeAspiratorMissionKey(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .toLowerCase();
}

function resolveAspiratorMissionOrderKey(segment = null) {
  return normalizeMergedSameAddressOrderKey(segment);
}

function isAspiratorMissionTimelineCandidate(segment = null) {
  const stopType = normalizeMergedSameAddressStopType(segment?.stopType);
  return Boolean(
    segment
    && segment?.isAspiratorMissionMerged !== true
    && segment?.requiresAspirator === true
    && normalizeAspiratorMissionKey(segment?.aspiratorMissionGroupKey)
    && (stopType === "loading" || stopType === "delivery")
    && segment?.isInitialPreload !== true
    && segment?.isStartOfDayPreload !== true
    && segment?.isPreloadedPreviousDay !== true
    && segment?.isEndOfDayLoad !== true
    && segment?.isEndOfDayLoadSourcePreload !== true
  );
}

function resolveAspiratorMissionSiteKey(segment = null) {
  return normalizeAspiratorMissionAddressKey(
    segment?.operationAddress
    || segment?.toAddress
    || segment?.routeFromAddress
    || segment?.fromAddress
    || "",
  );
}

function resolveAspiratorMissionSegmentLabel(segment = null) {
  return normalizeAspiratorMissionKey(
    segment?.operationLabel
    || segment?.toLabel
    || segment?.operationOwnerLabel
    || segment?.orderLabel
    || "",
  );
}

function joinAspiratorMissionDistinctLabels(labels = []) {
  const uniqueLabels = [];
  const seenKeys = new Set();

  (Array.isArray(labels) ? labels : []).forEach((label) => {
    const normalizedLabel = normalizeAspiratorMissionKey(label);
    if (!normalizedLabel) {
      return;
    }

    const labelKey = normalizeAspiratorMissionAddressKey(normalizedLabel) || normalizedLabel.toLowerCase();
    if (seenKeys.has(labelKey)) {
      return;
    }

    seenKeys.add(labelKey);
    uniqueLabels.push(normalizedLabel);
  });

  return uniqueLabels.join(" → ");
}

function resolveAspiratorMissionRouteFromKey(segment = null) {
  return normalizeAspiratorMissionAddressKey(
    segment?.routeFromAddress
    || segment?.fromAddress
    || "",
  );
}

function resolveAspiratorMissionRouteToKey(segment = null) {
  return normalizeAspiratorMissionAddressKey(
    segment?.routeToAddress
    || segment?.toAddress
    || "",
  );
}

function resolveAspiratorMissionTravelDedupeKey(segment = null, sourceIndex = 0) {
  const routeSegmentIndex = Number.parseInt(segment?.routeSegmentIndex, 10);
  if (Number.isInteger(routeSegmentIndex)) {
    return `${normalizeAspiratorMissionKey(segment?.tripId) || "trip"}::route:${routeSegmentIndex}`;
  }

  return `${normalizeAspiratorMissionKey(segment?.tripId) || "trip"}::source:${sourceIndex}`;
}

function sumAspiratorMissionMetric(segments = [], resolver = () => 0) {
  return segments.reduce((total, segment) => {
    const value = Number(resolver(segment));
    return Number.isFinite(value) && value >= 0 ? total + value : total;
  }, 0);
}

function resolveAspiratorMissionDistanceKm(segment = null) {
  const parsedDistanceKm = parseSequenceDistanceKm(segment?.distanceLabel);
  return parsedDistanceKm != null && parsedDistanceKm >= 0 ? parsedDistanceKm : 0;
}

function buildAspiratorMissionMetricChip(kind, label, key = kind) {
  return label
    ? {
        key,
        kind,
        label,
      }
    : null;
}

function buildAspiratorMissionChips(sourceSegment, {
  aspiratorMinutes = 0,
  distanceKm = 0,
  memberSegments = [],
  quantityTons = 0,
  travelMinutes = 0,
} = {}) {
  const omittedKinds = new Set([
    "aspirator_time",
    "compartment",
    "dist",
    "dur",
    "manual_buffer",
    "meunerie",
    "qty",
    "svc",
  ]);
  const resolvedMemberSegments = Array.isArray(memberSegments) && memberSegments.length
    ? memberSegments.filter(Boolean)
    : [sourceSegment].filter(Boolean);
  const retainedChips = (Array.isArray(sourceSegment?.chips) ? sourceSegment.chips : [])
    .filter((chip) => {
      const chipKind = normalizeAspiratorMissionKey(chip?.kind || chip?.key).toLowerCase();
      return chipKind && !omittedKinds.has(chipKind);
    });
  const compartmentLabel = joinGroupedSegmentCompartmentLabel(resolvedMemberSegments);
  const quantityLabel = Number.isFinite(quantityTons) && quantityTons > 0
    ? `${quantityTons.toFixed(1)} t`
    : "";
  const distanceLabel = Number.isFinite(distanceKm) && distanceKm > 0
    ? formatSequenceDistanceLabel(distanceKm)
    : "";
  const aspiratorLabel = Number.isFinite(aspiratorMinutes) && aspiratorMinutes > 0
    ? `+${Math.round(aspiratorMinutes)} min`
    : "";
  const travelLabel = Number.isFinite(travelMinutes) && travelMinutes >= 0
    ? (travelMinutes > 0 ? `≈ ${formatDurationShort(travelMinutes)}` : "0 min")
    : "";
  const quantityChip = buildAspiratorMissionMetricChip("qty", quantityLabel);
  const quantityHoverCardDetails = quantityChip
    ? resolveMergedQuantityHoverCardDetails(resolvedMemberSegments, quantityLabel, {
        isSingleCount: isMergedSameAddressLoadingDeliveryPair(resolvedMemberSegments),
      })
    : null;

  return [
    compartmentLabel
      ? {
          key: "compartment",
          kind: "compartment",
          label: compartmentLabel,
          title: `Compartiment(s) : ${compartmentLabel}`,
        }
      : null,
    ...retainedChips,
    quantityChip
      ? {
          ...quantityChip,
          hoverCardDetails: quantityHoverCardDetails,
          hoverCardTitle: "Quantité",
          title: `Quantité cumulée : ${quantityLabel}`,
        }
      : null,
    buildAspiratorMissionMetricChip("dist", distanceLabel),
    buildAspiratorMissionMetricChip("aspirator_time", aspiratorLabel),
    buildAspiratorMissionMetricChip("dur", travelLabel),
  ].filter(Boolean);
}

function resolveAspiratorMissionSequenceLabel(memberSegments = []) {
  const sequences = Array.from(new Set(
    memberSegments
      .map((segment) => Number(segment?.globalSequence))
      .filter((sequence) => Number.isInteger(sequence) && sequence > 0),
  )).sort((left, right) => left - right);

  return sequences.join(" + ");
}

function buildAspiratorMissionDescriptor(groupEntries = []) {
  const resolvedEntries = Array.isArray(groupEntries) ? groupEntries.filter(Boolean) : [];
  const memberEntriesByOrderKey = new Map();

  resolvedEntries.forEach((entry) => {
    const orderKey = resolveAspiratorMissionOrderKey(entry.segment);
    if (!orderKey) {
      return;
    }

    const orderEntries = memberEntriesByOrderKey.get(orderKey) || [];
    orderEntries.push(entry);
    memberEntriesByOrderKey.set(orderKey, orderEntries);
  });

  const completeOrderKeys = new Set(
    Array.from(memberEntriesByOrderKey.entries())
      .filter(([, orderEntries]) => {
        const stopTypes = new Set(orderEntries.map((entry) => normalizeMergedSameAddressStopType(entry.segment?.stopType)));
        return stopTypes.has("loading") && stopTypes.has("delivery");
      })
      .map(([orderKey]) => orderKey),
  );
  const memberEntries = resolvedEntries.filter(
    (entry) => completeOrderKeys.has(resolveAspiratorMissionOrderKey(entry.segment)),
  );
  const loadingEntries = memberEntries.filter(
    (entry) => normalizeMergedSameAddressStopType(entry.segment?.stopType) === "loading",
  );
  const deliveryEntries = memberEntries.filter(
    (entry) => normalizeMergedSameAddressStopType(entry.segment?.stopType) === "delivery",
  );

  if (!loadingEntries.length || !deliveryEntries.length) {
    return null;
  }

  const firstLoadingEntry = loadingEntries.reduce(
    (firstEntry, entry) => (!firstEntry || entry.index < firstEntry.index ? entry : firstEntry),
    null,
  );
  const firstDeliveryEntry = deliveryEntries.reduce(
    (firstEntry, entry) => (!firstEntry || entry.index < firstEntry.index ? entry : firstEntry),
    null,
  );
  const actionDeliveryEntry = deliveryEntries.find((entry) => (
    entry.segment?.businessStep
    || entry.segment?.reassignmentDrag
    || entry.segment?.segmentReorder
  )) || firstDeliveryEntry;
  const missionGroupKey = normalizeAspiratorMissionKey(firstLoadingEntry?.segment?.aspiratorMissionGroupKey);
  const loadingSiteKeys = new Set(loadingEntries.map((entry) => resolveAspiratorMissionSiteKey(entry.segment)).filter(Boolean));
  const deliverySiteKeys = new Set(deliveryEntries.map((entry) => resolveAspiratorMissionSiteKey(entry.segment)).filter(Boolean));
  const missionSiteKeys = new Set([...loadingSiteKeys, ...deliverySiteKeys]);
  const seenTravelKeys = new Set();
  const internalTravelEntries = [];
  const externalTravelEntries = [];

  memberEntries.forEach((entry) => {
    const segment = entry.segment;
    const travelKey = resolveAspiratorMissionTravelDedupeKey(segment, entry.index);
    if (seenTravelKeys.has(travelKey)) {
      return;
    }

    const routeFromKey = resolveAspiratorMissionRouteFromKey(segment);
    const routeToKey = resolveAspiratorMissionRouteToKey(segment);
    const hasTravelMetric = [
      resolveSequenceTravelMinutes(segment),
      resolveSequenceDisplayTravelMinutes(segment),
      resolveSequenceLegacyDisplayTravelMinutes(segment),
      resolveSequenceScheduledTravelMinutes(segment),
      resolveAspiratorMissionDistanceKm(segment),
    ].some((value) => Number.isFinite(Number(value)) && Number(value) > 0);
    const isInternalTravel = Boolean(
      routeToKey
      && deliverySiteKeys.has(routeToKey)
      && (!routeFromKey || missionSiteKeys.has(routeFromKey)),
    );
    const isExternalTravel = Boolean(
      hasTravelMetric
      && routeFromKey
      && deliverySiteKeys.has(routeFromKey)
      && routeToKey
      && !deliverySiteKeys.has(routeToKey),
    );

    if (isInternalTravel) {
      seenTravelKeys.add(travelKey);
      internalTravelEntries.push(entry);
    } else if (isExternalTravel) {
      seenTravelKeys.add(travelKey);
      externalTravelEntries.push(entry);
    }
  });

  const internalTravelSegments = internalTravelEntries.map((entry) => entry.segment);
  const aspiratorMinutes = Math.max(
    0,
    ...deliveryEntries.map((entry) => resolveTimelineAspiratorServiceMinutes(entry.segment)),
  );
  const displayTravelMinutes = sumAspiratorMissionMetric(internalTravelSegments, resolveSequenceDisplayTravelMinutes);
  const legacyTravelMinutes = sumAspiratorMissionMetric(internalTravelSegments, resolveSequenceLegacyDisplayTravelMinutes);
  const travelMinutes = sumAspiratorMissionMetric(internalTravelSegments, resolveSequenceTravelMinutes);
  const scheduledTravelValues = internalTravelSegments
    .map(resolveSequenceScheduledTravelMinutes)
    .filter((value) => Number.isFinite(value) && value >= 0);
  const scheduledTravelMinutes = scheduledTravelValues.length
    ? scheduledTravelValues.reduce((total, value) => total + value, 0)
    : null;
  const distanceKm = sumAspiratorMissionMetric(internalTravelSegments, resolveAspiratorMissionDistanceKm);
  const memberSegments = memberEntries.map((entry) => entry.segment);
  const quantityTons = resolveMergedSameAddressQuantityTons(memberSegments);
  const durationMinutes = displayTravelMinutes + aspiratorMinutes;
  const loadingLabel = resolveAspiratorMissionSegmentLabel(firstLoadingEntry?.segment) || "Chargement";
  const deliveryLabels = Array.from(new Map(
    deliveryEntries.map((entry) => {
      const label = resolveAspiratorMissionSegmentLabel(entry.segment) || "Livraison";
      return [normalizeAspiratorMissionAddressKey(label) || label.toLowerCase(), label];
    }),
  ).values());
  const samePhysicalSite = missionSiteKeys.size === 1;
  const operationLabel = samePhysicalSite
    ? (loadingLabel || deliveryLabels[0] || "Chargement")
    : (joinAspiratorMissionDistinctLabels([loadingLabel, ...deliveryLabels]) || loadingLabel);

  return {
    actionDeliveryEntry,
    aspiratorMinutes,
    completeOrderKeys,
    deliveryEntries,
    deliveryLabels,
    deliverySiteKeys,
    displayTravelMinutes,
    distanceKm,
    durationMinutes,
    externalTravelEntries,
    firstIndex: Math.min(...memberEntries.map((entry) => entry.index)),
    firstLoadingEntry,
    internalTravelEntries,
    lastIndex: Math.max(...memberEntries.map((entry) => entry.index)),
    legacyTravelMinutes,
    loadingEntries,
    loadingLabel,
    loadingSiteKeys,
    memberEntries,
    memberSegments,
    missionGroupKey,
    missionTravelEntries: internalTravelEntries,
    operationLabel,
    outboundTravelEntries: [],
    quantityTons,
    samePhysicalSite,
    scheduledTravelMinutes,
    terminalDeferredTravelEntries: [],
    travelMinutes,
  };
}

function appendAspiratorMissionTravelEntries(descriptor, travelEntries = []) {
  const seenTravelKeys = new Set(
    descriptor.missionTravelEntries.map((entry) => (
      resolveAspiratorMissionTravelDedupeKey(entry.segment, entry.index)
    )),
  );
  const appendedEntries = travelEntries.filter((entry) => {
    const travelKey = resolveAspiratorMissionTravelDedupeKey(entry.segment, entry.index);
    if (seenTravelKeys.has(travelKey)) {
      return false;
    }
    seenTravelKeys.add(travelKey);
    return true;
  });
  if (!appendedEntries.length) {
    return;
  }

  const appendedSegments = appendedEntries.map((entry) => entry.segment);
  const appendedDisplayTravelMinutes = sumAspiratorMissionMetric(
    appendedSegments,
    resolveSequenceDisplayTravelMinutes,
  );
  const appendedLegacyTravelMinutes = sumAspiratorMissionMetric(
    appendedSegments,
    resolveSequenceLegacyDisplayTravelMinutes,
  );
  const appendedTravelMinutes = sumAspiratorMissionMetric(
    appendedSegments,
    resolveSequenceTravelMinutes,
  );
  const appendedDistanceKm = sumAspiratorMissionMetric(
    appendedSegments,
    resolveAspiratorMissionDistanceKm,
  );
  const appendedScheduledValues = appendedSegments
    .map(resolveSequenceScheduledTravelMinutes)
    .filter((value) => Number.isFinite(value) && value >= 0);
  const appendedScheduledTravelMinutes = appendedScheduledValues.length
    ? appendedScheduledValues.reduce((total, value) => total + value, 0)
    : null;

  descriptor.displayTravelMinutes += appendedDisplayTravelMinutes;
  descriptor.legacyTravelMinutes += appendedLegacyTravelMinutes;
  descriptor.travelMinutes += appendedTravelMinutes;
  descriptor.distanceKm += appendedDistanceKm;
  descriptor.durationMinutes += appendedDisplayTravelMinutes;
  descriptor.outboundTravelEntries.push(...appendedEntries);
  descriptor.missionTravelEntries.push(...appendedEntries);
  if (appendedScheduledTravelMinutes != null) {
    descriptor.scheduledTravelMinutes = (descriptor.scheduledTravelMinutes || 0)
      + appendedScheduledTravelMinutes;
  }
}

function assignAspiratorMissionDisplayAnchors(descriptors = [], resolvedSegments = []) {
  const sortedDescriptors = [...descriptors].sort(
    (left, right) => left.firstLoadingEntry.index - right.firstLoadingEntry.index,
  );
  let cluster = [];
  let clusterEndIndex = -1;
  const flushCluster = () => {
    if (!cluster.length) {
      return;
    }

    const clusterExternalTravelEntries = [];
    const seenClusterTravelKeys = new Set();
    cluster.forEach((descriptor) => {
      descriptor.externalTravelEntries.forEach((entry) => {
        const travelKey = resolveAspiratorMissionTravelDedupeKey(entry.segment, entry.index);
        if (seenClusterTravelKeys.has(travelKey)) {
          return;
        }
        seenClusterTravelKeys.add(travelKey);
        clusterExternalTravelEntries.push(entry);
      });
    });
    const nextRawSegment = resolvedSegments[clusterEndIndex + 1] || null;
    const terminalPipelineOwnsExit = nextRawSegment?.stopType === "depot_return"
      || nextRawSegment?.isTerminalRouteMarker === true;
    if (clusterExternalTravelEntries.length) {
      const lastClusterDescriptor = cluster[cluster.length - 1];
      if (terminalPipelineOwnsExit) {
        lastClusterDescriptor.terminalDeferredTravelEntries.push(...clusterExternalTravelEntries);
      } else {
        appendAspiratorMissionTravelEntries(
          lastClusterDescriptor,
          clusterExternalTravelEntries,
        );
      }
    }

    const clusterStartCandidates = cluster
      .flatMap((descriptor) => descriptor.memberSegments)
      .map(resolveRenderSegmentStartMinutes)
      .filter((value) => value != null);
    let cursorMinutes = clusterStartCandidates.length ? Math.min(...clusterStartCandidates) : null;

    cluster.forEach((descriptor) => {
      descriptor.displayStartMinutes = cursorMinutes;
      descriptor.displayEndMinutes = cursorMinutes != null
        ? cursorMinutes + descriptor.durationMinutes
        : null;
      if (cursorMinutes != null) {
        cursorMinutes = descriptor.displayEndMinutes;
      }
    });
    cluster = [];
    clusterEndIndex = -1;
  };

  sortedDescriptors.forEach((descriptor) => {
    if (cluster.length && descriptor.firstIndex > clusterEndIndex) {
      flushCluster();
    }
    cluster.push(descriptor);
    clusterEndIndex = Math.max(clusterEndIndex, descriptor.lastIndex);
  });
  flushCluster();

  return sortedDescriptors;
}

function buildAspiratorMissionSyntheticSegment(descriptor) {
  const firstLoadingSegment = descriptor.firstLoadingEntry.segment;
  const actionSegment = descriptor.actionDeliveryEntry?.segment || descriptor.deliveryEntries[0]?.segment || firstLoadingSegment;
  const firstDeliverySegment = descriptor.deliveryEntries[0]?.segment || actionSegment;
  const uniqueDeliveryAddresses = Array.from(new Map(
    descriptor.deliveryEntries.map((entry) => {
      const address = normalizeAspiratorMissionKey(entry.segment?.operationAddress || entry.segment?.toAddress);
      return [normalizeAspiratorMissionAddressKey(address) || address.toLowerCase(), address];
    }).filter(([, address]) => Boolean(address)),
  ).values());
  const sequenceLabel = resolveAspiratorMissionSequenceLabel(descriptor.memberSegments);
  const samePhysicalSite = descriptor.samePhysicalSite === true
    || new Set([...descriptor.loadingSiteKeys, ...descriptor.deliverySiteKeys]).size === 1;
  const missionOrderIds = Array.from(new Map(
    descriptor.memberSegments.map((segment) => {
      const orderId = normalizeAspiratorMissionKey(segment?.operationOrderId || segment?.orderLabel);
      return [orderId.toLowerCase(), orderId];
    }).filter(([, orderId]) => Boolean(orderId)),
  ).values());
  const statusMarkers = Array.from(new Map(
    descriptor.memberSegments
      .flatMap((segment) => Array.isArray(segment?.statusMarkers) ? segment.statusMarkers : [])
      .filter(Boolean)
      .map((marker, markerIndex) => [
        normalizeAspiratorMissionKey(marker?.code || marker?.label) || `marker:${markerIndex}`,
        marker,
      ]),
  ).values());
  const outboundTravelSegment = descriptor.outboundTravelEntries[
    descriptor.outboundTravelEntries.length - 1
  ]?.segment || null;
  const finalDeliveryAddress = uniqueDeliveryAddresses[uniqueDeliveryAddresses.length - 1]
    || firstDeliverySegment?.operationAddress
    || firstDeliverySegment?.toAddress
    || "";
  const routeToAddress = outboundTravelSegment?.routeToAddress
    || outboundTravelSegment?.toAddress
    || finalDeliveryAddress;
  const routeToLabel = outboundTravelSegment?.routeToLabel
    || outboundTravelSegment?.toLabel
    || descriptor.deliveryLabels.join(" → ");

  return {
    ...firstLoadingSegment,
    actionSegmentSequence: actionSegment?.globalSequence ?? firstLoadingSegment?.globalSequence,
    aspiratorMissionExitAddress: outboundTravelSegment?.routeToAddress || outboundTravelSegment?.toAddress || "",
    aspiratorMissionExitLabel: outboundTravelSegment?.routeToLabel || outboundTravelSegment?.toLabel || "",
    aspiratorMissionInternalTravelSegments: descriptor.internalTravelEntries.map((entry) => entry.segment),
    aspiratorMissionOutboundTravelSegments: descriptor.outboundTravelEntries.map((entry) => entry.segment),
    aspiratorMissionDeferredTerminalTravelSegments: descriptor.terminalDeferredTravelEntries.map((entry) => entry.segment),
    aspiratorMissionTravelSegments: descriptor.missionTravelEntries.map((entry) => entry.segment),
    aspiratorServiceMinutes: descriptor.aspiratorMinutes,
    aspirator_service_minutes: descriptor.aspiratorMinutes,
    baseTimelineDurationLabel: formatDurationShort(descriptor.durationMinutes) || "0 min",
    baseTimelineDurationMinutes: descriptor.durationMinutes,
    businessStep: actionSegment?.businessStep || firstLoadingSegment?.businessStep || null,
    chips: buildAspiratorMissionChips(firstLoadingSegment, {
      aspiratorMinutes: descriptor.aspiratorMinutes,
      distanceKm: descriptor.distanceKm,
      memberSegments: descriptor.memberSegments,
      quantityTons: descriptor.quantityTons,
      travelMinutes: descriptor.displayTravelMinutes,
    }),
    deliveryVisitCount: descriptor.deliverySiteKeys.size,
    displayTravelDurationMinutes: descriptor.displayTravelMinutes,
    distanceLabel: formatSequenceDistanceLabel(descriptor.distanceKm) || null,
    fleetDisplayedTravelDurationMinutes: descriptor.displayTravelMinutes,
    globalSequence: firstLoadingSegment?.globalSequence,
    hasPriorityTimeAnchor: false,
    isAspiratorMissionMerged: true,
    isSameAddressLoadingDeliveryMerged: samePhysicalSite,
    isSameSiteSequenceGroup: false,
    legacyDisplayTravelDurationMinutes: descriptor.legacyTravelMinutes,
    loadingVisitCount: descriptor.loadingSiteKeys.size,
    manualBufferLabel: null,
    manualBufferMinutes: 0,
    mergedMemberSegments: descriptor.memberSegments,
    mergedSequenceLabel: sequenceLabel,
    missionOrderIds,
    operationAddress: firstLoadingSegment?.operationAddress || firstLoadingSegment?.toAddress || "",
    operationDeliveryId: actionSegment?.operationDeliveryId || firstDeliverySegment?.operationDeliveryId || "",
    operationKey: `${firstLoadingSegment?.assignmentId || "assignment"}::${firstLoadingSegment?.tripId || "trip"}::aspirator-mission::${descriptor.missionGroupKey}`,
    operationLabel: descriptor.operationLabel,
    operationOrderId: missionOrderIds[0] || firstLoadingSegment?.operationOrderId || "",
    operationTypeLabel: "Mission Aspiration",
    operationTypes: ["loading", "delivery"],
    physicalVisitCount: new Set([...descriptor.loadingSiteKeys, ...descriptor.deliverySiteKeys]).size,
    quantityLabel: descriptor.quantityTons > 0 ? `${descriptor.quantityTons.toFixed(1)} t` : null,
    quantityTons: descriptor.quantityTons || null,
    reassignmentAvailability: actionSegment?.reassignmentAvailability || firstLoadingSegment?.reassignmentAvailability || null,
    reassignmentDrag: actionSegment?.reassignmentDrag || firstLoadingSegment?.reassignmentDrag || null,
    reassignmentPreviewContexts: descriptor.memberSegments.flatMap(
      (segment) => Array.isArray(segment?.reassignmentPreviewContexts) ? segment.reassignmentPreviewContexts : [],
    ),
    routeFromAddress: firstLoadingSegment?.operationAddress || firstLoadingSegment?.routeFromAddress || "",
    routeFromLabel: descriptor.loadingLabel,
    routeScheduledTravelDurationMinutes: descriptor.scheduledTravelMinutes,
    routeToAddress,
    routeToLabel,
    sameSiteGroupContainsMixedStopTypes: false,
    sameSiteGroupIndex: 0,
    sameSiteGroupKey: samePhysicalSite
      ? normalizeAspiratorMissionKey(firstLoadingSegment?.sameSiteGroupKey)
      : "",
    sameSiteGroupSize: 1,
    scheduledAt: null,
    scheduledAtMinutes: descriptor.displayStartMinutes,
    segmentEndAt: null,
    segmentEndMinutes: descriptor.displayEndMinutes,
    segmentReorder: firstLoadingSegment?.segmentReorder || null,
    segmentStartAt: null,
    segmentStartMinutes: descriptor.displayStartMinutes,
    serviceDurationLabel: descriptor.aspiratorMinutes > 0
      ? (formatDurationShort(descriptor.aspiratorMinutes) || "0 min")
      : "0 min",
    serviceDurationMinutes: descriptor.aspiratorMinutes,
    statusMarkers,
    statusSummaryLabel: statusMarkers.map((marker) => marker?.label).filter(Boolean).join(" · "),
    stopType: "aspirator_mission",
    timelineDurationLabel: formatDurationShort(descriptor.durationMinutes) || "0 min",
    timelineDurationMinutes: descriptor.durationMinutes,
    toAddress: finalDeliveryAddress,
    toLabel: samePhysicalSite
      ? (descriptor.loadingLabel || descriptor.deliveryLabels[0] || "")
      : (joinAspiratorMissionDistinctLabels(descriptor.deliveryLabels) || descriptor.loadingLabel),
    travelDurationLabel: descriptor.travelMinutes > 0
      ? (formatDurationShort(descriptor.travelMinutes) || "0 min")
      : "0 min",
    travelDurationMinutes: descriptor.travelMinutes,
    typeLabel: "Mission Aspiration",
    usesDurationDrivenTimelineGeometry: true,
    visualSegmentEndAt: null,
    visualSegmentEndMinutes: descriptor.displayEndMinutes,
    visualSegmentStartAt: null,
    visualSegmentStartMinutes: descriptor.displayStartMinutes,
    visualServiceDurationMinutes: descriptor.aspiratorMinutes,
    visualTimelineDurationMinutes: descriptor.durationMinutes,
    waitMinutes: 0,
  };
}

export function buildAspiratorMissionDisplaySegments(segments = []) {
  const resolvedSegments = Array.isArray(segments) ? segments.filter(Boolean) : [];
  if (
    resolvedSegments.length < 2
    || resolvedSegments.some((segment) => segment?.isAspiratorMissionMerged === true)
  ) {
    return resolvedSegments;
  }

  const groupedEntries = new Map();
  resolvedSegments.forEach((segment, index) => {
    if (!isAspiratorMissionTimelineCandidate(segment)) {
      return;
    }

    const missionGroupKey = normalizeAspiratorMissionKey(segment?.aspiratorMissionGroupKey);
    const assignmentId = normalizeAspiratorMissionKey(segment?.assignmentId);
    const tripId = normalizeAspiratorMissionKey(segment?.tripId);
    const scopedGroupKey = `${assignmentId || "assignment"}::${tripId || "trip"}::${missionGroupKey}`;
    const groupEntries = groupedEntries.get(scopedGroupKey) || [];
    groupEntries.push({ index, segment });
    groupedEntries.set(scopedGroupKey, groupEntries);
  });

  const descriptors = assignAspiratorMissionDisplayAnchors(
    Array.from(groupedEntries.values())
      .map(buildAspiratorMissionDescriptor)
      .filter(Boolean),
    resolvedSegments,
  );
  if (!descriptors.length) {
    return resolvedSegments;
  }

  const consumedIndexes = new Set(
    descriptors.flatMap((descriptor) => descriptor.memberEntries.map((entry) => entry.index)),
  );
  const descriptorByFirstLoadingIndex = new Map(
    descriptors.map((descriptor) => [descriptor.firstLoadingEntry.index, descriptor]),
  );

  return resolvedSegments.flatMap((segment, index) => {
    const descriptor = descriptorByFirstLoadingIndex.get(index);
    if (descriptor) {
      return [buildAspiratorMissionSyntheticSegment(descriptor)];
    }
    if (consumedIndexes.has(index)) {
      return [];
    }

    return [segment];
  });
}

export function resolveAspiratorMissionRouteSegmentIndexes(segment = null) {
  const missionTravelSegments = Array.isArray(segment?.aspiratorMissionTravelSegments)
    && segment.aspiratorMissionTravelSegments.length
    ? segment.aspiratorMissionTravelSegments
    : [segment];
  const seenIndexes = new Set();

  return missionTravelSegments.reduce((indexes, travelSegment) => {
    const routeSegmentIndex = Number.parseInt(
      travelSegment?.routeSegmentIndex ?? travelSegment?.index,
      10,
    );
    if (!Number.isInteger(routeSegmentIndex) || routeSegmentIndex <= 0 || seenIndexes.has(routeSegmentIndex)) {
      return indexes;
    }

    seenIndexes.add(routeSegmentIndex);
    indexes.push(routeSegmentIndex);
    return indexes;
  }, []);
}

export function collectAspiratorMissionRouteSegmentIndexes(segments = []) {
  const seenIndexes = new Set();

  return buildAspiratorMissionDisplaySegments(segments).reduce((indexes, segment) => {
    if (segment?.isAspiratorMissionMerged !== true) {
      return indexes;
    }

    resolveAspiratorMissionRouteSegmentIndexes(segment).forEach((routeSegmentIndex) => {
      if (seenIndexes.has(routeSegmentIndex)) {
        return;
      }
      seenIndexes.add(routeSegmentIndex);
      indexes.push(routeSegmentIndex);
    });
    return indexes;
  }, []);
}

function resolveFrontendDrivingSegmentOperationLabel(segment = null) {
  if (segment?.stopType === "depot_start") {
    return "Inspection";
  }

  if (segment?.isTerminalRouteMerged === true || segment?.stopType === "depot_return") {
    return "Retour dépôt";
  }

  const primaryTitle = getRouteSequencePrimaryTitle(segment);
  if (primaryTitle && primaryTitle !== "Arrêt") {
    return primaryTitle;
  }

  if (segment?.stopType === "loading") {
    return "Chargement";
  }

  if (segment?.stopType === "delivery") {
    return "Livraison";
  }

  return primaryTitle || "Arrêt";
}

function resolveFrontendDrivingSegmentStepLabel(segment = null, fallbackIndex = 0) {
  const sequenceLabel = resolveDisplayedOperationalSequenceLabel(segment);
  if (sequenceLabel) {
    return sequenceLabel;
  }

  return fallbackIndex >= 0 ? String(fallbackIndex + 1) : "";
}

function resolveVisibleRouteSegmentMetrics(segment, displayOverrides, fallbackIndex = 0) {
  const travelMinutes = getSameSiteSequenceDisplayTravelDurationMinutes(segment, displayOverrides, fallbackIndex);
  const override = getSameSiteSequenceDisplayOverride(segment, displayOverrides, fallbackIndex);
  const overrideDistanceKm = Number(override?.distanceKm);
  const distanceLabel = getSameSiteSequenceDisplayDistanceLabel(segment, displayOverrides, fallbackIndex);
  const parsedDistanceKm = parseSequenceDistanceKm(distanceLabel);
  const distanceKm = Number.isFinite(overrideDistanceKm) && overrideDistanceKm >= 0
    ? (overrideDistanceKm > 0 ? overrideDistanceKm : null)
    : (parsedDistanceKm != null && parsedDistanceKm > 0 ? parsedDistanceKm : null);

  return {
    distanceKm,
    travelMinutes: Number.isFinite(travelMinutes) && travelMinutes >= 0 ? travelMinutes : 0,
  };
}

function buildVisibleRouteSegmentDedupeKey(segment, travelMinutes, distanceKm) {
  const tripId = typeof segment?.tripId === "string" ? segment.tripId.trim() : "";
  const routeSegmentIndex = Number.parseInt(segment?.routeSegmentIndex, 10);
  const routeFromLabel = getRouteSequenceDisplayLabel(
    segment?.routeFromLabel || segment?.from?.label || segment?.fromLabel,
  ) || "";
  const routeToLabel = getRouteSequenceDisplayLabel(
    segment?.routeToLabel || segment?.to?.label || segment?.toLabel,
  ) || "";
  const normalizedDistanceKm = distanceKm != null && distanceKm > 0 ? distanceKm.toFixed(3) : "";
  const normalizedTravelMinutes = Number.isFinite(travelMinutes) && travelMinutes > 0
    ? travelMinutes.toFixed(3)
    : "";
  const routeIdentity = `${routeFromLabel}::${routeToLabel}::${normalizedDistanceKm}::${normalizedTravelMinutes}`;

  if (tripId && Number.isInteger(routeSegmentIndex) && routeSegmentIndex > 0) {
    return `${tripId}::route-segment::${routeSegmentIndex}::${routeIdentity}`;
  }

  if (!routeFromLabel && !routeToLabel && !normalizedDistanceKm) {
    return null;
  }

  return routeIdentity;
}

function buildUniqueVisibleRouteSegments(segments = []) {
  const resolvedSegments = Array.isArray(segments) ? segments.filter(Boolean) : [];
  const displayOverrides = buildSameSiteSequenceDisplayOverrides(resolvedSegments);
  const seenDedupeKeys = new Set();
  const drivingSegments = [];
  let distanceKm = 0;
  let drivingMinutes = 0;

  resolvedSegments.forEach((segment, fallbackIndex) => {
    const { travelMinutes, distanceKm: segmentDistanceKm } = resolveVisibleRouteSegmentMetrics(
      segment,
      displayOverrides,
      fallbackIndex,
    );

    if (travelMinutes <= 0 && !(segmentDistanceKm != null && segmentDistanceKm > 0)) {
      return;
    }

    const dedupeKey = buildVisibleRouteSegmentDedupeKey(segment, travelMinutes, segmentDistanceKm);
    if (dedupeKey && seenDedupeKeys.has(dedupeKey)) {
      return;
    }

    if (dedupeKey) {
      seenDedupeKeys.add(dedupeKey);
    }

    if (segmentDistanceKm != null && segmentDistanceKm > 0) {
      distanceKm += segmentDistanceKm;
    }

    drivingMinutes += travelMinutes;

    const routeFromLabel = getRouteSequenceDisplayLabel(segment?.routeFromLabel || segment?.from?.label || segment?.fromLabel);
    const routeToLabel = getRouteSequenceDisplayLabel(segment?.routeToLabel || segment?.to?.label || segment?.toLabel);

    drivingSegments.push({
      distanceKm: segmentDistanceKm,
      operationLabel: resolveFrontendDrivingSegmentOperationLabel(segment),
      routeSummaryLabel: routeFromLabel && routeToLabel
        ? `${routeFromLabel} → ${routeToLabel}`
        : routeToLabel || routeFromLabel || "",
      stepLabel: resolveFrontendDrivingSegmentStepLabel(segment, fallbackIndex),
      travelMinutes,
    });
  });

  return {
    distanceKm: distanceKm > 0 ? distanceKm : null,
    drivingMinutes,
    drivingSegments,
  };
}

function buildDisplayedTimelineDrivingSegments(segments = []) {
  return buildUniqueVisibleRouteSegments(segments).drivingSegments;
}

function hasAbsorbedSameAddressGroupIdentity(leftSegment = null, rightSegment = null) {
  const leftOrderKey = normalizeMergedSameAddressOrderKey(leftSegment);
  const rightOrderKey = normalizeMergedSameAddressOrderKey(rightSegment);

  if (!leftOrderKey || !rightOrderKey || leftOrderKey !== rightOrderKey) {
    return false;
  }

  const leftAddressKey = normalizeMergedSameAddressTextValue(
    leftSegment?.operationAddress || leftSegment?.toAddress || leftSegment?.fromAddress,
  );
  const rightAddressKey = normalizeMergedSameAddressTextValue(
    rightSegment?.operationAddress || rightSegment?.toAddress || rightSegment?.fromAddress,
  );

  if (leftAddressKey && rightAddressKey && leftAddressKey !== rightAddressKey) {
    return false;
  }

  const leftStopType = normalizeMergedSameAddressStopType(leftSegment?.stopType);
  const rightStopType = normalizeMergedSameAddressStopType(rightSegment?.stopType);

  if (leftStopType === "loading" && rightStopType === "delivery") {
    return true;
  }

  const leftRouteFromKey = normalizeMergedSameAddressTextValue(leftSegment?.routeFromLabel || leftSegment?.fromLabel);
  const rightRouteFromKey = normalizeMergedSameAddressTextValue(rightSegment?.routeFromLabel || rightSegment?.fromLabel);

  if (leftRouteFromKey && rightRouteFromKey && leftRouteFromKey !== rightRouteFromKey) {
    return false;
  }

  const leftRouteToKey = normalizeMergedSameAddressTextValue(leftSegment?.routeToLabel || leftSegment?.toLabel);
  const rightRouteToKey = normalizeMergedSameAddressTextValue(rightSegment?.routeToLabel || rightSegment?.toLabel);

  if (leftRouteToKey && rightRouteToKey && leftRouteToKey !== rightRouteToKey) {
    return false;
  }

  return true;
}

function normalizeMergedSameAddressAddressKey(address = "") {
  return normalizeMergedSameAddressTextValue(address)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ");
}

function normalizeMergedSameAddressSiteKey(segment = null) {
  const address = segment?.operationAddress
    || segment?.toAddress
    || segment?.routeToAddress
    || segment?.to?.address
    || "";

  return normalizeMergedSameAddressAddressKey(address);
}

function buildMergedSameAddressOrderKeySet(memberSegments = [], stopType = "") {
  return new Set(
    (Array.isArray(memberSegments) ? memberSegments : [])
      .filter((segment) => normalizeMergedSameAddressStopType(segment?.stopType) === stopType)
      .map((segment) => normalizeMergedSameAddressOrderKey(segment))
      .filter(Boolean),
  );
}

function isMergedSameAddressLoadingDeliveryVisit(memberSegments = []) {
  const resolvedMemberSegments = Array.isArray(memberSegments) ? memberSegments.filter(Boolean) : [];
  if (resolvedMemberSegments.length < 2 || !resolvedMemberSegments.some((segment) => segment?.requiresAspirator === true)) {
    return false;
  }

  const loadingOrderKeys = buildMergedSameAddressOrderKeySet(resolvedMemberSegments, "loading");
  const deliveryOrderKeys = buildMergedSameAddressOrderKeySet(resolvedMemberSegments, "delivery");
  if (
    !loadingOrderKeys.size
    || loadingOrderKeys.size !== deliveryOrderKeys.size
    || Array.from(loadingOrderKeys).some((orderKey) => !deliveryOrderKeys.has(orderKey))
  ) {
    return false;
  }

  const identifiedMemberCount = resolvedMemberSegments.filter(
    (segment) => Boolean(normalizeMergedSameAddressOrderKey(segment)),
  ).length;
  if (identifiedMemberCount !== resolvedMemberSegments.length) {
    return false;
  }

  const aspiratorMissionGroupKeys = new Set(
    resolvedMemberSegments
      .map((segment) => normalizeAspiratorMissionKey(segment?.aspiratorMissionGroupKey))
      .filter(Boolean),
  );
  if (
    aspiratorMissionGroupKeys.size === 1
    && resolvedMemberSegments.every((segment) => segment?.requiresAspirator === true)
  ) {
    return true;
  }

  const sameSiteGroupKeys = new Set(
    resolvedMemberSegments
      .map((segment) => (typeof segment?.sameSiteGroupKey === "string" ? segment.sameSiteGroupKey.trim() : ""))
      .filter(Boolean),
  );
  if (sameSiteGroupKeys.size === 1) {
    return true;
  }

  const siteKeys = new Set(resolvedMemberSegments.map(normalizeMergedSameAddressSiteKey).filter(Boolean));
  return siteKeys.size === 1;
}

function isSameSiteGroupRunLead(segment = null, stopType = "") {
  const sameSiteGroupKey = typeof segment?.sameSiteGroupKey === "string"
    ? segment.sameSiteGroupKey.trim()
    : "";
  const sameSiteGroupSize = Number.isInteger(segment?.sameSiteGroupSize) ? segment.sameSiteGroupSize : 1;

  return Boolean(
    segment?.isSameSiteSequenceGroup
    && sameSiteGroupKey
    && sameSiteGroupSize > 1
    && segment?.sameSiteGroupIndex === 0
    && normalizeMergedSameAddressStopType(segment?.stopType) === stopType,
  );
}

function hasMergedSameAddressRouteMovement(segment = null) {
  const distanceKm = parseSequenceDistanceKm(segment?.distanceLabel);
  const hasPositiveMovement = (distanceKm != null && distanceKm > 0) || [
    segment?.travelDurationMinutes,
    segment?.displayTravelDurationMinutes,
    segment?.routeScheduledTravelDurationMinutes,
    segment?.routeEstimatedTravelDurationMinutes,
  ].some((durationMinutes) => {
    const resolvedDurationMinutes = Number(durationMinutes);
    return Number.isFinite(resolvedDurationMinutes) && resolvedDurationMinutes > 0;
  });
  if (!hasPositiveMovement) {
    return false;
  }

  const operationSiteKey = normalizeMergedSameAddressSiteKey(segment);
  const routeFromSiteKey = normalizeMergedSameAddressAddressKey(
    segment?.routeFromAddress || segment?.fromAddress || "",
  );

  // Les segments de chronologie portent normalement le trajet sortant de
  // l'opération courante. Il ne sépare donc pas le chargement du déchargement
  // regroupé lorsque ce trajet part précisément du site de la visite.
  return !operationSiteKey || !routeFromSiteKey || operationSiteKey !== routeFromSiteKey;
}

function findAdjacentAspiratorLoadingDeliveryGroupVisit(resolvedSegments = [], startIndex = 0) {
  const loadingLead = resolvedSegments[startIndex] || null;
  if (!isSameSiteGroupRunLead(loadingLead, "loading")) {
    return null;
  }

  const loadingGroupKey = loadingLead.sameSiteGroupKey.trim();
  const loadingGroup = collectSameSiteGroupMembers(resolvedSegments, startIndex, loadingGroupKey);
  if (
    loadingGroup.memberSegments.some(
      (segment) => normalizeMergedSameAddressStopType(segment?.stopType) !== "loading",
    )
  ) {
    return null;
  }

  const deliveryLead = resolvedSegments[loadingGroup.groupEnd] || null;
  if (!isSameSiteGroupRunLead(deliveryLead, "delivery") || hasMergedSameAddressRouteMovement(deliveryLead)) {
    return null;
  }

  const deliveryGroupKey = deliveryLead.sameSiteGroupKey.trim();
  const deliveryGroup = collectSameSiteGroupMembers(resolvedSegments, loadingGroup.groupEnd, deliveryGroupKey);
  if (
    deliveryGroup.memberSegments.some(
      (segment) => normalizeMergedSameAddressStopType(segment?.stopType) !== "delivery",
    )
  ) {
    return null;
  }

  const memberSegments = [...loadingGroup.memberSegments, ...deliveryGroup.memberSegments];
  if (!isMergedSameAddressLoadingDeliveryVisit(memberSegments)) {
    return null;
  }

  const assignmentKeys = new Set(
    memberSegments
      .map((segment) => normalizeMergedSameAddressTextValue(segment?.assignmentId))
      .filter(Boolean),
  );
  const tripKeys = new Set(
    memberSegments
      .map((segment) => normalizeMergedSameAddressTextValue(segment?.tripId))
      .filter(Boolean),
  );
  if (assignmentKeys.size !== 1 || tripKeys.size !== 1) {
    return null;
  }

  const loadingLastSequence = Number(loadingGroup.memberSegments[loadingGroup.memberSegments.length - 1]?.globalSequence);
  const deliveryFirstSequence = Number(deliveryLead?.globalSequence);
  if (
    Number.isInteger(loadingLastSequence)
    && Number.isInteger(deliveryFirstSequence)
    && deliveryFirstSequence !== loadingLastSequence + 1
  ) {
    return null;
  }

  return {
    groupEnd: deliveryGroup.groupEnd,
    memberSegments,
    visualGroupKey: `${loadingGroupKey}::${deliveryGroupKey}::aspirator-visit`,
  };
}

function findAbsorbingSameAddressGroupLead(resolvedSegments = [], startIndex = 0) {
  const segment = resolvedSegments[startIndex] || null;
  if (
    !segment
    || segment?.isSameSiteSequenceGroup
    || segment?.requiresAspirator !== true
    || normalizeMergedSameAddressStopType(segment?.stopType) !== "loading"
  ) {
    return null;
  }

  for (let candidateIndex = startIndex + 1; candidateIndex < resolvedSegments.length; candidateIndex += 1) {
    const candidateSegment = resolvedSegments[candidateIndex] || null;
    const candidateGroupKey = typeof candidateSegment?.sameSiteGroupKey === "string"
      ? candidateSegment.sameSiteGroupKey.trim()
      : "";
    const candidateGroupSize = Number.isInteger(candidateSegment?.sameSiteGroupSize) ? candidateSegment.sameSiteGroupSize : 1;

    if (
      !candidateSegment?.isSameSiteSequenceGroup
      || !candidateGroupKey
      || candidateGroupSize <= 1
      || candidateSegment?.sameSiteGroupIndex !== 0
    ) {
      continue;
    }

    for (let memberIndex = candidateIndex; memberIndex < resolvedSegments.length; memberIndex += 1) {
      const candidateMember = resolvedSegments[memberIndex] || null;
      const candidateMemberGroupKey = typeof candidateMember?.sameSiteGroupKey === "string"
        ? candidateMember.sameSiteGroupKey.trim()
        : "";

      if (candidateMemberGroupKey !== candidateGroupKey) {
        break;
      }

      if (
        candidateMember?.requiresAspirator === true
        && normalizeMergedSameAddressStopType(candidateMember?.stopType) === "delivery"
        && hasAbsorbedSameAddressGroupIdentity(segment, candidateMember)
      ) {
        return candidateSegment;
      }
    }
  }

  return null;
}

function resolveMergedSameAddressOrderLabel(segment = null) {
  const operationOrderId = typeof segment?.operationOrderId === "string"
    ? segment.operationOrderId.trim()
    : "";
  if (operationOrderId) {
    return operationOrderId;
  }

  return typeof segment?.orderLabel === "string" ? segment.orderLabel.trim() : "";
}

function mergeMergedSameAddressStatusMarkers(memberSegments = []) {
  const mergedMarkers = [];
  const seenMarkerKeys = new Set();

  (Array.isArray(memberSegments) ? memberSegments : []).forEach((segment) => {
    (Array.isArray(segment?.statusMarkers) ? segment.statusMarkers : []).forEach((marker) => {
      const markerKey = typeof marker?.key === "string" && marker.key.trim()
        ? marker.key.trim()
        : typeof marker?.label === "string"
          ? marker.label.trim()
          : "";

      if (!markerKey || seenMarkerKeys.has(markerKey)) {
        return;
      }

      seenMarkerKeys.add(markerKey);
      mergedMarkers.push(marker);
    });
  });

  return mergedMarkers;
}

function resolveMergedSameAddressActionSegment(memberSegments = [], fallbackSegment = null) {
  const resolvedMemberSegments = Array.isArray(memberSegments) ? memberSegments.filter(Boolean) : [];

  return resolvedMemberSegments.find((segment) => {
    const businessStep = segment?.businessStep || null;
    return Boolean(
      segment?.reassignmentDrag
      || segment?.isSelectable
      || segment?.clientId
      || businessStep?.dispatcherActionButton
      || businessStep?.confirmButton
      || businessStep?.reassignButton
      || businessStep?.releaseButton
      || businessStep?.suspendButton
    );
  }) || fallbackSegment;
}

function isMergedSameAddressLoadingDeliveryPair(memberSegments = []) {
  const resolvedMemberSegments = Array.isArray(memberSegments) ? memberSegments.filter(Boolean) : [];
  if (resolvedMemberSegments.length !== 2) {
    return false;
  }

  if (!resolvedMemberSegments.some((segment) => segment?.requiresAspirator === true)) {
    return false;
  }

  const sameSiteGroupKeys = new Set(
    resolvedMemberSegments
      .map((segment) => (typeof segment?.sameSiteGroupKey === "string" ? segment.sameSiteGroupKey.trim() : ""))
      .filter(Boolean),
  );
  if (sameSiteGroupKeys.size !== 1) {
    return false;
  }

  const stopTypes = resolvedMemberSegments
    .map((segment) => normalizeMergedSameAddressStopType(segment?.stopType))
    .sort();
  if (stopTypes.length !== 2 || stopTypes[0] !== "delivery" || stopTypes[1] !== "loading") {
    return false;
  }

  const orderKeys = new Set(
    resolvedMemberSegments
      .map((segment) => normalizeMergedSameAddressOrderKey(segment))
      .filter(Boolean),
  );

  return orderKeys.size <= 1;
}

function isFallbackSameSiteGroupKey(groupKey = "") {
  const normalizedGroupKey = typeof groupKey === "string" ? groupKey.trim() : "";
  return normalizedGroupKey.includes("::address:") || normalizedGroupKey.includes("::coords:");
}

function buildMergedSameAddressTimelineSegment(
  memberSegments = [],
  {
    leftPercent = 0,
    servicePercent = 0,
    widthPercent = 0,
  } = {},
) {
  const resolvedMemberSegments = Array.isArray(memberSegments) ? memberSegments.filter(Boolean) : [];
  const firstSegment = resolvedMemberSegments[0] || null;
  const lastSegment = resolvedMemberSegments[resolvedMemberSegments.length - 1] || firstSegment;
  const themedSegment = firstSegment;
  const actionSegment = resolveMergedSameAddressActionSegment(resolvedMemberSegments, themedSegment);
  const firstSequence = Number(firstSegment?.globalSequence) || 0;
  const lastSequence = Number(lastSegment?.globalSequence) || firstSequence;
  const mergedSequenceLabel = firstSequence && lastSequence && firstSequence !== lastSequence
    ? `${firstSequence}-${lastSequence}`
    : (firstSequence ? String(firstSequence) : "");
  const mergedTravelDurationMinutes = resolvedMemberSegments.reduce(
    (totalMinutes, segment) => totalMinutes + resolveSequenceTravelMinutes(segment),
    0,
  );
  const mergedDisplayTravelDurationMinutes = resolvedMemberSegments.reduce(
    (totalMinutes, segment) => totalMinutes + resolveTimelineDisplayTravelDurationMinutes(segment),
    0,
  );
  const mergedLegacyDisplayTravelDurationMinutes = resolvedMemberSegments.reduce(
    (totalMinutes, segment) => totalMinutes + resolveSequenceLegacyDisplayTravelMinutes(segment),
    0,
  );
  const mergedScheduledTravelDurationMinutes = resolvedMemberSegments.reduce((totalMinutes, segment) => {
    return totalMinutes + Math.max(resolveSequenceScheduledTravelMinutes(segment) ?? 0, 0);
  }, 0);
  const mergedServiceDurationMinutes = resolvedMemberSegments.reduce(
    (totalMinutes, segment) => totalMinutes + resolveTimelineDisplayServiceDurationMinutes(segment),
    0,
  );
  const mergedManualBufferMinutes = resolvedMemberSegments.reduce(
    (totalMinutes, segment) => totalMinutes + resolveSequenceManualBufferMinutes(segment),
    0,
  );
  const mergedBaseTimelineDurationMinutes = mergedServiceDurationMinutes + mergedTravelDurationMinutes;
  const mergedTimelineDurationMinutes = mergedBaseTimelineDurationMinutes + mergedManualBufferMinutes;
  const mergedQuantityTons = resolveMergedSameAddressQuantityTons(resolvedMemberSegments);
  const mergedQuantityLabel = formatMergedSameAddressQuantityLabel(mergedQuantityTons);
  const mergedDistanceKm = resolvedMemberSegments.reduce((totalDistanceKm, segment) => {
    return totalDistanceKm + Math.max(parseSequenceDistanceKm(segment?.distanceLabel) ?? 0, 0);
  }, 0);
  const mergedRouteSegmentIndex = resolveGroupedHoverRouteSegmentIndex(resolvedMemberSegments);
  const mergedStatusMarkers = mergeMergedSameAddressStatusMarkers(resolvedMemberSegments);
  const mergedStatusSummaryLabel = Array.from(new Set(
    resolvedMemberSegments
      .map((segment) => (typeof segment?.statusSummaryLabel === "string" ? segment.statusSummaryLabel.trim() : ""))
      .filter(Boolean),
  )).join(" · ");
  const mergedClientId = typeof actionSegment?.clientId === "string" && actionSegment.clientId
    ? actionSegment.clientId
    : (resolvedMemberSegments.find((segment) => typeof segment?.clientId === "string" && segment.clientId)?.clientId || "");
  const mergedStopType = normalizeMergedSameAddressStopType(themedSegment?.stopType)
    || normalizeMergedSameAddressStopType(actionSegment?.stopType)
    || "loading";
  const mergedSegmentEndMinutes = resolveRenderSegmentEndMinutes(lastSegment) ?? resolveRenderSegmentEndMinutes(themedSegment);
  const mergedSegmentEndAt = lastSegment?.visualSegmentEndAt
    || lastSegment?.segmentEndAt
    || themedSegment?.visualSegmentEndAt
    || themedSegment?.segmentEndAt
    || null;
  const mergedOrderLabel = resolveMergedSameAddressOrderLabel(themedSegment)
    || resolveMergedSameAddressOrderLabel(actionSegment)
    || "";
  const mergedOperationTypes = Array.from(new Set(
    resolvedMemberSegments
      .map((segment) => normalizeMergedSameAddressStopType(segment?.stopType))
      .filter(Boolean),
  ));
  const mergedAspiratorServiceMinutes = resolvedMemberSegments.reduce(
    (maxMinutes, segment) => Math.max(maxMinutes, resolveTimelineAspiratorServiceMinutes(segment)),
    0,
  );
  const mergedCompartmentFill = summarizeCompartmentFillSegments(resolvedMemberSegments);

  return {
    ...themedSegment,
    actionSegmentSequence: Number.isFinite(Number(actionSegment?.globalSequence))
      ? Number(actionSegment.globalSequence)
      : (Number.isFinite(Number(themedSegment?.globalSequence)) ? Number(themedSegment.globalSequence) : null),
    baseTimelineDurationLabel: formatDurationShort(mergedBaseTimelineDurationMinutes) || "0 min",
    baseTimelineDurationMinutes: mergedBaseTimelineDurationMinutes,
    businessStep: actionSegment?.businessStep ?? themedSegment?.businessStep ?? null,
    clientId: mergedClientId,
    compartmentFillOrderCount: mergedCompartmentFill.limitedCount,
    compartmentFillPercent: mergedCompartmentFill.percent,
    compartmentFillProductCode: mergedCompartmentFill.code,
    aspirator_service_minutes: mergedAspiratorServiceMinutes,
    aspiratorServiceMinutes: mergedAspiratorServiceMinutes,
    deliveryVisitCount: mergedOperationTypes.includes("delivery") ? 1 : 0,
    displayTravelDurationMinutes: mergedDisplayTravelDurationMinutes,
    displayedSegmentEndAt: mergedSegmentEndAt,
    displayedSegmentEndMinutes: mergedSegmentEndMinutes,
    distanceLabel: mergedDistanceKm > 0
      ? formatSequenceDistanceLabel(mergedDistanceKm)
      : (themedSegment?.distanceLabel || lastSegment?.distanceLabel || "0 km"),
    dominantPriority: resolvedMemberSegments.find((segment) => typeof segment?.dominantPriority === "string" && segment.dominantPriority.trim())?.dominantPriority
      || themedSegment?.dominantPriority
      || "",
    fleetDisplayedTravelDurationMinutes: mergedDisplayTravelDurationMinutes,
    fleetDisplayedTravelUsesHereTraffic: resolvedMemberSegments.some((segment) => segment?.fleetDisplayedTravelUsesHereTraffic === true),
    hasPriorityTimeAnchor: resolvedMemberSegments.some((segment) => segment?.hasPriorityTimeAnchor),
    isSameAddressLoadingDeliveryMerged: true,
    isSelectable: Boolean(actionSegment?.isSelectable || themedSegment?.isSelectable),
    legacyDisplayTravelDurationMinutes: mergedLegacyDisplayTravelDurationMinutes,
    leftPercent,
    loadingVisitCount: mergedOperationTypes.includes("loading") ? 1 : 0,
    manualBufferMinutes: mergedManualBufferMinutes,
    mergedMemberSegments: resolvedMemberSegments,
    mergedSequenceLabel,
    operationTypes: mergedOperationTypes,
    physicalVisitCount: 1,
    operationOrderId: mergedOrderLabel,
    operationTypeLabel: "Chargement + déchargement",
    orderLabel: themedSegment?.orderLabel || actionSegment?.orderLabel || mergedOrderLabel,
    chips: buildMergedSameAddressChips(resolvedMemberSegments, themedSegment),
    quantityLabel: mergedQuantityLabel || themedSegment?.quantityLabel || actionSegment?.quantityLabel || null,
    quantityTons: mergedQuantityTons > 0
      ? mergedQuantityTons
      : (Number.isFinite(Number(themedSegment?.quantityTons)) ? Number(themedSegment.quantityTons) : null),
    reassignmentDrag: actionSegment?.reassignmentDrag ?? themedSegment?.reassignmentDrag,
    requiresAspirator: resolvedMemberSegments.some((segment) => segment?.requiresAspirator),
    requiresOntario: resolvedMemberSegments.some((segment) => segment?.requiresOntario),
    routeSegmentIndex: mergedRouteSegmentIndex != null
      ? String(mergedRouteSegmentIndex)
      : (actionSegment?.routeSegmentIndex ?? themedSegment?.routeSegmentIndex),
    routeScheduledTravelDurationMinutes: mergedScheduledTravelDurationMinutes,
    sameSiteGroupContainsMixedStopTypes: true,
    sameSiteGroupIndex: 0,
    sameSiteGroupSize: resolvedMemberSegments.length,
    sameSiteGroupStartSequence: firstSequence || themedSegment?.sameSiteGroupStartSequence || 0,
    segmentEndAt: mergedSegmentEndAt,
    segmentEndMinutes: mergedSegmentEndMinutes,
    serviceDurationLabel: formatDurationShort(mergedServiceDurationMinutes) || "0 min",
    serviceDurationMinutes: mergedServiceDurationMinutes,
    servicePercent,
    statusMarkers: mergedStatusMarkers,
    statusSummaryLabel: mergedStatusSummaryLabel,
    stopType: mergedStopType,
    timelineDurationLabel: formatDurationShort(mergedTimelineDurationMinutes) || "0 min",
    timelineDurationMinutes: mergedTimelineDurationMinutes,
    travelDurationLabel: mergedTravelDurationMinutes > 0
      ? (formatDurationShort(mergedTravelDurationMinutes) || "0 min")
      : "0 min",
    travelDurationMinutes: mergedTravelDurationMinutes,
    typeLabel: "Chargement + déchargement",
    visualSegmentEndAt: mergedSegmentEndAt,
    visualSegmentEndMinutes: mergedSegmentEndMinutes,
    visualTimelineDurationMinutes: mergedTimelineDurationMinutes,
    widthPercent,
  };
}

function resolveMergedSameAddressTimelineLayout(memberSegments = []) {
  const resolvedMemberSegments = Array.isArray(memberSegments) ? memberSegments.filter(Boolean) : [];
  const firstSegment = resolvedMemberSegments[0] || null;
  const lastSegment = resolvedMemberSegments[resolvedMemberSegments.length - 1] || firstSegment;
  const firstLeftPercent = Number(firstSegment?.leftPercent);
  const lastLeftPercent = Number(lastSegment?.leftPercent);
  const lastWidthPercent = Number(lastSegment?.widthPercent);
  const leftPercent = Number.isFinite(firstLeftPercent) ? firstLeftPercent : 0;
  const rawWidthPercent = Number.isFinite(lastLeftPercent) && Number.isFinite(lastWidthPercent)
    ? Math.max((lastLeftPercent + lastWidthPercent) - leftPercent, 0)
    : Math.max(
      resolvedMemberSegments.reduce((totalWidth, segment) => {
        const segmentWidthPercent = Number(segment?.widthPercent);
        return Number.isFinite(segmentWidthPercent) && segmentWidthPercent > 0
          ? totalWidth + segmentWidthPercent
          : totalWidth;
      }, 0),
      0,
    );
  const serviceDurationMinutes = resolvedMemberSegments.reduce((totalMinutes, segment) => {
    return totalMinutes + resolveTimelineDisplayServiceDurationMinutes(segment);
  }, 0);
  const displayTravelDurationMinutes = resolvedMemberSegments.reduce((totalMinutes, segment) => {
    return totalMinutes + resolveTimelineDisplayTravelDurationMinutes(segment);
  }, 0);
  const manualBufferMinutes = resolvedMemberSegments.reduce((totalMinutes, segment) => {
    return totalMinutes + resolveSequenceManualBufferMinutes(segment);
  }, 0);
  const displayedOverlayDurationMinutes = displayTravelDurationMinutes + serviceDurationMinutes + manualBufferMinutes;
  const firstStartMinutes = resolveRenderSegmentStartMinutes(firstSegment);
  const lastEndMinutes = resolveRenderSegmentEndMinutes(lastSegment);
  const timelineScaleMinutes = resolveTimelineScaleMinutesFromPositionedItem(firstSegment)
    ?? resolveTimelineScaleMinutesFromPositionedItem(lastSegment);
  const timelineUsablePercent = resolveTimelineUsablePercentFromPositionedItem(firstSegment)
    ?? resolveTimelineUsablePercentFromPositionedItem(lastSegment);
  const absoluteSpanMinutes = Number.isFinite(firstStartMinutes) && Number.isFinite(lastEndMinutes)
    ? Math.max(lastEndMinutes - firstStartMinutes, 0)
    : 0;
  const scaledWidthPercent = Number.isFinite(timelineScaleMinutes)
    && timelineScaleMinutes > 0
    && timelineUsablePercent > 0
    && (absoluteSpanMinutes > 0 || displayedOverlayDurationMinutes > 0)
    ? ((absoluteSpanMinutes > 0 ? absoluteSpanMinutes : displayedOverlayDurationMinutes) / timelineScaleMinutes) * timelineUsablePercent
    : null;
  const widthPercent = Number.isFinite(scaledWidthPercent) && scaledWidthPercent > 0
    ? Math.max(scaledWidthPercent, rawWidthPercent)
    : rawWidthPercent;

  return {
    leftPercent,
    servicePercent: displayedOverlayDurationMinutes > 0 && serviceDurationMinutes > 0
      ? Math.min((serviceDurationMinutes / displayedOverlayDurationMinutes) * 100, 100)
      : 0,
    widthPercent,
  };
}

function buildMergedSameAddressGroupMemberSegment(memberSegments = []) {
  const mergedSegment = buildMergedSameAddressTimelineSegment(
    memberSegments,
    resolveMergedSameAddressTimelineLayout(memberSegments),
  );

  return {
    ...mergedSegment,
    isSameSiteSequenceGroup: false,
    sameSiteGroupContainsMixedStopTypes: false,
    sameSiteGroupIndex: null,
    sameSiteGroupSize: 1,
  };
}

function normalizeSameSiteGroupMemberSegments(memberSegments = []) {
  const resolvedMemberSegments = Array.isArray(memberSegments) ? memberSegments.filter(Boolean) : [];
  if (resolvedMemberSegments.length < 2) {
    return resolvedMemberSegments;
  }

  const normalizedMemberSegments = [];
  let cursor = 0;

  while (cursor < resolvedMemberSegments.length) {
    const currentSegment = resolvedMemberSegments[cursor] || null;
    const nextSegment = resolvedMemberSegments[cursor + 1] || null;

    if (nextSegment && isMergedSameAddressLoadingDeliveryPair([currentSegment, nextSegment])) {
      normalizedMemberSegments.push(
        buildMergedSameAddressGroupMemberSegment([currentSegment, nextSegment]),
      );
      cursor += 2;
      continue;
    }

    normalizedMemberSegments.push(currentSegment);
    cursor += 1;
  }

  return normalizedMemberSegments;
}

function resolveSameSiteGroupContainsMixedStopTypes(
  rawMemberSegments = [],
  normalizedMemberSegments = rawMemberSegments,
) {
  const resolvedRawMemberSegments = Array.isArray(rawMemberSegments) ? rawMemberSegments.filter(Boolean) : [];
  const resolvedNormalizedMemberSegments = Array.isArray(normalizedMemberSegments)
    ? normalizedMemberSegments.filter(Boolean)
    : [];
  const rawHasMixedStopTypes = resolvedRawMemberSegments.some((segment) => segment?.sameSiteGroupContainsMixedStopTypes);

  if (resolvedNormalizedMemberSegments.length >= resolvedRawMemberSegments.length) {
    return rawHasMixedStopTypes;
  }

  const normalizedStopTypes = new Set(
    resolvedNormalizedMemberSegments
      .map((segment) => normalizeMergedSameAddressStopType(segment?.stopType))
      .filter(Boolean),
  );

  return normalizedStopTypes.size > 1;
}

function resolveTerminalRouteMarkerEndMinutes(terminalItem = null) {
  const segmentEndMinutes = resolveRenderSegmentEndMinutes(terminalItem);
  if (segmentEndMinutes != null) {
    return segmentEndMinutes;
  }

  const scheduledAtMinutes = terminalItem?.scheduledAtMinutes;
  if (typeof scheduledAtMinutes === "number" && Number.isFinite(scheduledAtMinutes)) {
    return scheduledAtMinutes;
  }

  const arrivalAtMinutes = terminalItem?.arrivalAtMinutes;
  return typeof arrivalAtMinutes === "number" && Number.isFinite(arrivalAtMinutes)
    ? arrivalAtMinutes
    : null;
}

function resolveTerminalRouteMarkerEndAt(terminalItem = null) {
  if (typeof terminalItem?.visualSegmentEndAt === "string" && terminalItem.visualSegmentEndAt.trim()) {
    return terminalItem.visualSegmentEndAt.trim();
  }

  if (typeof terminalItem?.segmentEndAt === "string" && terminalItem.segmentEndAt.trim()) {
    return terminalItem.segmentEndAt.trim();
  }

  if (typeof terminalItem?.scheduledAt === "string" && terminalItem.scheduledAt.trim()) {
    return terminalItem.scheduledAt.trim();
  }

  if (typeof terminalItem?.arrivalAt === "string" && terminalItem.arrivalAt.trim()) {
    return terminalItem.arrivalAt.trim();
  }

  return null;
}

export function buildFleetTimelineDisplaySegments(segments = []) {
  const displaySegments = buildFleetSequenceDisplayItems(segments);

  if (displaySegments.length < 2) {
    return displaySegments;
  }

  const terminalItemIndex = displaySegments.length - 1;
  const terminalItem = displaySegments[terminalItemIndex] || null;

  if (!terminalItem?.isTerminalRouteMarker) {
    return displaySegments;
  }

  const previousItem = displaySegments[terminalItemIndex - 1] || null;

  if (!previousItem) {
    return displaySegments;
  }

  const previousLeftPercent = Number(previousItem?.leftPercent);
  const resolvedServiceDurationMinutes = resolveTimelineDisplayServiceDurationMinutes(previousItem);
  const resolvedManualBufferMinutes = resolveSequenceManualBufferMinutes(previousItem);
  const previousStartMinutes = resolveRenderSegmentStartMinutes(previousItem);
  const terminalEndMinutes = resolveTerminalRouteMarkerEndMinutes(terminalItem);
  const terminalEndAt = resolveTerminalRouteMarkerEndAt(terminalItem);
  const terminalMergedStopEndMinutes = Number.isFinite(Number(previousItem?.terminalMergedStopEndMinutes))
    ? Number(previousItem.terminalMergedStopEndMinutes)
    : null;
  const terminalMergedStopEndAt = typeof previousItem?.terminalMergedStopEndAt === "string" && previousItem.terminalMergedStopEndAt.trim()
    ? previousItem.terminalMergedStopEndAt.trim()
    : null;
  const visualEndMinutes = previousItem?.isTerminalRouteMerged === true && terminalMergedStopEndMinutes != null
    ? terminalMergedStopEndMinutes
    : terminalEndMinutes;
  const visualEndAt = previousItem?.isTerminalRouteMerged === true && terminalMergedStopEndMinutes != null
    ? terminalMergedStopEndAt
    : terminalEndAt;
  const timelineScaleMinutes = resolveTimelineScaleMinutesFromPositionedItem(previousItem)
    ?? resolveTimelineScaleMinutesFromPositionedItem(terminalItem);
  const timelineUsablePercent = resolveTimelineUsablePercentFromPositionedItem(previousItem)
    ?? resolveTimelineUsablePercentFromPositionedItem(terminalItem);
  const combinedBaseTimelineDurationMinutes = resolvedServiceDurationMinutes + resolveSequenceTravelMinutes(previousItem);
  const usesDurationDrivenTerminalGeometry = previousItem?.usesDurationDrivenTimelineGeometry === true
    || terminalItem?.usesDurationDrivenTimelineGeometry === true;
  const combinedDisplayTravelDurationMinutes = previousItem?.isTerminalRouteMerged === true
    ? resolveTimelineDisplayTravelDurationMinutes(previousItem)
    : resolveTimelineDisplayTravelDurationMinutes(previousItem)
      + resolveTimelineDisplayTravelDurationMinutes(terminalItem);
  const combinedDisplayedOverlayDurationMinutes = combinedDisplayTravelDurationMinutes + resolvedServiceDurationMinutes + resolvedManualBufferMinutes;
  const durationDrivenTerminalDurationMinutes = combinedDisplayedOverlayDurationMinutes;
  const combinedTimelineDurationMinutes = Number.isFinite(previousStartMinutes) && Number.isFinite(visualEndMinutes)
    ? (
        usesDurationDrivenTerminalGeometry
          ? durationDrivenTerminalDurationMinutes
          : Math.max(visualEndMinutes - previousStartMinutes, 0)
      )
    : combinedBaseTimelineDurationMinutes + resolvedManualBufferMinutes;
  const combinedVisualTimelineDurationMinutes = Number.isFinite(previousStartMinutes) && Number.isFinite(visualEndMinutes)
    ? (
        usesDurationDrivenTerminalGeometry
          ? durationDrivenTerminalDurationMinutes
          : Math.max(visualEndMinutes - previousStartMinutes, 0)
      )
    : combinedBaseTimelineDurationMinutes + resolvedManualBufferMinutes;
  const combinedWidthPercent = Number.isFinite(previousLeftPercent)
    && Number.isFinite(timelineScaleMinutes)
    && timelineScaleMinutes > 0
    ? Math.max((combinedVisualTimelineDurationMinutes / timelineScaleMinutes) * timelineUsablePercent, 0)
    : previousItem.widthPercent;
  const combinedEndMinutes = usesDurationDrivenTerminalGeometry && Number.isFinite(previousStartMinutes)
    ? previousStartMinutes + combinedVisualTimelineDurationMinutes
    : Number.isFinite(visualEndMinutes)
    ? visualEndMinutes
    : Number.isFinite(previousStartMinutes)
      ? previousStartMinutes + combinedTimelineDurationMinutes
      : previousItem.segmentEndMinutes;
  const combinedEndAt = usesDurationDrivenTerminalGeometry
    ? null
    : (visualEndAt || previousItem.segmentEndAt || null);
  const terminalMarkerLeftPercent = Number.isFinite(previousLeftPercent) && Number.isFinite(combinedWidthPercent)
    ? previousLeftPercent + combinedWidthPercent
    : terminalItem.leftPercent;

  return [
    ...displaySegments.slice(0, terminalItemIndex - 1),
    {
      ...previousItem,
      baseTimelineDurationLabel: formatDurationShort(usesDurationDrivenTerminalGeometry ? durationDrivenTerminalDurationMinutes : combinedBaseTimelineDurationMinutes) || "0 min",
      baseTimelineDurationMinutes: usesDurationDrivenTerminalGeometry ? durationDrivenTerminalDurationMinutes : combinedBaseTimelineDurationMinutes,
      displayedSegmentEndAt: combinedEndAt,
      displayedSegmentEndMinutes: combinedEndMinutes,
      segmentEndAt: combinedEndAt,
      segmentEndMinutes: combinedEndMinutes,
      servicePercent: combinedDisplayedOverlayDurationMinutes > 0 && resolvedServiceDurationMinutes > 0
        ? Math.min((resolvedServiceDurationMinutes / combinedDisplayedOverlayDurationMinutes) * 100, 100)
        : 0,
      timelineDurationLabel: combinedTimelineDurationMinutes > 0
        ? (formatDurationShort(combinedTimelineDurationMinutes) || "0 min")
        : null,
      timelineDurationMinutes: combinedTimelineDurationMinutes,
      terminalMergedStopEndAt: combinedEndAt,
      terminalMergedStopEndMinutes: combinedEndMinutes,
      visualSegmentEndAt: combinedEndAt,
      visualSegmentEndMinutes: combinedEndMinutes,
      visualTimelineDurationMinutes: combinedVisualTimelineDurationMinutes,
      widthPercent: combinedWidthPercent,
    },
    {
      ...terminalItem,
      leftPercent: terminalMarkerLeftPercent,
      servicePercent: 0,
      timelineDurationLabel: null,
      timelineDurationMinutes: 0,
      visualTimelineDurationMinutes: 0,
      widthPercent: 0,
    },
  ];
}

export function resolveDisplayedTimelineSpanMinutes(segments = [], rowStartMinutes = null) {
  const resolvedSegments = (Array.isArray(segments) ? segments : [])
    .filter((segment) => segment && !isInvisibleDisplayedTimelineMarker(segment) && !isEndOfDayLoadTimelineSegment(segment));
  const explicitStartMinutes = rowStartMinutes == null || rowStartMinutes === ""
    ? Number.NaN
    : Number(rowStartMinutes);
  const startCandidates = resolvedSegments
    .map((segment) => {
      const displayedStartMinutes = Number(segment?.displayedSegmentStartMinutes);
      if (Number.isFinite(displayedStartMinutes)) {
        return displayedStartMinutes;
      }

      const visualStartMinutes = Number(segment?.visualSegmentStartMinutes);
      if (Number.isFinite(visualStartMinutes)) {
        return visualStartMinutes;
      }

      const segmentStartMinutes = Number(segment?.segmentStartMinutes);
      return Number.isFinite(segmentStartMinutes) ? segmentStartMinutes : null;
    })
    .filter((value) => value != null);
  const startMinutes = Number.isFinite(explicitStartMinutes)
    ? explicitStartMinutes
    : (startCandidates.length ? Math.min(...startCandidates) : null);
  const endCandidates = resolvedSegments
    .map((segment) => {
      const displayedEndMinutes = Number(segment?.displayedSegmentEndMinutes);
      if (Number.isFinite(displayedEndMinutes)) {
        return displayedEndMinutes;
      }

      const visualEndMinutes = Number(segment?.visualSegmentEndMinutes);
      if (Number.isFinite(visualEndMinutes)) {
        return visualEndMinutes;
      }

      const segmentEndMinutes = Number(segment?.segmentEndMinutes);
      return Number.isFinite(segmentEndMinutes) ? segmentEndMinutes : null;
    })
    .filter((value) => value != null);

  if (startMinutes == null || !endCandidates.length) {
    return null;
  }

  return Math.max(Math.max(...endCandidates) - startMinutes, 0);
}

export function resolveDisplayedTimelineSpanLabel(segments = [], rowStartMinutes = null) {
  const spanMinutes = resolveDisplayedTimelineSpanMinutes(segments, rowStartMinutes);
  return formatDurationShort(spanMinutes);
}

function resolveDisplayedTimelineSegmentStartMinutes(segment = null) {
  const displayedStartMinutes = Number(segment?.displayedSegmentStartMinutes);
  if (Number.isFinite(displayedStartMinutes)) {
    return displayedStartMinutes;
  }

  const visualStartMinutes = Number(segment?.visualSegmentStartMinutes);
  if (Number.isFinite(visualStartMinutes)) {
    return visualStartMinutes;
  }

  const segmentStartMinutes = Number(segment?.segmentStartMinutes);
  return Number.isFinite(segmentStartMinutes) ? segmentStartMinutes : null;
}

function resolveDisplayedTimelineSegmentEndMinutes(segment = null) {
  const displayedEndMinutes = Number(segment?.displayedSegmentEndMinutes);
  if (Number.isFinite(displayedEndMinutes)) {
    return displayedEndMinutes;
  }

  const visualEndMinutes = Number(segment?.visualSegmentEndMinutes);
  if (Number.isFinite(visualEndMinutes)) {
    return visualEndMinutes;
  }

  const segmentEndMinutes = Number(segment?.segmentEndMinutes);
  return Number.isFinite(segmentEndMinutes) ? segmentEndMinutes : null;
}

function resolveDisplayedTimelineGapSegmentSummary(segment = null, fallbackIndex = 0) {
  return {
    operationLabel: resolveFrontendDrivingSegmentOperationLabel(segment),
    stepLabel: resolveFrontendDrivingSegmentStepLabel(segment, fallbackIndex),
  };
}

function resolveDisplayedTimelineGapTravelAbsorption(segment, segmentIndex, gapMinutes, displayOverrides) {
  const normalizedGapMinutes = Number(gapMinutes);
  if (!segment || !Number.isFinite(normalizedGapMinutes) || normalizedGapMinutes <= 0) {
    return null;
  }

  const { travelMinutes, distanceKm } = resolveVisibleRouteSegmentMetrics(
    segment,
    displayOverrides,
    segmentIndex,
  );
  const absorbedAlready = Number(segment?.fleetTimelineAbsorbedGapMinutes) || 0;
  const routeTravelMinutesBefore = absorbedAlready > 0
    ? Math.max(travelMinutes - absorbedAlready, 0)
    : travelMinutes;
  const fallbackTravelMinutesBefore = absorbedAlready > 0
    ? Math.max(resolveSequenceDisplayTravelMinutes(segment) - absorbedAlready, 0)
    : resolveSequenceDisplayTravelMinutes(segment);
  const travelMinutesBefore = routeTravelMinutesBefore > 0
    ? routeTravelMinutesBefore
    : fallbackTravelMinutesBefore;

  if (travelMinutesBefore <= 0) {
    return null;
  }

  return {
    distanceKm: distanceKm != null && distanceKm > 0 ? distanceKm : null,
    travelMinutesAfter: travelMinutesBefore + normalizedGapMinutes,
    travelMinutesBefore,
  };
}

export function resolveDisplayedTimelineGapDetails(segments = [], rowStartMinutes = null) {
  const workedSegments = (Array.isArray(segments) ? segments : [])
    .filter((segment) => segment && !isEndOfDayLoadTimelineSegment(segment));
  const displayOverrides = buildSameSiteSequenceDisplayOverrides(buildFleetSequenceDisplayItems(workedSegments));
  const visibleSegments = workedSegments
    .filter((segment) => !isInvisibleDisplayedTimelineMarker(segment))
    .map((segment, segmentIndex) => {
      const startMinutes = resolveDisplayedTimelineSegmentStartMinutes(segment);
      const endMinutes = resolveDisplayedTimelineSegmentEndMinutes(segment);

      return startMinutes != null && endMinutes != null
        ? {
            endMinutes: Math.max(endMinutes, startMinutes),
            segment,
            segmentIndex,
            startMinutes,
          }
        : null;
    })
    .filter(Boolean)
    .sort((left, right) => left.startMinutes - right.startMinutes || left.endMinutes - right.endMinutes);

  if (!visibleSegments.length) {
    return {
      gapMinutes: 0,
      gaps: [],
    };
  }

  const explicitStartMinutes = rowStartMinutes == null || rowStartMinutes === ""
    ? Number.NaN
    : Number(rowStartMinutes);
  let cursorMinutes = Number.isFinite(explicitStartMinutes)
    ? explicitStartMinutes
    : visibleSegments[0].startMinutes;
  const gaps = [];
  let previousEntry = null;

  visibleSegments.forEach((entry) => {
    // Un arrêt ancré (P1, P2) est dessiné sans sa pause dîner : l'intervalle qui le suit est la pause, pas de l'attente.
    const lunchBreakMinutes = previousEntry && isLunchBreakSegment(previousEntry.segment)
      ? resolveSequenceManualBufferMinutes(previousEntry.segment)
      : 0;
    if (previousEntry && entry.startMinutes > cursorMinutes + lunchBreakMinutes) {
      const gapMinutes = entry.startMinutes - cursorMinutes - lunchBreakMinutes;
      gaps.push({
        afterSegment: resolveDisplayedTimelineGapSegmentSummary(previousEntry.segment, previousEntry.segmentIndex),
        beforeSegment: resolveDisplayedTimelineGapSegmentSummary(entry.segment, entry.segmentIndex),
        gapMinutes,
        travelAbsorption: resolveDisplayedTimelineGapTravelAbsorption(
          entry.segment,
          entry.segmentIndex,
          gapMinutes,
          displayOverrides,
        ),
      });
    }

    previousEntry = entry;
    cursorMinutes = Math.max(cursorMinutes, entry.endMinutes);
  });

  return {
    gapMinutes: gaps.reduce((totalMinutes, gap) => totalMinutes + gap.gapMinutes, 0),
    gaps,
  };
}

function resolveDisplayedTimelineGapMinutes(segments = [], rowStartMinutes = null) {
  return resolveDisplayedTimelineGapDetails(segments, rowStartMinutes).gapMinutes;
}

export function resolveTimelineGroupSyncTarget({
  expandedSequenceGroupKey = "",
  selectedRow = null,
  selectedSegment = null,
} = {}) {
  const rowSegments = Array.isArray(selectedRow?.segments) ? selectedRow.segments : [];
  const isNonExpandableInitialPreloadGroup = (groupKey) => {
    const resolvedGroupKey = typeof groupKey === "string" ? groupKey.trim() : "";
    if (!resolvedGroupKey) {
      return false;
    }

    return rowSegments.some((segment) => segment?.sameSiteGroupKey === resolvedGroupKey && isLeadingPreloadTimelineSegment(segment));
  };
  const candidateExpandedGroupKey = typeof expandedSequenceGroupKey === "string"
    ? expandedSequenceGroupKey.trim()
    : "";

  if (
    candidateExpandedGroupKey
    && !isNonExpandableInitialPreloadGroup(candidateExpandedGroupKey)
    && rowSegments.some((segment) => segment?.sameSiteGroupKey === candidateExpandedGroupKey)
  ) {
    return {
      assignmentId: selectedRow?.assignmentId || "",
      groupKey: candidateExpandedGroupKey,
      source: "sequence-sync",
    };
  }

  const candidateSelectedGroupKey = typeof selectedSegment?.sameSiteGroupKey === "string"
    ? selectedSegment.sameSiteGroupKey.trim()
    : "";

  if (
    candidateSelectedGroupKey
    && !isNonExpandableInitialPreloadGroup(candidateSelectedGroupKey)
    && rowSegments.some((segment) => segment?.sameSiteGroupKey === candidateSelectedGroupKey)
  ) {
    return {
      assignmentId: selectedRow?.assignmentId || "",
      groupKey: candidateSelectedGroupKey,
      source: "selection-sync",
    };
  }

  return null;
}

export function shouldDisplaySameSiteDurationChips(segment) {
  if (!segment?.isSameSiteSequenceGroup) {
    return true;
  }

  return !Number.isInteger(segment?.sameSiteGroupIndex) || segment.sameSiteGroupIndex <= 0;
}

export function buildSameSiteSequenceDisplayOverrides(segments = []) {
  const resolvedSegments = Array.isArray(segments) ? segments.filter(Boolean) : [];
  const overrides = new Map();

  resolvedSegments.forEach((segment, index) => {
    const distanceKm = parseSequenceDistanceKm(segment?.distanceLabel);
    overrides.set(getSameSiteSequenceOverrideKey(segment, index), {
      adjusted: false,
      distanceKm,
      distanceLabel: distanceKm != null
        ? formatSequenceDistanceLabel(distanceKm)
        : (typeof segment?.distanceLabel === "string" ? segment.distanceLabel : ""),
      displayTravelDurationMinutes: resolveSequenceDisplayTravelMinutes(segment),
      legacyDisplayTravelDurationMinutes: resolveSequenceLegacyDisplayTravelMinutes(segment),
      routeScheduledTravelDurationMinutes: resolveSequenceScheduledTravelMinutes(segment),
      travelDurationMinutes: resolveSequenceTravelMinutes(segment),
    });
  });

  let cursor = 0;

  while (cursor < resolvedSegments.length) {
    const firstSegment = resolvedSegments[cursor] || null;
    const sameSiteGroupKey = typeof firstSegment?.sameSiteGroupKey === "string"
      ? firstSegment.sameSiteGroupKey.trim()
      : "";
    const sameSiteGroupSize = Number.isInteger(firstSegment?.sameSiteGroupSize) ? firstSegment.sameSiteGroupSize : 1;

    if (!firstSegment?.isSameSiteSequenceGroup || !sameSiteGroupKey || sameSiteGroupSize <= 1 || firstSegment.sameSiteGroupIndex !== 0) {
      cursor += 1;
      continue;
    }

    const { groupEnd, memberSegments } = collectSameSiteGroupMembers(resolvedSegments, cursor, sameSiteGroupKey);

    const terminalMergedMemberIndex = memberSegments.findIndex((segment) => segment?.isTerminalRouteMerged === true);
    if (terminalMergedMemberIndex > 0) {
      const terminalSegment = memberSegments[terminalMergedMemberIndex] || null;
      const terminalSegmentKey = getSameSiteSequenceOverrideKey(terminalSegment, cursor + terminalMergedMemberIndex);
      const terminalOverride = overrides.get(terminalSegmentKey);
      const firstSegmentKey = getSameSiteSequenceOverrideKey(firstSegment, cursor);
      const firstOverride = overrides.get(firstSegmentKey);

      if (firstOverride && terminalOverride) {
        firstOverride.adjusted = true;
        firstOverride.distanceKm = Math.max(terminalOverride.distanceKm ?? 0, 0);
        firstOverride.distanceLabel = formatSequenceDistanceLabel(firstOverride.distanceKm);
        firstOverride.displayTravelDurationMinutes = Math.max(terminalOverride.displayTravelDurationMinutes ?? 0, 0);
        firstOverride.legacyDisplayTravelDurationMinutes = Math.max(terminalOverride.legacyDisplayTravelDurationMinutes ?? 0, 0);
        firstOverride.routeScheduledTravelDurationMinutes = Math.max(terminalOverride.routeScheduledTravelDurationMinutes ?? 0, 0);
        firstOverride.travelDurationMinutes = Math.max(terminalOverride.travelDurationMinutes ?? 0, 0);
        overrides.set(firstSegmentKey, firstOverride);
      }

      memberSegments.slice(1).forEach((segment, memberIndex) => {
        const segmentKey = getSameSiteSequenceOverrideKey(segment, cursor + memberIndex + 1);
        const override = overrides.get(segmentKey);
        if (!override) {
          return;
        }

        override.adjusted = true;
        override.distanceKm = 0;
        override.distanceLabel = formatSequenceDistanceLabel(0);
        override.displayTravelDurationMinutes = 0;
        override.legacyDisplayTravelDurationMinutes = 0;
        override.routeScheduledTravelDurationMinutes = 0;
        override.travelDurationMinutes = 0;
        overrides.set(segmentKey, override);
      });

      cursor = groupEnd;
      continue;
    }

    let carriedDistanceKm = 0;
    let carriedDisplayTravelDurationMinutes = 0;
    let carriedLegacyDisplayTravelDurationMinutes = 0;
    let carriedRouteScheduledTravelDurationMinutes = 0;
    let carriedTravelDurationMinutes = 0;

    memberSegments.slice(1).forEach((segment, memberIndex) => {
      const segmentKey = getSameSiteSequenceOverrideKey(segment, cursor + memberIndex + 1);
      const override = overrides.get(segmentKey);
      if (!override) {
        return;
      }

      if (
        (override.distanceKm ?? 0) <= 0
        && override.displayTravelDurationMinutes <= 0
        && (override.legacyDisplayTravelDurationMinutes ?? 0) <= 0
        && (override.routeScheduledTravelDurationMinutes ?? 0) <= 0
        && override.travelDurationMinutes <= 0
      ) {
        return;
      }

      carriedDistanceKm += Math.max(override.distanceKm ?? 0, 0);
      carriedDisplayTravelDurationMinutes += Math.max(override.displayTravelDurationMinutes ?? 0, 0);
      carriedLegacyDisplayTravelDurationMinutes += Math.max(override.legacyDisplayTravelDurationMinutes ?? 0, 0);
      carriedRouteScheduledTravelDurationMinutes += Math.max(override.routeScheduledTravelDurationMinutes ?? 0, 0);
      carriedTravelDurationMinutes += Math.max(override.travelDurationMinutes, 0);
      override.adjusted = true;
      override.distanceKm = 0;
      override.distanceLabel = formatSequenceDistanceLabel(0);
      override.displayTravelDurationMinutes = 0;
      override.legacyDisplayTravelDurationMinutes = 0;
      override.routeScheduledTravelDurationMinutes = 0;
      override.travelDurationMinutes = 0;
      overrides.set(segmentKey, override);
    });

    if (
      carriedDistanceKm > 0
      || carriedDisplayTravelDurationMinutes > 0
      || carriedLegacyDisplayTravelDurationMinutes > 0
      || carriedRouteScheduledTravelDurationMinutes > 0
      || carriedTravelDurationMinutes > 0
    ) {
      const firstSegmentKey = getSameSiteSequenceOverrideKey(firstSegment, cursor);
      const firstOverride = overrides.get(firstSegmentKey);
      if (firstOverride) {
        firstOverride.adjusted = true;
        if (carriedDistanceKm > 0) {
          firstOverride.distanceKm = Math.max((firstOverride.distanceKm ?? 0) + carriedDistanceKm, 0);
          firstOverride.distanceLabel = formatSequenceDistanceLabel(firstOverride.distanceKm);
        }
        firstOverride.displayTravelDurationMinutes = Math.max(
          (firstOverride.displayTravelDurationMinutes ?? 0) + carriedDisplayTravelDurationMinutes,
          0,
        );
        firstOverride.legacyDisplayTravelDurationMinutes = Math.max(
          (firstOverride.legacyDisplayTravelDurationMinutes ?? 0) + carriedLegacyDisplayTravelDurationMinutes,
          0,
        );
        firstOverride.routeScheduledTravelDurationMinutes = Math.max(
          (firstOverride.routeScheduledTravelDurationMinutes ?? 0) + carriedRouteScheduledTravelDurationMinutes,
          0,
        );
        firstOverride.travelDurationMinutes = Math.max(firstOverride.travelDurationMinutes + carriedTravelDurationMinutes, 0);
        overrides.set(firstSegmentKey, firstOverride);
      }
    }

    cursor = groupEnd;
  }

  return overrides;
}

export function formatTimelineClockLabel(valueMinutes) {
  const normalizedMinutes = Number(valueMinutes);

  if (!Number.isFinite(normalizedMinutes)) {
    return "";
  }

  const roundedMinutes = Math.round(normalizedMinutes);
  const wrappedMinutes = ((roundedMinutes % 1440) + 1440) % 1440;
  const dayOffset = Math.floor(roundedMinutes / 1440);
  const hours = Math.floor(wrappedMinutes / 60);
  const minutes = wrappedMinutes % 60;
  const dayOffsetLabel = dayOffset === 0
    ? ""
    : ` (${dayOffset > 0 ? "+" : ""}${dayOffset} j)`;

  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}${dayOffsetLabel}`;
}

const TIMELINE_LEADING_TICK_MIN_GAP_PERCENT = 4.5;
const TIMELINE_TRAILING_TICK_MIN_GAP_PERCENT = 5;
const TIMELINE_TRAILING_TICK_SEPARATION_GAP_PERCENT = 8;

function isInformationalTimelineSegment(segment) {
  return Boolean(isLeadingPreloadTimelineSegment(segment) || segment?.isEndOfDayLoad);
}

export function resolveTimelineHourTicks(startMinutes = null, endMinutes = null, { leadingPaddingPercent = 0, trailingPaddingPercent = 0 } = {}) {
  const resolvedStartMinutes = Number(startMinutes);
  const resolvedEndMinutes = Number(endMinutes);
  const normalizedLeadingPaddingPercent = Number.isFinite(Number(leadingPaddingPercent))
    ? Math.min(Math.max(Number(leadingPaddingPercent), 0), 40)
    : 0;
  const normalizedTrailingPaddingPercent = Number.isFinite(Number(trailingPaddingPercent))
    ? Math.min(Math.max(Number(trailingPaddingPercent), 0), 40)
    : 0;
  const usablePercent = Math.max(100 - normalizedLeadingPaddingPercent - normalizedTrailingPaddingPercent, 0);

  if (!Number.isFinite(resolvedStartMinutes) || !Number.isFinite(resolvedEndMinutes) || resolvedEndMinutes < resolvedStartMinutes) {
    return [];
  }

  const durationMinutes = resolvedEndMinutes - resolvedStartMinutes;
  if (durationMinutes === 0) {
    return [{
      label: formatTimelineClockLabel(resolvedStartMinutes),
      labelAlignment: "center",
      minutes: resolvedStartMinutes,
      percent: normalizedLeadingPaddingPercent,
    }];
  }

  const tickMinutes = [resolvedStartMinutes];
  const firstTickMinutes = resolvedStartMinutes % 60 === 0
    ? resolvedStartMinutes
    : Math.ceil(resolvedStartMinutes / 60) * 60;

  for (let currentTickMinutes = firstTickMinutes; currentTickMinutes <= resolvedEndMinutes; currentTickMinutes += 60) {
    tickMinutes.push(currentTickMinutes);
  }

  tickMinutes.push(resolvedEndMinutes);

  const uniqueTickMinutes = Array.from(new Set(tickMinutes));

  const resolvedTicks = uniqueTickMinutes.map((minutes) => ({
    label: formatTimelineClockLabel(minutes),
    labelAlignment: "center",
    labelExtendRight: false,
    minutes,
    percent: normalizedLeadingPaddingPercent + ((((minutes - resolvedStartMinutes) / durationMinutes) * 100) / 100) * usablePercent,
  }));

  if (resolvedTicks.length >= 2) {
    const leadingTick = resolvedTicks[0];
    const nextTick = resolvedTicks[1];
    const leadingGapPercent = nextTick.percent - leadingTick.percent;
    const isSubHourTimelineStart = leadingTick.minutes % 60 !== 0;

    if (leadingGapPercent < TIMELINE_LEADING_TICK_MIN_GAP_PERCENT) {
      if (isSubHourTimelineStart) {
        leadingTick.labelAlignment = "leading-edge";
        nextTick.labelAlignment = "trailing-edge";
      } else if (leadingTick.percent <= 0) {
        return resolvedTicks.slice(1);
      }
    }
  }

  if (resolvedTicks.length >= 2) {
    const trailingTick = resolvedTicks[resolvedTicks.length - 1];
    const previousTick = resolvedTicks[resolvedTicks.length - 2];
    const trailingGapPercent = trailingTick.percent - previousTick.percent;
    const isSubHourTimelineEnd = trailingTick.minutes % 60 !== 0;

    if (trailingGapPercent < TIMELINE_TRAILING_TICK_SEPARATION_GAP_PERCENT) {
      if (isSubHourTimelineEnd) {
        if (previousTick.labelAlignment === "center") {
          previousTick.labelAlignment = "leading-edge";
        }
        trailingTick.labelAlignment = "trailing-edge";
        trailingTick.labelExtendRight = true;
      } else if (trailingGapPercent < TIMELINE_TRAILING_TICK_MIN_GAP_PERCENT && trailingTick.percent >= 100) {
        return resolvedTicks.slice(0, -1);
      } else if (previousTick.labelAlignment === "center") {
        previousTick.labelAlignment = "leading-edge";
        trailingTick.labelAlignment = "trailing-edge";
      }
    }
  }

  return resolvedTicks;
}

export function getTimelineTickLabelStyle(tick) {
  const percent = typeof tick === "object" && tick != null ? Number(tick.percent) : Number(tick);
  const labelAlignment = typeof tick === "object" && tick != null && typeof tick.labelAlignment === "string"
    ? tick.labelAlignment
    : "center";
  const labelExtendRight = typeof tick === "object" && tick != null && tick.labelExtendRight === true;

  if (labelAlignment === "leading-edge") {
    if (percent <= 0) {
      return { left: 0, transform: "translateX(-100%)" };
    }

    return { left: `${percent}%`, transform: "translateX(-100%)" };
  }

  if (labelAlignment === "trailing-edge") {
    if (percent >= 100) {
      // Fin de journée collée au bord : libellé en diagonale pour rester visible en entier.
      return labelExtendRight
        ? { left: "100%", top: "auto", bottom: 0, transform: "rotate(-60deg)", transformOrigin: "0 100%" }
        : { left: "100%", transform: "translateX(-100%)" };
    }

    return { left: `${percent}%`, transform: "translateX(0)" };
  }

  if (percent <= 0) {
    return { left: 0, transform: "none" };
  }

  if (percent >= 100) {
    return { left: "100%", transform: "translateX(-100%)" };
  }

  return { left: `${percent}%`, transform: "translateX(-50%)" };
}

export function resolveTimelineBounds(rows = []) {
  const resolvedRows = Array.isArray(rows) ? rows : [];
  const rowStartCandidates = resolvedRows
    .map((row) => {
      const hasDisplayedStart = Object.prototype.hasOwnProperty.call(row || {}, "displayedTimelineStartMinutes");
      if (hasDisplayedStart) {
        const rawDisplayedStartMinutes = row?.displayedTimelineStartMinutes;
        const displayedStartMinutes = Number(rawDisplayedStartMinutes);
        if (rawDisplayedStartMinutes == null || rawDisplayedStartMinutes === "") {
          return null;
        }

        return Number.isFinite(displayedStartMinutes) ? displayedStartMinutes : null;
      }

      return Number(row?.timelineStartMinutes);
    })
    .filter((value) => Number.isFinite(value));
  const rowEndCandidates = resolvedRows
    .map((row) => {
      const hasDisplayedEnd = Object.prototype.hasOwnProperty.call(row || {}, "displayedTimelineEndMinutes");
      if (hasDisplayedEnd) {
        const rawDisplayedEndMinutes = row?.displayedTimelineEndMinutes;
        const displayedEndMinutes = Number(rawDisplayedEndMinutes);
        if (rawDisplayedEndMinutes == null || rawDisplayedEndMinutes === "") {
          return null;
        }

        return Number.isFinite(displayedEndMinutes) ? displayedEndMinutes : null;
      }

      const explicitEndMinutes = Number(row?.timelineEndMinutes);
      const rowStartMinutes = Number(row?.timelineStartMinutes);
      const rowDurationMinutes = Number(row?.totalDurationMinutes);
      const canonicalEndMinutes = Number.isFinite(rowStartMinutes) && Number.isFinite(rowDurationMinutes) && rowDurationMinutes >= 0
        ? rowStartMinutes + rowDurationMinutes
        : null;

      if (Number.isFinite(explicitEndMinutes)) {
        return Number.isFinite(canonicalEndMinutes)
          ? Math.min(explicitEndMinutes, canonicalEndMinutes)
          : explicitEndMinutes;
      }

      return Number.isFinite(canonicalEndMinutes) ? canonicalEndMinutes : null;
    })
    .filter((value) => Number.isFinite(value));
  const resolveIncidentBoundaryMinutes = (incident, boundary) => {
    const fieldNames = boundary === "end"
      ? ["display_end_minutes", "end_minutes"]
      : ["display_start_minutes", "start_minutes"];

    for (const fieldName of fieldNames) {
      const rawValue = incident?.[fieldName];
      if (rawValue == null || rawValue === "") {
        continue;
      }
      const value = Number(rawValue);
      if (Number.isFinite(value) && value >= 0) {
        return value;
      }
    }

    const clockValue = boundary === "end" ? incident?.end_time : incident?.start_time;
    if (typeof clockValue !== "string") {
      return null;
    }

    const match = clockValue.trim().match(/^(\d{1,2}):(\d{2})$/);
    if (!match) {
      return null;
    }

    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    return Number.isInteger(hours) && hours >= 0 && hours < 24
      && Number.isInteger(minutes) && minutes >= 0 && minutes < 60
      ? (hours * 60) + minutes
      : null;
  };
  const incidentStartCandidates = resolvedRows
    .flatMap((row) => (Array.isArray(row?.incidents) ? row.incidents : []))
    .map((incident) => resolveIncidentBoundaryMinutes(incident, "start"))
    .filter((value) => Number.isFinite(value));
  const incidentEndCandidates = resolvedRows
    .flatMap((row) => (Array.isArray(row?.incidents) ? row.incidents : []))
    .map((incident) => resolveIncidentBoundaryMinutes(incident, "end"))
    .filter((value) => Number.isFinite(value));
  const startCandidates = [...rowStartCandidates, ...incidentStartCandidates];
  const endCandidates = [...rowEndCandidates, ...incidentEndCandidates];

  if (!startCandidates.length && !endCandidates.length) {
    return {
      durationMinutes: 0,
      endLabel: "00:00",
      endMinutes: 0,
      startLabel: "00:00",
      startMinutes: 0,
    };
  }

  const startMinutes = startCandidates.length
    ? Math.min(...startCandidates)
    : Math.min(...endCandidates);
  const endMinutes = endCandidates.length
    ? Math.max(...endCandidates)
    : startMinutes;

  return {
    durationMinutes: Math.max(endMinutes - startMinutes, 0),
    endLabel: formatTimelineClockLabel(endMinutes),
    endMinutes,
    startLabel: formatTimelineClockLabel(startMinutes),
    startMinutes,
  };
}

export function resolveAssignmentDurationLabel(assignment, fallbackMinutes = null) {
  const explicitLabel = typeof assignment?.total_estimated_duration_label === "string"
    ? assignment.total_estimated_duration_label.trim()
    : "";

  return explicitLabel || formatDurationShort(assignment?.total_estimated_duration_minutes ?? fallbackMinutes) || "Durée estimée indisponible";
}

export function resolveAssignmentBackendTotalDurationMinutes(assignment = null) {
  const breakdown = resolveAssignmentBackendDurationBreakdown(assignment);
  return breakdown?.backendTotalMinutes ?? normalizePositiveDurationMinutes(assignment?.total_estimated_duration_minutes);
}

function normalizePositiveDurationMinutes(value) {
  const resolvedValue = Number(value);
  return Number.isFinite(resolvedValue) && resolvedValue >= 0 ? resolvedValue : null;
}

function normalizePositiveMetricValue(value) {
  const resolvedValue = Number(value);
  return Number.isFinite(resolvedValue) && resolvedValue > 0 ? resolvedValue : null;
}

function collectAssignmentStops(assignment = null) {
  return (Array.isArray(assignment?.trips) ? assignment.trips : [])
    .flatMap((trip) => {
      if (Array.isArray(trip?.stops) && trip.stops.length) {
        return trip.stops;
      }

      return Array.isArray(trip?.routable_stops) ? trip.routable_stops : [];
    })
    .filter(Boolean);
}

function sumAssignmentManualBufferMinutes(assignment = null) {
  return (Array.isArray(assignment?.trips) ? assignment.trips : []).reduce((totalMinutes, trip) => {
    const segmentDistances = Array.isArray(trip?.route?.segment_distances) ? trip.route.segment_distances : [];
    return totalMinutes + segmentDistances.reduce((segmentTotalMinutes, segment) => {
      // La pause dîner n'est pas du temps travaillé.
      const bufferMinutes = isLunchBreakSegment(segment) ? null : normalizePositiveDurationMinutes(segment?.manual_buffer_minutes);
      return segmentTotalMinutes + (bufferMinutes ?? 0);
    }, 0);
  }, 0);
}

function sumTripRouteMetricMinutes(assignment = null, key = "") {
  return (Array.isArray(assignment?.trips) ? assignment.trips : []).reduce((totalMinutes, trip) => {
    const metricMinutes = normalizePositiveDurationMinutes(trip?.route?.[key]);
    return metricMinutes == null ? totalMinutes : totalMinutes + metricMinutes;
  }, 0);
}

function resolveAssignmentDistanceKm(assignment = null) {
  const actualDistanceMeters = normalizePositiveMetricValue(assignment?.total_actual_distance_meters);
  if (actualDistanceMeters != null) {
    return {
      distanceKm: actualDistanceMeters / 1000,
      sourceLabel: "trajet réel",
    };
  }

  const tripActualDistanceMeters = (Array.isArray(assignment?.trips) ? assignment.trips : []).reduce((totalMeters, trip) => {
    const distanceMeters = normalizePositiveMetricValue(trip?.route?.actual_distance_meters);
    return distanceMeters == null ? totalMeters : totalMeters + distanceMeters;
  }, 0);

  if (tripActualDistanceMeters > 0) {
    return {
      distanceKm: tripActualDistanceMeters / 1000,
      sourceLabel: "trajet réel",
    };
  }

  const estimatedDistanceKm = normalizePositiveMetricValue(assignment?.total_estimated_distance_km);
  if (estimatedDistanceKm != null) {
    return {
      distanceKm: estimatedDistanceKm,
      sourceLabel: "distance estimée",
    };
  }

  return {
    distanceKm: null,
    sourceLabel: "distance indisponible",
  };
}

function resolveAssignmentTruckSpeedKmh(assignment = null) {
  return normalizePositiveMetricValue(
    assignment?.avg_speed_kmh
      ?? assignment?.truck_raw_data?.avg_speed_kmh
      ?? assignment?.truck_raw_data?.Vitesse_moy,
  );
}

export function resolveAssignmentBackendDurationBreakdown(assignment = null, {
  displayedDurationMinutes = null,
} = {}) {
  if (!assignment || typeof assignment !== "object") {
    return null;
  }

  const backendTotalMinutes = normalizePositiveDurationMinutes(assignment.total_estimated_duration_minutes);
  if (backendTotalMinutes == null) {
    return null;
  }

  const stops = collectAssignmentStops(assignment);
  const serviceMinutesByType = stops.reduce((totals, stop) => {
    const stopType = typeof stop?.stop_type === "string" ? stop.stop_type.trim() : "";
    const serviceMinutes = normalizePositiveDurationMinutes(stop?.service_time_minutes) ?? 0;
    const aspiratorMinutes = stopType === "delivery"
      ? Math.max(0, Math.round(Number(stop?.aspirator_service_minutes) || 0))
      : 0;
    const baseServiceMinutes = aspiratorMinutes > 0 && serviceMinutes >= aspiratorMinutes
      ? serviceMinutes - aspiratorMinutes
      : serviceMinutes;

    if (stopType === "depot_start") {
      totals.inspectionMinutes += serviceMinutes;
    } else if (stopType === "loading") {
      totals.loadingMinutes += baseServiceMinutes;
    } else if (stopType === "delivery") {
      totals.unloadingMinutes += baseServiceMinutes;
    }

    addTimelineFixedServiceMinutes(totals, stopType, stop, stop?.is_preloaded_previous_day);
    totals.aspiratorMinutes += aspiratorMinutes;
    return totals;
  }, {
    aspiratorMinutes: 0,
    inspectionMinutes: 0,
    loadingMinutes: 0,
    unloadingMinutes: 0,
  });

  const waitMinutes = stops.reduce((totalMinutes, stop) => {
    const stopWaitMinutes = normalizePositiveDurationMinutes(stop?.wait_minutes) ?? 0;
    return totalMinutes + stopWaitMinutes;
  }, 0);

  const routeDrivingMinutes = sumTripRouteMetricMinutes(assignment, "driving_duration_minutes");
  const manualBufferMinutes = sumAssignmentManualBufferMinutes(assignment);
  const serviceTotalMinutes = serviceMinutesByType.inspectionMinutes
    + serviceMinutesByType.loadingMinutes
    + serviceMinutesByType.unloadingMinutes
    + serviceMinutesByType.aspiratorMinutes
    + waitMinutes;
  const inferredDrivingMinutes = Math.max(backendTotalMinutes - serviceTotalMinutes - manualBufferMinutes, 0);
  const drivingMinutes = routeDrivingMinutes > 0 ? routeDrivingMinutes : inferredDrivingMinutes;
  const segmentTotalMinutes = drivingMinutes + serviceTotalMinutes + manualBufferMinutes;
  const resolvedBackendTotalMinutes = manualBufferMinutes > 0
    ? Math.max(backendTotalMinutes, segmentTotalMinutes)
    : backendTotalMinutes;
  const { distanceKm, sourceLabel: distanceSourceLabel } = resolveAssignmentDistanceKm(assignment);
  const truckSpeedKmh = resolveAssignmentTruckSpeedKmh(assignment);
  const speedKmh = truckSpeedKmh;
  const speedSource = truckSpeedKmh != null ? "truck" : "unknown";
  const resolvedDisplayedDurationMinutes = normalizePositiveDurationMinutes(displayedDurationMinutes);

  return {
    backendTotalMinutes: resolvedBackendTotalMinutes,
    distanceKm,
    distanceSourceLabel,
    displayedDurationMinutes: resolvedDisplayedDurationMinutes,
    drivingMinutes,
    aspiratorMinutes: serviceMinutesByType.aspiratorMinutes,
    inspectionMinutes: serviceMinutesByType.inspectionMinutes,
    loadingMinutes: serviceMinutesByType.loadingMinutes,
    loadingFixedMinutes: serviceMinutesByType.loadingFixedMinutes || 0,
    unloadingFixedMinutes: serviceMinutesByType.unloadingFixedMinutes || 0,
    manualBufferMinutes,
    segmentTotalMinutes,
    speedKmh,
    speedSource,
    truckSpeedKmh,
    unloadingMinutes: serviceMinutesByType.unloadingMinutes,
    visibleDeltaMinutes: resolvedDisplayedDurationMinutes == null
      ? null
      : resolvedBackendTotalMinutes - resolvedDisplayedDurationMinutes,
    waitMinutes,
  };
}

export function resolveDisplayedTimelineFrontendDurationBreakdown(segments = [], {
  rowStartMinutes = null,
} = {}) {
  const resolvedSegments = (Array.isArray(segments) ? segments.filter(Boolean) : [])
    .filter((segment) => !isEndOfDayLoadTimelineSegment(segment));
  if (!resolvedSegments.length) {
    return null;
  }

  const displayedDurationMinutes = normalizePositiveDurationMinutes(
    resolveDisplayedTimelineSpanMinutes(resolvedSegments, rowStartMinutes),
  );

  const serviceMinutesByType = resolvedSegments.reduce((totals, segment) => {
    getTimelineBreakdownServiceSegments(segment).forEach((serviceSegment) => {
      addTimelineServiceBreakdownSegment(totals, serviceSegment);
    });
    return totals;
  }, {
    aspiratorMinutes: 0,
    inspectionMinutes: 0,
    loadingMinutes: 0,
    unloadingMinutes: 0,
  });

  const {
    distanceKm,
    drivingMinutes,
    drivingSegments,
  } = buildUniqueVisibleRouteSegments(resolvedSegments);
  // La pause dîner décale les heures mais n'est pas du temps travaillé.
  const lunchBreakMinutes = resolvedSegments.reduce(
    (totalMinutes, segment) => totalMinutes + (isLunchBreakSegment(segment) ? resolveSequenceManualBufferMinutes(segment) : 0),
    0,
  );
  const manualBufferMinutes = resolvedSegments.reduce(
    (totalMinutes, segment) => totalMinutes + resolveSequenceManualBufferMinutes(segment),
    0,
  ) - lunchBreakMinutes;
  const stopWaitMinutes = resolvedSegments.reduce(
    (totalMinutes, segment) => totalMinutes + (normalizePositiveDurationMinutes(segment?.waitMinutes) ?? 0),
    0,
  );
  const ganttGapDetails = displayedDurationMinutes != null
    ? resolveDisplayedTimelineGapDetails(resolvedSegments, rowStartMinutes)
    : { gapMinutes: 0, gaps: [] };
  const ganttGapMinutes = ganttGapDetails.gapMinutes;
  const productiveMinutesBeforeInferredWait = drivingMinutes
    + serviceMinutesByType.inspectionMinutes
    + serviceMinutesByType.loadingMinutes
    + serviceMinutesByType.unloadingMinutes
    + serviceMinutesByType.aspiratorMinutes
    + manualBufferMinutes;
  const visibleWaitMinutes = displayedDurationMinutes != null
    ? Math.min(
      ganttGapMinutes,
      Math.max(displayedDurationMinutes - productiveMinutesBeforeInferredWait, 0),
    )
    : stopWaitMinutes;
  const serviceTotalMinutes = serviceMinutesByType.inspectionMinutes
    + serviceMinutesByType.loadingMinutes
    + serviceMinutesByType.unloadingMinutes
    + serviceMinutesByType.aspiratorMinutes
    + visibleWaitMinutes;
  const segmentTotalMinutes = drivingMinutes + serviceTotalMinutes + manualBufferMinutes;

  return {
    displayedDurationMinutes,
    distanceKm: distanceKm > 0 ? distanceKm : null,
    drivingMinutes,
    drivingSegments,
    ganttGapMinutes,
    ganttGaps: ganttGapDetails.gaps,
    aspiratorMinutes: serviceMinutesByType.aspiratorMinutes,
    inspectionMinutes: serviceMinutesByType.inspectionMinutes,
    loadingMinutes: serviceMinutesByType.loadingMinutes,
    loadingFixedMinutes: serviceMinutesByType.loadingFixedMinutes || 0,
    unloadingFixedMinutes: serviceMinutesByType.unloadingFixedMinutes || 0,
    lunchBreakMinutes,
    manualBufferMinutes,
    segmentTotalMinutes,
    stopWaitMinutes,
    unloadingMinutes: serviceMinutesByType.unloadingMinutes,
    visibleDeltaMinutes: displayedDurationMinutes == null
      ? null
      : displayedDurationMinutes - segmentTotalMinutes,
    waitMinutes: visibleWaitMinutes,
  };
}

export function resolveDisplayedTimelineFrontendTotalDurationMinutes(frontendDurationBreakdown = null, fallbackDurationMinutes = null) {
  const displayedDurationMinutes = Number(frontendDurationBreakdown?.displayedDurationMinutes);
  if (Number.isFinite(displayedDurationMinutes) && displayedDurationMinutes >= 0) {
    return displayedDurationMinutes;
  }

  const normalizedFallbackDurationMinutes = Number(fallbackDurationMinutes);
  if (Number.isFinite(normalizedFallbackDurationMinutes) && normalizedFallbackDurationMinutes >= 0) {
    return normalizedFallbackDurationMinutes;
  }

  const segmentTotalMinutes = Number(frontendDurationBreakdown?.segmentTotalMinutes);
  return Number.isFinite(segmentTotalMinutes) && segmentTotalMinutes >= 0 ? segmentTotalMinutes : null;
}

export function resolveFrontendCanonicalTotalDurationMinutes(
  frontendDurationBreakdown = null,
  {
    rowVisibleDurationMinutes = null,
    timelineSpanDurationMinutes = null,
  } = {},
) {
  const segmentTotalMinutes = normalizePositiveDurationMinutes(frontendDurationBreakdown?.segmentTotalMinutes);
  // Début → Fin compte la pause dîner, qui n'est pas du temps travaillé.
  const lunchBreakMinutes = Number(frontendDurationBreakdown?.lunchBreakMinutes) || 0;
  const withoutLunchBreak = (minutes) => (minutes == null ? minutes : Number(minutes) - lunchBreakMinutes);
  const timelineSpanMinutes = normalizePositiveDurationMinutes(
    withoutLunchBreak(frontendDurationBreakdown?.displayedDurationMinutes ?? timelineSpanDurationMinutes),
  );
  const rowEnvelopeMinutes = normalizePositiveDurationMinutes(withoutLunchBreak(rowVisibleDurationMinutes));
  const spanInflatedBeyondSegmentTotal = timelineSpanMinutes != null
    && segmentTotalMinutes != null
    && timelineSpanMinutes > segmentTotalMinutes + 0.5;
  const rowEnvelopeMatchesInflatedSpan = rowEnvelopeMinutes != null
    && timelineSpanMinutes != null
    && Math.abs(rowEnvelopeMinutes - timelineSpanMinutes) < 1;

  if (spanInflatedBeyondSegmentTotal && rowEnvelopeMatchesInflatedSpan) {
    return segmentTotalMinutes;
  }

  return [segmentTotalMinutes, timelineSpanMinutes, rowEnvelopeMinutes]
    .filter((value) => value != null)
    .reduce((maxValue, value) => Math.max(maxValue, value), null);
}

export function getRowActiveTrip(row, preferredTripId = "") {
  const tripGroups = Array.isArray(row?.tripGroups) ? row.tripGroups : [];

  if (preferredTripId) {
    const preferredTripGroup = tripGroups.find((tripGroup) => tripGroup?.tripId === preferredTripId);
    if (preferredTripGroup?.trip) {
      return preferredTripGroup.trip;
    }
  }

  const selectedTripGroup = tripGroups.find((tripGroup) => tripGroup?.tripId === row?.selectedTripId);
  return selectedTripGroup?.trip || tripGroups[0]?.trip || null;
}

export function buildSegmentPositionData(segments, scaleMinutes, rowDurationMinutes = null, timelineStartMinutes = null, { leadingFixedPercent = 0, trailingFixedPercent = 0, informationalSegmentWidthPercent = 0 } = {}) {
  const safeScaleMinutes = scaleMinutes > 0 ? scaleMinutes : 1;
  const resolvedSegments = Array.isArray(segments) ? segments : [];
  const resolvedLeadingFixedPercent = Number.isFinite(Number(leadingFixedPercent)) && Number(leadingFixedPercent) > 0
    ? Math.min(Math.max(Number(leadingFixedPercent), 0), 40)
    : 0;
  const resolvedTrailingFixedPercent = Number.isFinite(Number(trailingFixedPercent)) && Number(trailingFixedPercent) > 0
    ? Math.min(Math.max(Number(trailingFixedPercent), 0), 40)
    : 0;
  const resolvedInformationalSegmentWidthPercent = Number.isFinite(Number(informationalSegmentWidthPercent)) && Number(informationalSegmentWidthPercent) > 0
    ? Number(informationalSegmentWidthPercent)
    : 0;
  const timedUsablePercent = Math.max(100 - resolvedLeadingFixedPercent - resolvedTrailingFixedPercent, 0);
  const totalDurationMinutes = resolvedSegments.reduce(
    (total, segment) => {
      if (isInformationalTimelineSegment(segment)) {
        return total;
      }

      const visualTimelineDurationMinutes = Number(segment?.visualTimelineDurationMinutes) > 0
        ? Number(segment.visualTimelineDurationMinutes)
        : Number(segment?.timelineDurationMinutes) > 0
          ? Number(segment.timelineDurationMinutes)
          : 0;

      return total + visualTimelineDurationMinutes;
    },
    0,
  );
  const normalizedRowDurationMinutes = Number(rowDurationMinutes);
  const displayedRowDurationMinutes = Number.isFinite(normalizedRowDurationMinutes) && normalizedRowDurationMinutes > totalDurationMinutes
    ? normalizedRowDurationMinutes
    : totalDurationMinutes;
  const rowSpanPercent = (displayedRowDurationMinutes / safeScaleMinutes) * timedUsablePercent;
  const segmentCount = resolvedSegments.filter((segment) => !isInformationalTimelineSegment(segment)).length;
  const minSegmentPercent = rowSpanPercent > 0 && segmentCount > 0
    ? Math.min(MIN_SEGMENT_VISUAL_PERCENT, rowSpanPercent / segmentCount)
    : 0;
  const distributablePercent = Math.max(rowSpanPercent - (minSegmentPercent * segmentCount), 0);
  const safeTotalDurationMinutes = totalDurationMinutes > 0 ? totalDurationMinutes : 1;
  let cursorPercent = resolvedLeadingFixedPercent;
  let preloadCursorPercent = 0;
  let endOfDayCursorPercent = 0;
  const preloadSegmentCount = resolvedSegments.filter(isLeadingPreloadTimelineSegment).length;
  const endOfDaySegmentCount = resolvedSegments.filter((segment) => {
    if (!segment?.isEndOfDayLoad) {
      return false;
    }

    return !(
      segment?.isSameSiteSequenceGroup
      && Number(segment?.sameSiteGroupSize) > 1
      && Number(segment?.sameSiteGroupIndex) > 0
    );
  }).length;
  const hasInitialPreload = preloadSegmentCount > 0;

  return resolvedSegments.map((segment) => {
    if (isLeadingPreloadTimelineSegment(segment)) {
      const isGroupedFollower = segment?.isSameSiteSequenceGroup
        && Number(segment?.sameSiteGroupSize) > 1
        && Number(segment?.sameSiteGroupIndex) > 0;
      const pillWidthPercent = resolvedInformationalSegmentWidthPercent > 0
        ? resolvedInformationalSegmentWidthPercent
        : preloadSegmentCount > 0
          ? resolvedLeadingFixedPercent / preloadSegmentCount
          : 0;
      const widthPercent = isGroupedFollower ? 0 : pillWidthPercent;
      const leftPercent = isGroupedFollower
        ? Math.max(preloadCursorPercent - pillWidthPercent, 0)
        : preloadCursorPercent;

      if (!isGroupedFollower) {
        preloadCursorPercent += pillWidthPercent;
      }

      return {
        ...segment,
        leftPercent,
        servicePercent: 0,
        widthPercent,
      };
    }

    if (segment?.isEndOfDayLoad) {
      const isGroupedFollower = segment?.isSameSiteSequenceGroup
        && Number(segment?.sameSiteGroupSize) > 1
        && Number(segment?.sameSiteGroupIndex) > 0;
      const pillWidthPercent = resolvedInformationalSegmentWidthPercent > 0
        ? resolvedInformationalSegmentWidthPercent
        : endOfDaySegmentCount > 0
          ? resolvedTrailingFixedPercent / endOfDaySegmentCount
          : 0;
      const widthPercent = isGroupedFollower ? 0 : pillWidthPercent;
      const totalEndOfDayWidthPercent = pillWidthPercent * endOfDaySegmentCount;
      const trailingGutterOffsetPercent = Math.max(resolvedTrailingFixedPercent - totalEndOfDayWidthPercent, 0);
      const leftPercent = 100 - resolvedTrailingFixedPercent + trailingGutterOffsetPercent + (
        isGroupedFollower
          ? Math.max(endOfDayCursorPercent - pillWidthPercent, 0)
          : endOfDayCursorPercent
      );

      if (!isGroupedFollower) {
        endOfDayCursorPercent += pillWidthPercent;
      }

      return {
        ...segment,
        leftPercent,
        servicePercent: 0,
        timelineScaleMinutes: safeScaleMinutes,
        timelineUsablePercent: timedUsablePercent,
        widthPercent,
      };
    }

    const visualTimelineDurationMinutes = Number(segment?.visualTimelineDurationMinutes) > 0
      ? Number(segment.visualTimelineDurationMinutes)
      : Number(segment?.timelineDurationMinutes) > 0
        ? Number(segment.timelineDurationMinutes)
        : 0;
    const timelineDurationMinutes = Number(segment?.timelineDurationMinutes) > 0
      ? Number(segment.timelineDurationMinutes)
      : visualTimelineDurationMinutes;
    const displayTimelineDurationMinutes = timelineDurationMinutes > 0
      ? timelineDurationMinutes
      : visualTimelineDurationMinutes;
    const serviceDurationMinutes = resolveTimelineDisplayServiceDurationMinutes(segment);
    const displayTravelDurationMinutes = resolveTimelineDisplayTravelDurationMinutes(segment);
    const displayedOverlayDurationMinutes = displayTravelDurationMinutes + serviceDurationMinutes + resolveSequenceManualBufferMinutes(segment);
    const fallbackLeftPercent = cursorPercent;
    const fallbackWidthPercent = rowSpanPercent > 0
      ? minSegmentPercent + (distributablePercent * (visualTimelineDurationMinutes / safeTotalDurationMinutes))
      : 0;
    const scheduledSegmentStartMinutes = resolveTimelinePositionStartMinutes(segment, { hasInitialPreload });
    const scheduledSegmentEndMinutes = resolveTimelinePositionEndMinutes(segment);
    const hasAbsoluteAnchor = scheduledSegmentStartMinutes != null && isFiniteTimelineMinutes(timelineStartMinutes);
    const visibleSegmentStartMinutes = hasAbsoluteAnchor
      ? Math.max(scheduledSegmentStartMinutes, Number(timelineStartMinutes))
      : scheduledSegmentStartMinutes;
    const visibleDisplayTimelineDurationMinutes = hasAbsoluteAnchor && scheduledSegmentEndMinutes != null
      ? Math.max(scheduledSegmentEndMinutes - visibleSegmentStartMinutes, 0)
      : displayTimelineDurationMinutes;
    const anchoredLeftPercent = hasAbsoluteAnchor
      ? Math.max(
        resolvedLeadingFixedPercent + (((visibleSegmentStartMinutes - Number(timelineStartMinutes)) / safeScaleMinutes) * timedUsablePercent),
        resolvedLeadingFixedPercent,
      )
      : null;
    const absoluteWidthPercent = visibleDisplayTimelineDurationMinutes > 0
      ? Math.max((visibleDisplayTimelineDurationMinutes / safeScaleMinutes) * timedUsablePercent, 0)
      : 0;
    const leftPercent = anchoredLeftPercent != null
      ? anchoredLeftPercent
      : fallbackLeftPercent;
    const widthPercent = anchoredLeftPercent != null
      ? absoluteWidthPercent
      : fallbackWidthPercent;
    cursorPercent = Math.max(cursorPercent, leftPercent + widthPercent);

    return {
      ...segment,
      leftPercent,
      servicePercent: displayedOverlayDurationMinutes > 0 && serviceDurationMinutes > 0
        ? Math.min((serviceDurationMinutes / displayedOverlayDurationMinutes) * 100, 100)
        : 0,
      timelineScaleMinutes: safeScaleMinutes,
      timelineUsablePercent: timedUsablePercent,
      widthPercent,
    };
  });
}

function resolveContiguousTimelineItemEndMinutes(segment) {
  const displayedEndMinutes = resolveDisplayedSegmentEndMinutes(segment);
  return displayedEndMinutes != null ? displayedEndMinutes : resolveRenderSegmentEndMinutes(segment);
}

function addAbsorbedGapToDurationValue(value, absorbedGapMinutes) {
  const durationMinutes = Number(value);
  if (!Number.isFinite(durationMinutes) || durationMinutes < 0) {
    return null;
  }

  return durationMinutes + absorbedGapMinutes;
}

function assignAdjustedDurationField(target, field, value) {
  if (Number.isFinite(value) && value >= 0) {
    target[field] = value;
  }
}

function buildTimelineSegmentWithAbsorbedGap(segment, {
  absorbedGapMinutes = 0,
  displayedSegmentStartMinutes = null,
} = {}) {
  const normalizedGapMinutes = Number(absorbedGapMinutes);
  const normalizedStartMinutes = Number(displayedSegmentStartMinutes);
  if (
    !segment
    || !Number.isFinite(normalizedGapMinutes)
    || normalizedGapMinutes <= 0
    || !Number.isFinite(normalizedStartMinutes)
  ) {
    return segment;
  }

  const displayTravelDurationMinutes = resolveSequenceDisplayTravelMinutes(segment) + normalizedGapMinutes;
  const legacyDisplayTravelDurationMinutes = resolveSequenceLegacyDisplayTravelMinutes(segment) + normalizedGapMinutes;
  const travelDurationMinutes = resolveSequenceTravelMinutes(segment) + normalizedGapMinutes;
  const fleetDisplayedTravelDurationMinutes = addAbsorbedGapToDurationValue(segment?.fleetDisplayedTravelDurationMinutes, normalizedGapMinutes)
    ?? displayTravelDurationMinutes;
  const adjustedSegment = {
    ...segment,
    displayedSegmentStartAt: null,
    displayedSegmentStartMinutes: normalizedStartMinutes,
    displayTravelDurationMinutes,
    fleetDisplayedTravelDurationMinutes,
    fleetTimelineAbsorbedGapMinutes: normalizedGapMinutes,
    legacyDisplayTravelDurationMinutes,
    travelDurationMinutes,
  };

  assignAdjustedDurationField(
    adjustedSegment,
    "routeScheduledTravelDurationMinutes",
    addAbsorbedGapToDurationValue(segment?.routeScheduledTravelDurationMinutes, normalizedGapMinutes),
  );
  assignAdjustedDurationField(
    adjustedSegment,
    "baseTimelineDurationMinutes",
    addAbsorbedGapToDurationValue(segment?.baseTimelineDurationMinutes, normalizedGapMinutes),
  );
  assignAdjustedDurationField(
    adjustedSegment,
    "timelineDurationMinutes",
    addAbsorbedGapToDurationValue(segment?.timelineDurationMinutes, normalizedGapMinutes),
  );
  assignAdjustedDurationField(
    adjustedSegment,
    "visualTimelineDurationMinutes",
    addAbsorbedGapToDurationValue(segment?.visualTimelineDurationMinutes, normalizedGapMinutes),
  );
  assignAdjustedDurationField(
    adjustedSegment,
    "displayedSegmentTotalDurationMinutes",
    addAbsorbedGapToDurationValue(segment?.displayedSegmentTotalDurationMinutes, normalizedGapMinutes),
  );

  if (Number.isFinite(adjustedSegment.baseTimelineDurationMinutes)) {
    adjustedSegment.baseTimelineDurationLabel = formatDurationShort(adjustedSegment.baseTimelineDurationMinutes) || "0 min";
  }
  if (Number.isFinite(adjustedSegment.timelineDurationMinutes)) {
    adjustedSegment.timelineDurationLabel = formatDurationShort(adjustedSegment.timelineDurationMinutes) || "0 min";
  }
  adjustedSegment.travelDurationLabel = displayTravelDurationMinutes > 0
    ? (formatDurationShort(displayTravelDurationMinutes) || "0 min")
    : "0 min";

  return adjustedSegment;
}

export function buildTimelineRenderItems(segments, {
  expandedGroupKey = "",
  preserveAbsoluteTimelinePositions = false,
  timelineStartMinutes = null,
} = {}) {
  const resolvedSegments = Array.isArray(segments) ? segments.filter(Boolean) : [];
  const renderItems = [];
  let cursor = 0;
  let collapsedPercentOffset = 0;
  let previousRenderEndPercent = 0;
  let previousRenderEndIsInformationalSegment = false;
  let previousRenderEndMinutes = isFiniteTimelineMinutes(timelineStartMinutes)
    ? Number(timelineStartMinutes)
    : null;

  const resolveShiftedLeftPercent = (rawLeftPercent) => {
    const resolvedRawLeftPercent = Number(rawLeftPercent);
    if (!Number.isFinite(resolvedRawLeftPercent)) {
      return 0;
    }

    return preserveAbsoluteTimelinePositions
      ? Math.max(resolvedRawLeftPercent, 0)
      : Math.max(resolvedRawLeftPercent - collapsedPercentOffset, 0);
  };

  const maybePushPriorityIdleItem = (segment, itemLeftPercent) => {
    if (!shouldRenderPriorityIdleItem(segment)) {
      return null;
    }

    const currentStartMinutes = resolveRenderSegmentStartMinutes(segment);
    const resolvedLeftPercent = Number(itemLeftPercent);
    const fallbackIdleWidthPercent = resolvedLeftPercent - previousRenderEndPercent;

    if (
      !Number.isFinite(resolvedLeftPercent)
      || !Number.isFinite(fallbackIdleWidthPercent)
      || !isFiniteTimelineMinutes(previousRenderEndMinutes)
      || currentStartMinutes == null
      || currentStartMinutes <= previousRenderEndMinutes
    ) {
      return null;
    }

    const timelineScaleMinutes = resolveTimelineScaleMinutesFromPositionedItem(segment);
    const timelineUsablePercent = resolveTimelineUsablePercentFromPositionedItem(segment);
    const scaledIdleWidthPercent = Number.isFinite(timelineScaleMinutes) && timelineScaleMinutes > 0
      ? ((currentStartMinutes - previousRenderEndMinutes) / timelineScaleMinutes) * timelineUsablePercent
      : null;
    const idleWidthPercent = Number.isFinite(scaledIdleWidthPercent) && scaledIdleWidthPercent > 0
      ? scaledIdleWidthPercent
      : fallbackIdleWidthPercent;

    if (!Number.isFinite(idleWidthPercent) || idleWidthPercent <= 0) {
      return null;
    }

    renderItems.push({
      durationMinutes: currentStartMinutes - previousRenderEndMinutes,
      endMinutes: currentStartMinutes,
      key: `idle:${segment?.assignmentId || "assignment"}::${segment?.tripId || "trip"}::${segment?.globalSequence || cursor + 1}`,
      kind: "idle",
      leftPercent: previousRenderEndPercent,
      priority: segment?.dominantPriority || null,
      tripId: segment?.tripId || "",
      startMinutes: previousRenderEndMinutes,
      widthPercent: idleWidthPercent,
    });

    return previousRenderEndPercent + idleWidthPercent;
  };

  const resolveContiguousTimelineItemPlacement = (segment, rawLeftPercent, rawWidthPercent) => {
    const resolvedRawLeftPercent = Number(rawLeftPercent);
    const resolvedRawWidthPercent = Number(rawWidthPercent) || 0;
    if (!Number.isFinite(resolvedRawLeftPercent)) {
      return {
        absorbedGapMinutes: 0,
        displayedSegmentStartMinutes: null,
        leftPercent: 0,
        widthPercent: resolvedRawWidthPercent,
      };
    }

    if (segment?.isEndOfDayLoad) {
      return {
        absorbedGapMinutes: 0,
        displayedSegmentStartMinutes: null,
        leftPercent: resolvedRawLeftPercent,
        widthPercent: resolvedRawWidthPercent,
      };
    }

    if (
      previousRenderEndIsInformationalSegment
      && !isInformationalTimelineSegment(segment)
      && renderItems.length > 0
      && resolvedRawLeftPercent > previousRenderEndPercent
    ) {
      return {
        absorbedGapMinutes: 0,
        displayedSegmentStartMinutes: null,
        leftPercent: resolvedRawLeftPercent,
        widthPercent: resolvedRawWidthPercent,
      };
    }

    const priorityIdleEndPercent = maybePushPriorityIdleItem(segment, resolvedRawLeftPercent);
    if (Number.isFinite(priorityIdleEndPercent)) {
      return {
        absorbedGapMinutes: 0,
        displayedSegmentStartMinutes: null,
        leftPercent: priorityIdleEndPercent,
        widthPercent: resolvedRawWidthPercent,
      };
    }

    if (renderItems.length > 0 && resolvedRawLeftPercent > previousRenderEndPercent) {
      const gapPercent = resolvedRawLeftPercent - previousRenderEndPercent;
      const currentStartMinutes = resolveRenderSegmentStartMinutes(segment);
      const timelineScaleMinutes = resolveTimelineScaleMinutesFromPositionedItem(segment);
      const timelineUsablePercent = resolveTimelineUsablePercentFromPositionedItem(segment);
      const inferredGapMinutes = Number.isFinite(timelineScaleMinutes) && timelineScaleMinutes > 0 && timelineUsablePercent > 0
        ? (gapPercent / timelineUsablePercent) * timelineScaleMinutes
        : 0;
      const gapMinutes = currentStartMinutes != null && isFiniteTimelineMinutes(previousRenderEndMinutes)
        ? currentStartMinutes - previousRenderEndMinutes
        : inferredGapMinutes;
      // La pause dîner a son propre bloc : l'intervalle qu'elle occupe n'est pas de la route.
      const previousItem = renderItems[renderItems.length - 1];
      const lunchBreakMinutes = resolveSequenceManualBufferMinutes(
        [previousItem?.segment, ...(previousItem?.memberSegments || [])].find(isLunchBreakSegment),
      );
      const absorbedGapMinutes = Math.max(gapMinutes - lunchBreakMinutes, 0);
      const displayedSegmentStartMinutes = currentStartMinutes != null && absorbedGapMinutes > 0
        ? currentStartMinutes - absorbedGapMinutes
        : previousRenderEndMinutes;

      return {
        absorbedGapMinutes,
        displayedSegmentStartMinutes,
        leftPercent: previousRenderEndPercent,
        widthPercent: Math.max(resolvedRawWidthPercent + gapPercent, 0),
      };
    }

    return {
      absorbedGapMinutes: 0,
      displayedSegmentStartMinutes: null,
      leftPercent: resolvedRawLeftPercent,
      widthPercent: resolvedRawWidthPercent,
    };
  };

  const updatePreviousRenderEnd = (endPercent, endMinutes, { isInitialPreload = false, isEndOfDayLoad = false, isEndOfDayLoadSourcePreload = false } = {}) => {
    const resolvedEndPercent = Number(endPercent);
    if (Number.isFinite(resolvedEndPercent)) {
      previousRenderEndPercent = Math.max(previousRenderEndPercent, resolvedEndPercent);
    }

    if (isFiniteTimelineMinutes(endMinutes)) {
      previousRenderEndMinutes = previousRenderEndMinutes == null
        ? Number(endMinutes)
        : Math.max(previousRenderEndMinutes, Number(endMinutes));
    }

    previousRenderEndIsInformationalSegment = isInitialPreload === true || isEndOfDayLoad === true || isEndOfDayLoadSourcePreload === true;
  };

  while (cursor < resolvedSegments.length) {
    const segment = resolvedSegments[cursor] || null;
    const sameSiteGroupKey = typeof segment?.sameSiteGroupKey === "string"
      ? segment.sameSiteGroupKey.trim()
      : "";
    const sameSiteGroupSize = Number.isInteger(segment?.sameSiteGroupSize) ? segment.sameSiteGroupSize : 1;
    const adjacentAspiratorVisit = findAdjacentAspiratorLoadingDeliveryGroupVisit(resolvedSegments, cursor);

    if (adjacentAspiratorVisit) {
      const mergedLayout = resolveMergedSameAddressTimelineLayout(adjacentAspiratorVisit.memberSegments);
      const shiftedLeftPercent = resolveShiftedLeftPercent(mergedLayout.leftPercent);
      const placement = resolveContiguousTimelineItemPlacement(
        adjacentAspiratorVisit.memberSegments[0],
        shiftedLeftPercent,
        mergedLayout.widthPercent,
      );
      const mergedSegment = buildTimelineSegmentWithAbsorbedGap(
        {
          ...buildMergedSameAddressTimelineSegment(adjacentAspiratorVisit.memberSegments, {
            leftPercent: placement.leftPercent,
            servicePercent: mergedLayout.servicePercent,
            widthPercent: placement.widthPercent,
          }),
          isSameSiteSequenceGroup: false,
          sameSiteGroupIndex: null,
          sameSiteGroupKey: adjacentAspiratorVisit.visualGroupKey,
          sameSiteGroupSize: 1,
        },
        placement,
      );

      renderItems.push({
        key: `segment:${adjacentAspiratorVisit.visualGroupKey}`,
        kind: "segment",
        segment: mergedSegment,
      });
      updatePreviousRenderEnd(
        placement.leftPercent + placement.widthPercent,
        resolveContiguousTimelineItemEndMinutes(mergedSegment),
        {
          isInitialPreload: isLeadingPreloadTimelineSegment(mergedSegment),
          isEndOfDayLoad: mergedSegment?.isEndOfDayLoad === true,
          isEndOfDayLoadSourcePreload: mergedSegment?.isEndOfDayLoadSourcePreload === true,
        },
      );
      cursor = adjacentAspiratorVisit.groupEnd;
      continue;
    }

    if (!segment?.isSameSiteSequenceGroup || !sameSiteGroupKey || sameSiteGroupSize <= 1 || segment.sameSiteGroupIndex !== 0) {
      const rawLeftPercent = resolveShiftedLeftPercent(segment?.leftPercent);
      const widthPercent = Number(segment?.widthPercent) || 0;
      const absorbingSameAddressGroupLead = findAbsorbingSameAddressGroupLead(resolvedSegments, cursor);

      if (absorbingSameAddressGroupLead) {
        collapsedPercentOffset += widthPercent;
        updatePreviousRenderEnd(rawLeftPercent, resolveContiguousTimelineItemEndMinutes(segment), {
          isInitialPreload: isLeadingPreloadTimelineSegment(segment),
          isEndOfDayLoad: segment?.isEndOfDayLoad === true,
          isEndOfDayLoadSourcePreload: segment?.isEndOfDayLoadSourcePreload === true,
        });
        cursor += 1;
        continue;
      }

      const placement = resolveContiguousTimelineItemPlacement(segment, rawLeftPercent, widthPercent);
      const renderedSegment = buildTimelineSegmentWithAbsorbedGap(segment, placement);

      renderItems.push({
        key: `segment:${segment?.assignmentId || "assignment"}::${segment?.tripId || "trip"}::${segment?.globalSequence || cursor + 1}`,
        kind: "segment",
        segment: {
          ...renderedSegment,
          leftPercent: placement.leftPercent,
          widthPercent: placement.widthPercent,
        },
      });
      updatePreviousRenderEnd(
        placement.leftPercent + placement.widthPercent,
        resolveContiguousTimelineItemEndMinutes(renderedSegment),
        {
          isInitialPreload: isLeadingPreloadTimelineSegment(renderedSegment),
          isEndOfDayLoad: renderedSegment?.isEndOfDayLoad === true,
          isEndOfDayLoadSourcePreload: renderedSegment?.isEndOfDayLoadSourcePreload === true,
        },
      );
      cursor += 1;
      continue;
    }

    const { groupEnd, memberSegments } = collectSameSiteGroupMembers(resolvedSegments, cursor, sameSiteGroupKey);

    const firstSegment = memberSegments[0] || null;
    const lastSegment = memberSegments[memberSegments.length - 1] || firstSegment;
    const normalizedMemberSegments = normalizeSameSiteGroupMemberSegments(memberSegments);
    const normalizedFirstSegment = normalizedMemberSegments[0] || firstSegment;
    const normalizedLastSegment = normalizedMemberSegments[normalizedMemberSegments.length - 1] || normalizedFirstSegment;
    const nextSegment = resolvedSegments[groupEnd] || null;
    const rawLeftPercent = Number(firstSegment?.leftPercent) || 0;
    const rawFirstSegmentWidthPercent = Number(firstSegment?.widthPercent) || 0;
    const rawFirstSegmentEndPercent = rawLeftPercent + rawFirstSegmentWidthPercent;
    const rawGroupEndPercent = (Number(lastSegment?.leftPercent) || 0) + (Number(lastSegment?.widthPercent) || 0);
    const normalizedMergedSegmentWidthPercent = normalizedMemberSegments.length === 1
      && normalizedMemberSegments[0]?.isSameAddressLoadingDeliveryMerged === true
      && Number(normalizedMemberSegments[0]?.widthPercent) > 0
      ? Number(normalizedMemberSegments[0].widthPercent)
      : null;
    const shiftedLeftPercent = resolveShiftedLeftPercent(rawLeftPercent);
    const isNonExpandableInformationalGroup = Boolean(isLeadingPreloadTimelineSegment(firstSegment) || firstSegment?.isEndOfDayLoad);
    const preserveCollapsedInformationalWidth = isNonExpandableInformationalGroup;
    const preserveTrailingTerminalWidth = Boolean(nextSegment?.isTerminalRouteMarker)
      && rawGroupEndPercent > rawFirstSegmentEndPercent;
    const widthPercent = preserveTrailingTerminalWidth
      ? Math.max(rawGroupEndPercent - rawLeftPercent, 0)
      : preserveCollapsedInformationalWidth
        ? Math.max(rawFirstSegmentWidthPercent, 0)
        : Math.max(normalizedMergedSegmentWidthPercent ?? rawFirstSegmentWidthPercent, 0);
    const removedWidthPercent = Math.max(rawGroupEndPercent - (rawLeftPercent + widthPercent), 0);
    const sameSiteGroupContainsMixedStopTypes = resolveSameSiteGroupContainsMixedStopTypes(
      memberSegments,
      normalizedMemberSegments,
    );
    const groupServiceDurationMinutes = normalizedMemberSegments.reduce((total, memberSegment) => {
      const serviceDurationMinutes = resolveTimelineDisplayServiceDurationMinutes(memberSegment);
      return serviceDurationMinutes > 0
        ? total + serviceDurationMinutes
        : total;
    }, 0);
    const groupDisplayTravelDurationMinutes = normalizedMemberSegments.reduce((total, memberSegment) => {
      const travelDurationMinutes = resolveTimelineDisplayTravelDurationMinutes(memberSegment);
      return travelDurationMinutes > 0
        ? total + travelDurationMinutes
        : total;
    }, 0);
    const groupManualBufferMinutes = normalizedMemberSegments.reduce((total, memberSegment) => {
      const manualBufferMinutes = resolveSequenceManualBufferMinutes(memberSegment);
      return manualBufferMinutes > 0
        ? total + manualBufferMinutes
        : total;
    }, 0);
    const groupDisplayedOverlayDurationMinutes = groupDisplayTravelDurationMinutes + groupServiceDurationMinutes + groupManualBufferMinutes;
    const placement = resolveContiguousTimelineItemPlacement(firstSegment, shiftedLeftPercent, widthPercent);
    const leftPercent = placement.leftPercent;
    const placedWidthPercent = placement.widthPercent;
    const placedNormalizedMemberSegments = placement.absorbedGapMinutes > 0 && normalizedMemberSegments.length
      ? [
          buildTimelineSegmentWithAbsorbedGap(normalizedMemberSegments[0], placement),
          ...normalizedMemberSegments.slice(1),
        ]
      : normalizedMemberSegments;
    const placedNormalizedFirstSegment = placedNormalizedMemberSegments[0] || normalizedFirstSegment;
    const placedNormalizedLastSegment = placedNormalizedMemberSegments[placedNormalizedMemberSegments.length - 1]
      || normalizedLastSegment;
    const placedGroupDisplayedOverlayDurationMinutes = groupDisplayedOverlayDurationMinutes + Math.max(placement.absorbedGapMinutes || 0, 0);

    if (
      placedNormalizedMemberSegments.length === 1
      && placedNormalizedMemberSegments[0]?.isSameAddressLoadingDeliveryMerged === true
    ) {
      const mergedSegment = {
        ...placedNormalizedMemberSegments[0],
        leftPercent,
        widthPercent: placedWidthPercent,
      };

      renderItems.push({
        key: `segment:${sameSiteGroupKey}:merged`,
        kind: "segment",
        segment: mergedSegment,
      });

      collapsedPercentOffset += removedWidthPercent;
      updatePreviousRenderEnd(
        leftPercent + placedWidthPercent,
        resolveContiguousTimelineItemEndMinutes(mergedSegment) ?? resolveContiguousTimelineItemEndMinutes(lastSegment),
        {
          isInitialPreload: isLeadingPreloadTimelineSegment(mergedSegment),
          isEndOfDayLoad: mergedSegment?.isEndOfDayLoad === true,
          isEndOfDayLoadSourcePreload: mergedSegment?.isEndOfDayLoadSourcePreload === true,
        },
      );
      cursor = groupEnd;
      continue;
    }

    const shouldExpandFallbackMixedGroup = sameSiteGroupContainsMixedStopTypes
      && isFallbackSameSiteGroupKey(sameSiteGroupKey);
    const shouldExpandRequestedGroup = !isNonExpandableInformationalGroup
      && expandedGroupKey
      && sameSiteGroupKey === expandedGroupKey;

    if (shouldExpandFallbackMixedGroup || shouldExpandRequestedGroup) {
      const expandedMemberSegments = buildExpandedTimelineGroupMemberSegments(placedNormalizedMemberSegments, {
        leftPercent,
        widthPercent: placedWidthPercent,
      });

      expandedMemberSegments.forEach((memberSegment, memberIndex) => {
        const resolvedWidthPercent = Number(memberSegment?.widthPercent) || 0;
        const resolvedLeftPercent = Number(memberSegment?.leftPercent) || 0;

        renderItems.push({
          key: `segment:${memberSegment?.assignmentId || "assignment"}::${memberSegment?.tripId || "trip"}::${memberSegment?.globalSequence || cursor + memberIndex + 1}`,
          kind: "segment",
          segment: memberSegment,
        });

        updatePreviousRenderEnd(
          resolvedLeftPercent + resolvedWidthPercent,
          resolveContiguousTimelineItemEndMinutes(memberSegment),
          {
            isInitialPreload: isLeadingPreloadTimelineSegment(memberSegment),
            isEndOfDayLoad: memberSegment?.isEndOfDayLoad === true,
            isEndOfDayLoadSourcePreload: memberSegment?.isEndOfDayLoadSourcePreload === true,
          },
        );
      });

      collapsedPercentOffset += removedWidthPercent;
      cursor = groupEnd;
      continue;
    }

    renderItems.push({
      collapsedEndMinutes: resolveRenderSegmentEndMinutes(firstSegment),
      endSequence: lastSegment?.globalSequence || firstSegment?.globalSequence || 0,
      groupKey: sameSiteGroupKey,
      groupEndMinutes: resolveRenderSegmentEndMinutes(lastSegment),
      key: `group:${sameSiteGroupKey}`,
      kind: "group",
      isInitialPreload: placedNormalizedMemberSegments.some((memberSegment) => memberSegment?.isInitialPreload),
      isEndOfDayLoadSourcePreload: placedNormalizedMemberSegments.some((memberSegment) => memberSegment?.isEndOfDayLoadSourcePreload),
      isEndOfDayLoad: placedNormalizedMemberSegments.some((memberSegment) => memberSegment?.isEndOfDayLoad),
      leftPercent,
      memberSegments: placedNormalizedMemberSegments,
      servicePercent: placedGroupDisplayedOverlayDurationMinutes > 0 && groupServiceDurationMinutes > 0
        ? Math.min((groupServiceDurationMinutes / placedGroupDisplayedOverlayDurationMinutes) * 100, 100)
        : 0,
      sameSiteGroupContainsMixedStopTypes,
      sameSiteGroupSize: placedNormalizedMemberSegments.length,
      startSequence: firstSegment?.sameSiteGroupStartSequence || firstSegment?.globalSequence || 0,
      stopType: sameSiteGroupContainsMixedStopTypes ? "mixed" : (placedNormalizedFirstSegment?.stopType || firstSegment?.stopType || "delivery"),
      toAddress: placedNormalizedFirstSegment?.toAddress || firstSegment?.toAddress || "",
      toLabel: placedNormalizedFirstSegment?.toLabel || firstSegment?.toLabel || "",
      widthPercent: placedWidthPercent,
    });

    collapsedPercentOffset += removedWidthPercent;
    // Groupe replié : dessiné jusqu'à la fin du premier membre (route vers l'arrêt suivant), souvent après le dernier.
    updatePreviousRenderEnd(
      leftPercent + placedWidthPercent,
      Math.max(
        resolveContiguousTimelineItemEndMinutes(placedNormalizedFirstSegment) ?? -Infinity,
        resolveContiguousTimelineItemEndMinutes(placedNormalizedLastSegment) ?? resolveContiguousTimelineItemEndMinutes(lastSegment) ?? -Infinity,
      ),
      {
        isInitialPreload: placedNormalizedMemberSegments.some((memberSegment) => memberSegment?.isInitialPreload),
        isEndOfDayLoad: placedNormalizedMemberSegments.some((memberSegment) => memberSegment?.isEndOfDayLoad),
        isEndOfDayLoadSourcePreload: placedNormalizedMemberSegments.some((memberSegment) => memberSegment?.isEndOfDayLoadSourcePreload),
      },
    );
    cursor = groupEnd;
  }

  return assignVisibleOperationalDisplaySequences(renderItems);
}

function buildExpandedGroupKeySet({
  expandedGroupKey = "",
  expandedGroupKeys = undefined,
} = {}) {
  const normalizedKeys = new Set();

  const registerGroupKey = (groupKey) => {
    if (typeof groupKey !== "string") {
      return;
    }

    const normalizedGroupKey = groupKey.trim();
    if (normalizedGroupKey) {
      normalizedKeys.add(normalizedGroupKey);
    }
  };

  if (Array.isArray(expandedGroupKeys)) {
    expandedGroupKeys.forEach(registerGroupKey);
  } else if (expandedGroupKeys instanceof Set) {
    expandedGroupKeys.forEach(registerGroupKey);
  }

  registerGroupKey(expandedGroupKey);
  return normalizedKeys;
}

export function buildSequenceTableRenderItems(segments, {
  expandedGroupKey = "",
  expandedGroupKeys = undefined,
} = {}) {
  const resolvedSegments = Array.isArray(segments) ? segments.filter(Boolean) : [];
  const expandedGroupKeySet = buildExpandedGroupKeySet({ expandedGroupKey, expandedGroupKeys });
  const renderItems = [];
  let cursor = 0;

  while (cursor < resolvedSegments.length) {
    const segment = resolvedSegments[cursor] || null;
    const sameSiteGroupKey = typeof segment?.sameSiteGroupKey === "string"
      ? segment.sameSiteGroupKey.trim()
      : "";
    const sameSiteGroupSize = Number.isInteger(segment?.sameSiteGroupSize) ? segment.sameSiteGroupSize : 1;
    const adjacentAspiratorVisit = findAdjacentAspiratorLoadingDeliveryGroupVisit(resolvedSegments, cursor);

    if (adjacentAspiratorVisit) {
      renderItems.push({
        key: `segment:${adjacentAspiratorVisit.visualGroupKey}`,
        kind: "segment",
        segment: {
          ...buildMergedSameAddressGroupMemberSegment(adjacentAspiratorVisit.memberSegments),
          sameSiteGroupKey: adjacentAspiratorVisit.visualGroupKey,
        },
      });
      cursor = adjacentAspiratorVisit.groupEnd;
      continue;
    }

    if (!segment?.isSameSiteSequenceGroup || !sameSiteGroupKey || sameSiteGroupSize <= 1 || segment.sameSiteGroupIndex !== 0) {
      renderItems.push({
        key: `segment:${segment?.assignmentId || "assignment"}::${segment?.tripId || "trip"}::${segment?.globalSequence || cursor + 1}`,
        kind: "segment",
        segment,
      });
      cursor += 1;
      continue;
    }

    const { groupEnd, memberSegments } = collectSameSiteGroupMembers(resolvedSegments, cursor, sameSiteGroupKey);

    const firstSegment = memberSegments[0] || null;
    const lastSegment = memberSegments[memberSegments.length - 1] || firstSegment;
    const normalizedMemberSegments = normalizeSameSiteGroupMemberSegments(memberSegments);
    const normalizedFirstSegment = normalizedMemberSegments[0] || firstSegment;
    const sameSiteGroupContainsMixedStopTypes = resolveSameSiteGroupContainsMixedStopTypes(
      memberSegments,
      normalizedMemberSegments,
    );
    const isExpanded = expandedGroupKeySet.has(sameSiteGroupKey);

    if (
      normalizedMemberSegments.length === 1
      && normalizedMemberSegments[0]?.isSameAddressLoadingDeliveryMerged === true
    ) {
      renderItems.push({
        key: `segment:${sameSiteGroupKey}:merged`,
        kind: "segment",
        segment: normalizedMemberSegments[0],
      });
      cursor = groupEnd;
      continue;
    }

    renderItems.push({
      endSequence: lastSegment?.routeSequenceDisplaySequence || lastSegment?.globalSequence || firstSegment?.routeSequenceDisplaySequence || firstSegment?.globalSequence || 0,
      groupKey: sameSiteGroupKey,
      isExpanded,
      isInitialPreload: normalizedMemberSegments.some((memberSegment) => memberSegment?.isInitialPreload),
      isEndOfDayLoadSourcePreload: normalizedMemberSegments.some((memberSegment) => memberSegment?.isEndOfDayLoadSourcePreload),
      isEndOfDayLoad: normalizedMemberSegments.some((memberSegment) => memberSegment?.isEndOfDayLoad),
      key: `sequence-group:${sameSiteGroupKey}`,
      kind: "group",
      memberSegments: normalizedMemberSegments,
      operationAddress: normalizedFirstSegment?.operationAddress || normalizedFirstSegment?.toAddress || firstSegment?.operationAddress || firstSegment?.toAddress || "",
      operationDisambiguatorLabel: normalizedMemberSegments.length > 1 ? `${normalizedMemberSegments.length} visites` : (normalizedFirstSegment?.operationDisambiguatorLabel || firstSegment?.operationDisambiguatorLabel || ""),
      operationKey: normalizedFirstSegment?.operationKey || firstSegment?.operationKey || `sequence-group:${sameSiteGroupKey}`,
      operationLabel: normalizedFirstSegment?.operationLabel || normalizedFirstSegment?.toLabel || firstSegment?.operationLabel || firstSegment?.toLabel || "",
      operationTypeLabel: normalizedFirstSegment?.operationTypeLabel || normalizedFirstSegment?.typeLabel || firstSegment?.operationTypeLabel || firstSegment?.typeLabel || "",
      sameSiteGroupContainsMixedStopTypes,
      sameSiteGroupSize: normalizedMemberSegments.length,
      startSequence: firstSegment?.sameSiteGroupDisplayStartSequence || firstSegment?.routeSequenceDisplaySequence || firstSegment?.sameSiteGroupStartSequence || firstSegment?.globalSequence || 0,
      stopType: sameSiteGroupContainsMixedStopTypes ? "mixed" : (normalizedFirstSegment?.stopType || firstSegment?.stopType || "delivery"),
      toAddress: normalizedFirstSegment?.toAddress || firstSegment?.toAddress || "",
      toLabel: normalizedFirstSegment?.toLabel || firstSegment?.toLabel || "",
    });

    if (isExpanded) {
      normalizedMemberSegments.forEach((memberSegment, memberIndex) => {
        renderItems.push({
          key: `sequence-member:${memberSegment?.assignmentId || "assignment"}::${memberSegment?.tripId || "trip"}::${memberSegment?.globalSequence || cursor + memberIndex + 1}`,
          kind: "group-member",
          segment: memberSegment,
        });
      });
    }

    cursor = groupEnd;
  }

  return assignVisibleOperationalDisplaySequences(renderItems);
}

function normalizeSequenceGroupingLabel(value = "") {
  return typeof value === "string"
    ? value.trim().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()
    : "";
}

export function isInitialRouteSequenceTravelSegment(segment) {
  return segment?.isInitialRouteSegment === true
    || (segment?.stopType === "depot_start" && Number(segment?.serviceDurationMinutes) === 0);
}

function getRouteSequenceDisplayLabel(value = "") {
  return typeof value === "string" ? value.trim() : "";
}

export function getRouteSequencePrimaryTitle(segment) {
  const operationLabel = getRouteSequenceDisplayLabel(segment?.operationLabel || segment?.to?.label || segment?.toLabel);

  if (isInitialRouteSequenceTravelSegment(segment)) {
    return "Inspection";
  }

  if (segment?.stopType === "depot_return") {
    return "Retour";
  }

  return operationLabel || "Arrêt";
}

export function getRouteSequencePrimaryAddress(segment) {
  const fromAddress = getRouteSequenceDisplayLabel(segment?.routeFromAddress || segment?.from?.address || segment?.fromAddress);
  const toAddress = getRouteSequenceDisplayLabel(segment?.operationAddress || segment?.to?.address || segment?.toAddress);

  if (isInitialRouteSequenceTravelSegment(segment)) {
    return fromAddress || toAddress;
  }

  if (segment?.stopType === "depot_return") {
    return toAddress || fromAddress;
  }

  return toAddress || fromAddress;
}

function getRouteSequenceRouteSummaryLabel(segment) {
  const fromLabel = getRouteSequenceDisplayLabel(segment?.routeFromLabel || segment?.from?.label);
  const toLabel = getRouteSequenceDisplayLabel(segment?.routeToLabel || segment?.toLabel || segment?.to?.label);

  if (!fromLabel || !toLabel || normalizeSequenceGroupingLabel(fromLabel) === normalizeSequenceGroupingLabel(toLabel)) {
    return "";
  }

  const prefix = isInitialRouteSequenceTravelSegment(segment)
    ? "Trajet de départ"
    : segment?.stopType === "depot_return"
      ? "Trajet de retour"
      : "Trajet sortant";

  return `${prefix}: ${fromLabel} → ${toLabel}`;
}

export function getRouteSequenceDisplaySummaryLabel(sequenceItems = [], segment = null, fallbackIndex = -1) {
  if (segment?.isTerminalRouteMarker) {
    return "";
  }

  const directSummaryLabel = getRouteSequenceRouteSummaryLabel(segment);
  const currentLabel = getRouteSequencePrimaryTitle(segment);
  const directFromLabel = getRouteSequenceDisplayLabel(segment?.routeFromLabel || segment?.from?.label);
  const canUseDirectSummary = Boolean(directSummaryLabel) && (
    isInitialRouteSequenceTravelSegment(segment)
    || segment?.stopType === "depot_return"
    || normalizeSequenceGroupingLabel(directFromLabel) === normalizeSequenceGroupingLabel(currentLabel)
  );

  if (canUseDirectSummary) {
    return directSummaryLabel;
  }

  if (!segment || isInitialRouteSequenceTravelSegment(segment) || segment?.stopType === "depot_return") {
    return "";
  }

  const resolvedSequenceItems = Array.isArray(sequenceItems) ? sequenceItems.filter(Boolean) : [];
  const resolvedIndex = resolvedSequenceItems.indexOf(segment) >= 0
    ? resolvedSequenceItems.indexOf(segment)
    : fallbackIndex;

  if (!Number.isInteger(resolvedIndex) || resolvedIndex < 0) {
    return "";
  }

  const nextSegment = resolvedSequenceItems[resolvedIndex + 1] || null;
  const nextLabel = getRouteSequencePrimaryTitle(nextSegment);

  if (!currentLabel || !nextLabel || normalizeSequenceGroupingLabel(currentLabel) === normalizeSequenceGroupingLabel(nextLabel)) {
    return "";
  }

  return `Trajet sortant: ${currentLabel} → ${nextLabel}`;
}

function buildRouteSequenceDisplaySegment(segment) {
  if (!segment) {
    return segment;
  }

  const routeFromLabel = getRouteSequenceDisplayLabel(segment?.routeFromLabel);
  const routeFromAddress = getRouteSequenceDisplayLabel(segment?.routeFromAddress);
  if (!routeFromLabel && !routeFromAddress) {
    return segment;
  }

  return {
    ...segment,
    from: segment?.from
      ? {
          ...segment.from,
          address: routeFromAddress || segment.from.address,
          label: routeFromLabel || segment.from.label,
        }
      : segment?.from,
  };
}

export function buildRouteSequenceRenderItems(segments, {
  expandedGroupKey = "",
  expandedGroupKeys = undefined,
} = {}) {
  return buildSequenceTableRenderItems(segments, { expandedGroupKey, expandedGroupKeys }).map((item) => (
    item?.kind === "segment"
      ? {
          ...item,
          segment: buildRouteSequenceDisplaySegment(item.segment || null),
        }
      : item
  ));
}

export function getRowVisualDurationMinutes(row) {
  const segmentsDurationMinutes = (Array.isArray(row?.segments) ? row.segments : []).reduce(
    (total, segment) => total + (Number(segment?.timelineDurationMinutes) > 0 ? Number(segment.timelineDurationMinutes) : 0),
    0,
  );

  return Math.max(Number(row?.totalDurationMinutes) || 0, segmentsDurationMinutes);
}

export function resolveTimelineScaleMinutes(rows = []) {
  return resolveTimelineBounds(rows).durationMinutes;
}

export function getTruckFilterMetaLabel(row, { includeTripCount = true } = {}) {
  if (row?.timelineRenderState?.status === "unrenderable") {
    const assignedItemCount = Number.isFinite(Number(row.timelineRenderState.assignedItemCount))
      ? Math.max(Math.round(Number(row.timelineRenderState.assignedItemCount)), 0)
      : 0;
    return `Tournée à vérifier — ${assignedItemCount} commande${assignedItemCount > 1 ? "s" : ""} · Durée non calculable`;
  }

  const tripCount = Number.isFinite(Number(row?.tripCount)) ? Number(row.tripCount) : 0;
  const tripLabel = tripCount > 1 ? `${tripCount} tournées` : tripCount === 1 ? "1 tournée" : "Aucune tournée";
  const assignmentDurationLabel = typeof row?.assignment?.total_estimated_duration_label === "string"
    ? row.assignment.total_estimated_duration_label.trim()
    : "";
  const durationLabel = assignmentDurationLabel || (typeof row?.totalDurationLabel === "string" ? row.totalDurationLabel.trim() : "");

  if (!row?.hasTrips) {
    return "Aucune étape planifiée";
  }

  if (!includeTripCount) {
    return durationLabel;
  }

  return durationLabel ? `${tripLabel} · ${durationLabel}` : tripLabel;
}

export function getTruckRowMetricItems(row, shell) {
  const items = [];
  const denseDurationLabel = formatDurationDense(row?.totalDurationMinutes);
  const compactCapacityLabel = getCompactCapacityLabel(shell);
  const compartmentsLabel = typeof shell?.compartmentsLabel === "string" ? shell.compartmentsLabel.trim() : "";

  if (row?.timelineRenderState?.status === "unrenderable") {
    items.push("Durée non calculable");
  } else if (denseDurationLabel && row?.hasTrips) {
    items.push(denseDurationLabel);
  }

  if (compactCapacityLabel) {
    items.push(compactCapacityLabel);
  }

  if (shell?.showCompartments && compartmentsLabel) {
    items.push(compartmentsLabel);
  }

  if (!items.length) {
    items.push(row?.hasTrips ? `${row.tripCount} tr.` : "Sans tournée");
  }

  return items.filter((item) => typeof item === "string" && item.trim()).slice(0, 3);
}

function buildTruckMaxWorkHoursAndStartTimeKpiItem(assignment) {
  const maxWorkHours = Number(assignment?.max_work_hours);
  const maxWorkHoursLabel = Number.isFinite(maxWorkHours) && maxWorkHours > 0
    ? `${Math.round(maxWorkHours * 10) / 10} h`
    : EMPTY_TRUCK_HOVER_CARD_VALUE;
  const startTimeLabel = formatClockValue(assignment?.start_time) || EMPTY_TRUCK_HOVER_CARD_VALUE;
  const value = `${maxWorkHoursLabel} · ${startTimeLabel}`;

  return buildTruckHoverCardNoteItem({
    key: "max-work-hours-start-time",
    label: "Heures max / Début",
    value,
    title: "Heures max et début de journée",
    note: "Budget horaire maximal et heure de début de journée déclarés pour ce camion.",
  });
}

function insertTruckHoverCardMaxWorkHoursAndStartTimeItem(items, assignment) {
  return [buildTruckMaxWorkHoursAndStartTimeKpiItem(assignment), ...items];
}

function normalizeTruckHoverCardBooleanText(value) {
  return (typeof value === "string" ? value.trim() : String(value ?? ""))
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function resolveTruckHoverCardBooleanLike(values = [], fallback = false) {
  for (const value of Array.isArray(values) ? values : [values]) {
    if (typeof value === "boolean") {
      return value;
    }
    if (typeof value === "number") {
      return value !== 0;
    }
    const normalized = normalizeTruckHoverCardBooleanText(value);
    if (["1", "o", "oui", "true", "vrai", "yes"].includes(normalized)) {
      return true;
    }
    if (["0", "n", "non", "false", "faux", "no"].includes(normalized)) {
      return false;
    }
  }
  return fallback;
}

function getTruckHoverCardRawData(assignment) {
  if (assignment?.truck_raw_data && typeof assignment.truck_raw_data === "object") {
    return assignment.truck_raw_data;
  }
  if (assignment?.truckRawData && typeof assignment.truckRawData === "object") {
    return assignment.truckRawData;
  }
  return null;
}

// Le produit associé vient du seul champ du Data Manager, jamais d'une copie d'import (truck_raw_data).
function resolveTruckHoverCardPreferredProduct(assignment) {
  const candidates = [assignment?.preferred_grain_type, assignment?.preferredGrainType];

  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim();
    }
  }

  return "";
}

function resolveTruckHoverCardHasAspirator(assignment) {
  const rawData = getTruckHoverCardRawData(assignment);
  return resolveTruckHoverCardBooleanLike([
    assignment?.aspirator,
    rawData?.aspirator,
    rawData?.Aspirateur,
  ], false);
}

function resolveTruckHoverCardInitialPreloadEnabled(assignment) {
  const rawData = getTruckHoverCardRawData(assignment);
  return resolveTruckHoverCardBooleanLike([
    assignment?.isInitialPreloadEnabled,
    assignment?.is_initial_preload_enabled,
    rawData?.is_initial_preload_enabled,
    rawData?.pool_preload,
    rawData?.["Pool preload"],
    rawData?.["Pool Preload"],
  ], true);
}

function formatTruckHoverCardTypeLabel(assignment) {
  if (isTruckDompeurType(assignment)) {
    return "Dompeur";
  }

  const rawData = getTruckHoverCardRawData(assignment);
  const truckType = [
    assignment?.truck_type,
    assignment?.truckType,
    rawData?.truck_type,
    rawData?.type,
    rawData?.Type,
  ].find((value) => typeof value === "string" && value.trim());

  const normalizedType = normalizeTruckHoverCardBooleanText(truckType || "citerne");
  if (normalizedType === "dompeur") {
    return "Dompeur";
  }
  return "Citerne";
}

function buildTruckHoverCardNoteItem({ key, label, value, title, note }) {
  return {
    key,
    label,
    value,
    title,
    hoverCardDetails: {
      type: "note",
      lines: [],
      note,
      total: value,
      totalLabel: label,
    },
  };
}

function buildTruckMasterHoverCardKpiItems(assignment) {
  const preferredProduct = resolveTruckHoverCardPreferredProduct(assignment);
  const hasAspirator = resolveTruckHoverCardHasAspirator(assignment);
  const aspiratorMinutes = Math.max(0, Math.round(Number(assignment?.aspirator_service_minutes) || 0));

  return [
    buildTruckHoverCardNoteItem({
      key: "truck-type",
      label: "Type",
      value: formatTruckHoverCardTypeLabel(assignment),
      title: "Type de camion",
      note: "Type de véhicule déclaré dans le Data Manager (Citerne ou Dompeur).",
    }),
    buildTruckHoverCardNoteItem({
      key: "preferred-product",
      label: "Produit associé",
      value: preferredProduct || EMPTY_TRUCK_HOVER_CARD_VALUE,
      title: "Produit associé au camion",
      note: "Produit préféré déclaré dans le Data Manager pour ce camion.",
    }),
    buildTruckHoverCardNoteItem({
      key: "aspirator",
      label: "Aspirateur",
      value: `${hasAspirator ? "Oui" : "Non"} · ${aspiratorMinutes} min`,
      title: "Aspirateur et temps de service",
      note: "Équipement aspirateur et durée de service déclarés dans le Data Manager.",
    }),
    buildTruckHoverCardNoteItem({
      key: "pool-preload",
      label: "Pool preload",
      value: resolveTruckHoverCardInitialPreloadEnabled(assignment) ? "Oui" : "Non",
      title: "Pool preload",
      note: "Indique si le camion participe au pool de préchargement selon le Data Manager.",
    }),
  ];
}

function resolveTruckHoverCardCapacityValue(assignment) {
  const capacityTons = Number(assignment?.capacity_tons);
  return Number.isFinite(capacityTons)
    ? `${formatTons(capacityTons)} t`
    : EMPTY_TRUCK_HOVER_CARD_VALUE;
}

function mergeTruckHoverCardCapacityPeakAndDeliveredItems(assignment, items) {
  const peakIndex = items.findIndex((item) => item?.key === "peak-load");
  const deliveredIndex = items.findIndex((item) => item?.key === "delivered-quantity");
  const peakItem = peakIndex >= 0 ? items[peakIndex] : null;
  const deliveredItem = deliveredIndex >= 0 ? items[deliveredIndex] : null;
  const capacityValue = resolveTruckHoverCardCapacityValue(assignment);
  const peakValue = peakItem?.value || EMPTY_TRUCK_HOVER_CARD_VALUE;
  const deliveredValue = deliveredItem?.value || EMPTY_TRUCK_HOVER_CARD_VALUE;
  const matchedIndexes = [peakIndex, deliveredIndex].filter((index) => index >= 0);
  const insertIndex = matchedIndexes.length ? Math.min(...matchedIndexes) : 0;
  const value = `${capacityValue} · ${peakValue} · ${deliveredValue}`;

  const combinedItem = {
    key: "capacity-peak-delivered",
    label: "Capacité / Pic / Total",
    value,
    title: "Capacité max, pic de charge et total livré",
    hoverCardDetails: {
      type: "formula",
      lines: [
        {
          label: "Capacité max",
          value: capacityValue,
        },
        {
          label: "Pic charge",
          value: peakValue,
        },
        {
          label: "Total livré",
          value: deliveredValue,
        },
      ],
      note: "Capacité maximale du camion, pic de charge de la tournée et tonnage total livré.",
      total: value,
      totalLabel: "Capacité / Pic / Total",
    },
  };

  return [
    ...items.slice(0, insertIndex),
    combinedItem,
    ...items.slice(insertIndex).filter((item) => (
      item?.key !== "peak-load" && item?.key !== "delivered-quantity"
    )),
  ];
}

export function getTruckHoverCardKpiItems(assignment, trip) {
  if (!assignment) {
    return [];
  }

  const masterItems = buildTruckMasterHoverCardKpiItems(assignment);
  const kpiData = trip ? selectKpiGridData(assignment, trip) : null;
  const baseItems = mergeTruckHoverCardCapacityPeakAndDeliveredItems(
    assignment,
    (Array.isArray(kpiData?.items) ? kpiData.items : [])
      .filter((item) => item?.label && item?.value)
      .filter((item) => !TRUCK_HOVER_CARD_EXCLUDED_KPI_KEYS.has(item?.key)),
  );
  const totalCompartments = Math.max(Number(assignment?.total_compartments) || 0, 0);
  const usedCompartments = Math.max(Number(assignment?.compartments_used) || 0, 0);

  if (totalCompartments <= 0) {
    return insertTruckHoverCardMaxWorkHoursAndStartTimeItem([...masterItems, ...baseItems], assignment);
  }

  const compartmentItem = {
    key: "compartments-used",
    label: "Comp. occupés",
    value: `${Math.min(usedCompartments, totalCompartments)}/${totalCompartments}`,
    title: "Compartiments occupés",
    hoverCardDetails: {
      type: "formula",
      lines: [
        {
          label: "Occupés",
          value: String(Math.min(usedCompartments, totalCompartments)),
        },
        {
          label: "Déclarés",
          value: String(totalCompartments),
        },
      ],
      note: "Nombre de compartiments actuellement utilisés, rapporté au total déclaré.",
      total: `${Math.min(usedCompartments, totalCompartments)}/${totalCompartments}`,
      totalLabel: "Comp. occupés",
    },
  };

  const loadCombinedIndex = baseItems.findIndex((item) => item?.key === "capacity-peak-delivered");
  const compartmentInsertIndex = loadCombinedIndex >= 0 ? loadCombinedIndex + 1 : Math.min(1, baseItems.length);

  return insertTruckHoverCardMaxWorkHoursAndStartTimeItem([
    ...masterItems,
    ...baseItems.slice(0, compartmentInsertIndex),
    compartmentItem,
    ...baseItems.slice(compartmentInsertIndex),
  ], assignment);
}

function normalizeFleetTruckIncidentText(value) {
  return typeof value === "string" ? value.trim() : "";
}

export function resolveFleetTruckIncidentMinutes(value) {
  if (Number.isFinite(Number(value)) && value !== "" && value != null) {
    return Number(value);
  }

  const match = /^(\d{1,2}):(\d{2})$/.exec(normalizeFleetTruckIncidentText(value));
  if (!match) {
    return null;
  }

  const hour = Number(match[1]);
  const minute = Number(match[2]);
  return Number.isInteger(hour) && hour >= 0 && hour <= 23
    && Number.isInteger(minute) && minute >= 0 && minute <= 59
    ? (hour * 60) + minute
    : null;
}

export function resolveFleetRowTruckIncidents(row = null) {
  const incidents = Array.isArray(row?.incidents)
    ? row.incidents
    : Array.isArray(row?.assignment?.truck_incidents)
      ? row.assignment.truck_incidents
      : [];

  return incidents
    .filter((incident) => incident && typeof incident === "object")
    .slice()
    .sort((left, right) => (
      (resolveFleetTruckIncidentMinutes(left?.display_start_minutes ?? left?.start_minutes ?? left?.start_time) ?? Number.MAX_SAFE_INTEGER)
      - (resolveFleetTruckIncidentMinutes(right?.display_start_minutes ?? right?.start_minutes ?? right?.start_time) ?? Number.MAX_SAFE_INTEGER)
      || normalizeFleetTruckIncidentText(left?.id).localeCompare(normalizeFleetTruckIncidentText(right?.id))
    ));
}

export function formatFleetTruckIncidentClockLabel(value, fallbackMinutes = null) {
  const match = /^(\d{1,2}):(\d{2})$/.exec(normalizeFleetTruckIncidentText(value));
  if (match) {
    return `${Number(match[1])} h ${match[2]}`;
  }

  const normalizedMinutes = resolveFleetTruckIncidentMinutes(fallbackMinutes);
  if (normalizedMinutes == null) {
    return "—";
  }

  const roundedMinutes = Math.max(0, Math.round(normalizedMinutes));
  const hours = Math.floor(roundedMinutes / 60);
  const minutes = roundedMinutes % 60;
  return `${hours} h ${String(minutes).padStart(2, "0")}`;
}

export function resolveFleetTruckIncidentDurationMinutes(incident) {
  const explicitDuration = Number(incident?.duration_minutes);
  if (Number.isFinite(explicitDuration) && explicitDuration > 0) {
    return Math.round(explicitDuration);
  }

  const startMinutes = resolveFleetTruckIncidentMinutes(
    incident?.display_start_minutes ?? incident?.start_minutes ?? incident?.start_time,
  );
  const endMinutes = resolveFleetTruckIncidentMinutes(
    incident?.display_end_minutes ?? incident?.end_minutes ?? incident?.end_time,
  );
  if (startMinutes == null || endMinutes == null || endMinutes <= startMinutes) {
    return 0;
  }

  return Math.round(endMinutes - startMinutes);
}

export function listOverlappingTruckIncidents(
  incidents = [],
  windowStartMinutes = null,
  windowEndMinutes = null,
) {
  const normalizedStart = Number(windowStartMinutes);
  const normalizedEnd = Number(windowEndMinutes);
  if (!Number.isFinite(normalizedStart) || !Number.isFinite(normalizedEnd) || normalizedEnd <= normalizedStart) {
    return [];
  }

  return (Array.isArray(incidents) ? incidents : []).filter((incident) => {
    if (!incident || typeof incident !== "object") {
      return false;
    }

    const incidentStart = resolveFleetTruckIncidentMinutes(
      incident?.display_start_minutes ?? incident?.start_minutes ?? incident?.start_time,
    );
    const incidentEnd = resolveFleetTruckIncidentMinutes(
      incident?.display_end_minutes ?? incident?.end_minutes ?? incident?.end_time,
    );
    if (incidentStart == null || incidentEnd == null || incidentEnd <= incidentStart) {
      return false;
    }

    return incidentStart < normalizedEnd && incidentEnd > normalizedStart;
  });
}

export function sumOverlappingTruckIncidentMinutes(
  incidents = [],
  windowStartMinutes = null,
  windowEndMinutes = null,
) {
  return listOverlappingTruckIncidents(incidents, windowStartMinutes, windowEndMinutes)
    .reduce((total, incident) => total + resolveFleetTruckIncidentDurationMinutes(incident), 0);
}

export function buildFleetSequenceTruckIncidentChip(
  incidents = [],
  windowStartMinutes = null,
  windowEndMinutes = null,
  keyPrefix = "segment",
) {
  const overlappingIncidents = listOverlappingTruckIncidents(
    incidents,
    windowStartMinutes,
    windowEndMinutes,
  );
  const totalMinutes = overlappingIncidents.reduce(
    (total, incident) => total + resolveFleetTruckIncidentDurationMinutes(incident),
    0,
  );
  if (totalMinutes <= 0) {
    return null;
  }

  const durationLabel = formatDurationShort(totalMinutes) || `${totalMinutes} min`;
  const lines = overlappingIncidents.map((incident) => {
    const startLabel = formatFleetTruckIncidentClockLabel(
      incident?.start_time,
      incident?.display_start_minutes ?? incident?.start_minutes,
    );
    const endLabel = formatFleetTruckIncidentClockLabel(
      incident?.end_time,
      incident?.display_end_minutes ?? incident?.end_minutes,
    );
    const incidentDurationMinutes = resolveFleetTruckIncidentDurationMinutes(incident);
    const incidentDurationLabel = formatDurationShort(incidentDurationMinutes) || `${incidentDurationMinutes} min`;

    return {
      label: `${startLabel} — ${endLabel}`,
      value: `+${incidentDurationLabel}`,
    };
  });

  return {
    key: `${keyPrefix}:truck_incident`,
    kind: "truck_incident",
    label: `+${durationLabel}`,
    tone: "danger",
    hoverCardDetails: {
      type: "formula",
      lines,
      note: "Durée des bris mécaniques / contretemps qui chevauchent cet arrêt. L'heure de fin affichée tient déjà compte de ce temps.",
      total: `+${durationLabel}`,
      totalLabel: "Durée bris",
    },
    hoverCardTitle: "Bris mécanique / Contretemps",
    title: `Bris mécanique / Contretemps : +${durationLabel}`,
  };
}
